import React, { useEffect, useRef, useState } from 'react';

import moment from 'moment-timezone';
import update from 'immutability-helper';

import Abstract from 'classes/Abstract.js';
import { BookAppointmentFromLead, AppointmentDetails } from 'managers/Appointments.js';
import AltFieldMapper, { validateRequiredFields } from 'views/AltFieldMapper.js';
import Appointment from 'classes/Appointment.js';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import OutboundCall from 'classes/OutboundCall.js';
import Checkbox from 'views/Checkbox.js';
import Client from 'classes/Client.js';
import ClientLookupField from 'views/ClientLookupField.js';
import { ClientDetails, getEventStatus } from 'managers/Clients.js';
import Content from 'managers/Content.js';
import Cookies from 'js-cookie';
import CreditsPicker from 'views/CreditsPicker.js';
import CustomListField from 'views/CustomListField.js';
import DatePickerField from 'views/DatePickerField.js';
import FieldMapper, { formatFields } from 'views/FieldMapper.js';
import FilePickerField from 'views/FilePickerField.js';
import Layer, { LayerItem, ToolbarHeight, getLayerSizingHeight } from 'structure/Layer.js';
import Lead from 'classes/Lead.js';
import LeadScriptEditor, { RebuttalsCollection, formatLeadScriptContent } from 'views/LeadScriptEditor.js';
import ListField from 'views/ListField.js';
import LottieView from 'views/Lottie.js';
import { Map } from 'views/MapElements.js';
import { MessageComponent } from 'managers/Messaging.js';
import Panel from 'structure/Panel.js';
import PageControl from 'views/PageControl.js';
import { PaymentsOverview, SystemEventDetails, SystemEventsLayerItem, UserDetails } from 'managers/Users.js';
import PermissionsContainer from 'views/PermissionsContainer.js';
import PrintContent from 'views/PrintContent.js';
import Survey from 'classes/Survey.js';
import { SurveyDetails } from 'managers/Surveys.js';
import ProgressBar from 'views/ProgressBar.js';
import Request from 'files/Request.js';
import StatusCodeFilters from 'views/StatusCodeFilters.js';
import SystemEvent from 'classes/SystemEvent.js';
import TableListHeader from 'views/TableListHeader.js';
import TextField from 'views/TextField.js';
import TextView from 'views/TextView.js';
import TimezoneLookupField from 'views/TimezoneLookupField.js';
import OutboundCallButton from 'views/OutboundCallButton.js';
import User from 'classes/User.js';
import UserLookupField from 'views/UserLookupField.js';
import Utils from 'files/Utils.js';
import Views, { AltBadge, AsyncViewComponent } from 'views/Main.js';
import { WeekCalendar } from 'views/Calendar.js';
import LeadLookupField from 'views/LeadLookupField';

export const BatchLeadOptions = ({ filterProps, leads, onChange, props, selectAll }, { index, options, utils }) => {

    const layerID = 'batch_lead_options';
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onArchiveLeads = async () => {
        utils.alert.show({
            title: 'Archive Leads',
            message: `Are you sure that you want to archive the selected ${leads.length === 1 ? 'lead' : 'leads'}?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onArchiveLeadsConfirm();
                    return;
                }
            }
        })
    }

    const onArchiveLeadsConfirm = async () => {
        try {
            setLoading('archive');
            let { background, threshold } = await Request.post(utils, '/leads/', {
                ids: leads.map(lead => lead.id),
                type: 'batch_archive'
            });

            // end loading and close layer
            setLoading(false);
            setLayerState('close');

            // end loading and notify listeners of change
            if(typeof(onChange) === 'function') {
                onChange();
            }

            // determine if request is going to be processed in the background
            if(background) {
                utils.alert.show({
                    title: 'Request Submitted',
                    message: `We have received your request to archive ${leads.length} ${leads.length === 1 ? 'lead' : 'leads'} and will notify you when it has completed. Request for ${Utils.softNumberFormat(threshold)} or more can take up to a minute to complete.`
                });
                return;
            }

            // show default confirmation alert for foreground task 
            utils.banner.show({
                title: 'Leads Archived',
                message: `We have archived ${leads.length} ${leads.length === 1 ? 'lead' : 'leads'}. You can move ${leads.length === 1 ? 'this lead' : 'these leads'} back into your active lead pool at any time by finding ${leads.length === 1 ? 'this lead' : 'these leads'} in the archive list and selecting "Unarchive".`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue archiving ${leads.length === 1 ? 'this lead' : 'these leads'}. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onAssignLeads = () => {
        utils.layer.open({
            Component: AssignLeads.bind(this, {
                leads: leads,
                onClose: results => {
                    onChange(results);
                    setLayerState('close');
                }
            }),
            id: 'assign_leads'
        });
    }

    const onDeleteLeads = () => {
        utils.alert.show({
            title: `Delete ${leads.length} ${leads.length === 1 ? 'lead' : 'leads'}`,
            message: `Are you sure that you want to delete ${leads.length === 1 ? 'this lead' : 'these leads'} from your client workspace? This will also remove any appointments, appointment requests, and call logs where ${leads.length === 1 ? 'this lead is' : 'these leads are'} attached. This can not be undone.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onDeleteLeadsConfirm();
                    return;
                }
            }
        });
    }

    const onDeleteLeadsConfirm = async () => {
        try {

            // start loading and sleep breifly before moving on
            setLoading('delete');
            await Utils.sleep(0.25);

            // submit request to server
            let { outbound_calls, appointments } = await Request.post(utils, '/leads/', {
                ids: leads.map(lead => lead.id),
                type: 'batch_delete'
            });

            // end loading and close layer
            setLoading(false);
            setLayerState('close');

            // notify listeners of change
            if(typeof(onChange) === 'function') {
                onChange();
            }

            // show confirmation alert to user where only the lead was delete if applicable
            if(outbound_calls === 0 && appointments === 0) {
                utils.banner.show({
                    title: 'Leads Deleted',
                    message: `${leads.length === 1 ? 'This lead has' : 'These leads have'} been deleted from your client workspace.`,
                    onShow: utils.content.fetch.bind(this, 'lead')
                });
                return;
            }

            // prepare array of targets representing the quantity removed
            let targets = [{
                title: 'call log',
                value: outbound_calls
            },{
                title: 'appointment',
                value: appointments
            }];

            // prepare an array of messages representing the targets removed
            let messages = targets.reduce((array, target) => {
                if(target.value > 0) {
                    array.push(`${target.value} ${target.value === 1 ? target.title : `${target.title}s`}`);
                }
                return array;
            }, []);
            
            // show confirmation alert to user with removal totals
            utils.banner.show({
                title: 'Leads Deleted',
                message: `${leads.length === 1 ? 'This lead has' : 'These leads have'} been deleted from your client workspace along with ${Utils.oxfordImplode(messages)}`,
                onShow: utils.content.fetch.bind(this, 'lead')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting this lead from your client workspace. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onDownloadLeads = async () => {
        try {

            // start loading and submit request to server
            setLoading('download');
            let { url } = await Request.post(utils, '/leads/', {
                all_leads: selectAll,
                filter_props: filterProps,
                lead_ids: selectAll === false && leads.map(lead => lead.id),
                type: 'download'
            });

            // end loading and close layer
            setLoading(false);
            setLayerState('close');

            // notify listeners of change
            if(typeof(onChange) === 'function') {
                onChange();
            }

            // open download url in new tab
            utils.alert.show({
                title: 'Download Ready',
                message: 'Your document has been prepare and is ready to download. Click the button below to begin your download.',
                buttons: [{
                    key: 'download',
                    title: 'Start Download',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'download') {
                        window.open(url);
                        return;
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing your download from your client workspace. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onPrintLeads = () => {
        utils.layer.open({
            id: 'print_content',
            Component: PrintContent.bind(this, {
                title: 'Leads',
                onFetch: async () => new Promise(resolve => resolve(leads)), // promise used to conform to panel print requirements
                onRenderItem: item => ({
                    full_name: item.full_name,
                    address: item.address ? Utils.formatAddress(item.address) : null,
                    phone_number: item.phone_number ? item.phone_number : null,
                    ...props?.follow_ups === true && {
                        last_updated: item.extended_details && item.extended_details.sys_evt_date ? Utils.formatDate(item.extended_details.sys_evt_date) : null,
                    },
                    status: getLeadStatus(utils, item)
                }),
                headers: [{
                    key: 'full_name',
                    title: 'Name'
                },{
                    key: 'address',
                    title: 'Address'
                },{
                    key: 'phone_number',
                    title: 'Phone Number'
                },{
                    key: 'last_updated',
                    title: 'Last Updated',
                    visible: props?.follow_ups === true
                },{
                    key: 'status',
                    title: 'Status'
                }]
            })
        });
    }

    const onTransferLeads = () => {

        let client = null;
        utils.alert.show({
            title: 'Transfer Leads',
            message: `You can select one or more leads to transfer to another client workspace. We'll send a request to the other client that they can accept or decline. If accepted, the leads will be removed from your client workspace and added to the recipient's client workspace. If declined, the leads will not be transfered out of your client workspace.`,
            content: (
                <div style={{
                    padding: 12,
                    width: '100%'
                }}>
                    <ClientLookupField
                    containerStyle={{
                        width: '100%'
                    }}
                    globalVisibility={true}
                    icon={'search'}
                    inline={true}
                    onChange={result => client = result}
                    utils={utils}/>
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: `Send ${leads.length} ${leads.length === 1 ? 'lead' : 'leads'}`,
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm' && client) {
                    onTransferLeadsConfirm(client);
                    return;
                }
            }
        })
    }

    const onTransferLeadsConfirm = async client => {
        try {
            // start loading and sleep breifly before moving on
            setLoading('transfer');
            await Utils.sleep(0.25);

            // submit request to server
            await Request.post(utils, '/leads/', {
                destination_client_id: client.id,
                ids: leads.map(lead => lead.id),
                type: 'new_transfer'
            });

            // end loading and close layer
            setLoading(false);
            setLayerState('close');

            // notify listeners of change
            if(typeof(onChange) === 'function') {
                onChange();
            }

            // show confirmation alert
            utils.banner.show({
                title: 'Leads Transferred',
                message: `We have sent ${leads.length} ${leads.length - 1 ? 'lead' : 'leads'} to the ${client.name} client workspace. We'll notify your client workspace when they accept or decline this transfer.`,
                onShow: utils.content.fetch.bind(this, 'lead')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue transferring your leads from your client workspace. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onResort = () => {
        utils.alert.show({
            title: 'Resort Leads',
            message: 'Are you sure that you want to resort these leads? This will remove the general assignment, setter assignment, and set the status for each lead to "New".',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onResortConfirm();
                    return;
                }
            }
        });
    }

    const onResortConfirm = async () => {
        try {

            // start loading and sleep breifly before moving on
            setLoading('resort');
            await Utils.sleep(0.25);

            // submit request to server
            await Request.post(utils, '/leads/', {
                ids: leads.map(lead => lead.id),
                type: 'batch_resort'
            });

            // end loading and close layer
            setLoading(false);
            setLayerState('close');

            // notify listeners of change
            if(typeof(onChange) === 'function') {
                onChange();
            }

            // notify subscribers of data change and show confirmation alert
            utils.banner.show({
                title: 'Leads Resorted',
                message: `We have successfully resorted ${leads.length} ${leads.length === 1 ? 'lead' : 'leads'}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue resorting these leads. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSendSms = () => {
        utils.layer.open({
            Component: LeadTextMessageManager.bind(this, {
                leads: leads,
                onClose: results => {
                    onChange(results);
                    setLayerState('close');
                }
            }),
            id: 'lead_text_message_manager'
        });
    }

    const onSetRelease = release => {
        utils.alert.show({
            title: `${release ? 'Release' : 'Unrelease'}`,
            message: `Are you sure that you want to ${release ? 'release' : 'unrelease'} these Leads? ${release ? 'This will open these leads up to the rest of the client.' : 'This will prevent others in the client from viewing these leads.'}`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: release ? 'default' : 'destructive'
            },{
                key: 'cancel',
                title: `Do Not ${release ? 'Release': 'Unrelease'}`,
                style: release ? 'destructive' : 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSetReleaseConfirm(release);
                    return;
                }
            }
        })
    }

    const onSetReleaseConfirm = async release => {
        try {

            // start loading and sleep breifly before moving on
            setLoading('release');
            await Utils.sleep(0.25);

            // submit request to server
            await Request.post(utils, '/leads/', {
                type: 'set_release_batch',
                ids: leads.map(lead => lead.id),
                release: release
            });

            // end loading and close layer
            setLoading(false);
            setLayerState('close');

            // notify listeners of change
            if(typeof(onChange) === 'function') {
                onChange();
            }

            // notify subscribers of data change and show confirmation alert
            utils.banner.show({
                title: 'Release Status Changed',
                message: `${leads.length === 1 ? 'This lead has' : 'These Leads have'} been ${release ? `released and ${leads.length === 1 ? 'is' : 'are'} avaiable for others to view.` : `unreleased and ${leads.length === 1 ? 'is' : 'are'} no longer available for others to view.`}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the release status for these leads. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUnarchiveLeads = async () => {
        utils.alert.show({
            title: 'Unarchive Leads',
            message: `Are you sure that you want to unarchive the selected ${leads.length === 1 ? 'lead' : 'leads'}? This will return the leads back to their leads pools.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onUnarchiveLeadsConfirm();
                    return;
                }
            }
        });
    }

    const onUnarchiveLeadsConfirm = async () => {
        try {

            // start loading and submit request to server
            setLoading('unarchive');
            let { background, threshold } = await Request.post(utils, '/leads/', {
                type: 'batch_unarchive',
                ids: leads.map(lead => lead.id)
            });

            // end loading and close layer
            setLoading(false);
            setLayerState('close');

            // notify listeners of change
            if(typeof(onChange) === 'function') {
                onChange();
            }

            // determine if request is going to be processed in the background
            if(background) {
                utils.alert.show({
                    title: 'Request Submitted',
                    message: `We have received your request to unarchive ${leads.length} ${leads.length === 1 ? 'lead' : 'leads'} and will notify you when it has completed. Request for ${Utils.softNumberFormat(threshold)} leads or more can take up to a minute to complete.`
                });
                return;
            }
            
            // show default confirmation alert for foreground task 
            utils.banner.show({
                title: 'Leads Unarchived',
                message: `We have returned ${leads.length} ${leads.length === 1 ? 'lead' : 'leads'} back to your general lead pool.`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue unarchiving ${leads.length === 1 ? 'this lead' : 'these leads'}. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUnassignLeads = async () => {
        utils.alert.show({
            title: 'Unassign Leads',
            message: `Are you sure that you want to unassign the selected ${leads.length === 1 ? 'lead' : 'leads'}? This cannot be undone.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onUnassignLeadsConfirm();
                    return;
                }
            }
        });
    }

    const onUnassignLeadsConfirm = async () => {
        try {

            // start loading and submit request to server
            setLoading('unassign');
            let { background, count, estimated_duration } = await Request.post(utils, '/leads/', {
                ids: leads.map(lead => lead.id),
                type: 'batch_unassign'
            });

            // end loading and close layer
            setLoading(false);
            setLayerState('close');

            // notify listeners of change
            if(typeof(onChange) === 'function') {
                onChange();
            }

            // determine if request is going to be processed in the background
            if(background) {
                utils.alert.show({
                    title: 'Request Submitted',
                    message: `We have received your request to unassign ${Utils.softNumberFormat(count)} ${count === 1 ? 'lead' : 'leads'} and will notify you when it has completed. The estimated time to remove your assignments is about ${Utils.parseDuration(estimated_duration)}.`
                });
                return;
            }

            // show default confirmation alert for foreground task 
            utils.banner.show({
                title: 'Assignments Removed',
                message: `We have removed the assignments for your selected ${leads.length === 1 ? 'lead' : 'leads'}.`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue unassigning ${leads.length === 1 ? 'this lead' : 'these leads'}. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUpdateDetails = () => {
        utils.layer.open({
            Component: BatchUpdateLeadDetails.bind(this, {
                ids: leads.map(lead => lead.id),
                onClose: onChange
            }),
            id: `batch_update_lead_details`
        });
    }

    const getContent = () => {

        // only allow lead transfering for administrators and the primary of the current client
        let client = utils.client.get();
        let isAdmin = utils.user.get().level === User.levels.get().admin;
        let canTransferLeads = isAdmin || utils.user.get().user_id === client.primary_user_id ? true : false;

        // prepare list of items for batch lead management
        let items = [{
            description: 'Move the selected leads to the archive list. Archived leads are removed from active workflows but can be restored later.',
            id: 'archive',
            onClick: onArchiveLeads,
            title: 'Archive',
            visible: props?.show_archived_only === true ? false : true
        },{
            description: 'Assign the selected leads to a specific user for follow-up or management.',
            id: 'assign_user',
            onClick: onAssignLeads,
            title: 'Assign to User'
        },{
            description: 'Permanently remove the selected leads and all related data from the system.',
            id: 'delete',
            onClick: onDeleteLeads,
            title: 'Delete',
            visible: isAdmin
        },{
            description: 'Export the selected leads as a downloadable file for offline use or reporting.',
            id: 'download',
            onClick: onDownloadLeads,
            title: 'Download',
            visible: isAdmin
        },{
            description: 'Print a formatted list of the selected leads and their details.',
            id: 'print',
            onClick: onPrintLeads,
            title: 'Print',
            visible: isAdmin
        },{
            description: 'Send a text message to the selected leads, if allowed by their status.',
            id: 'sms',
            onClick: onSendSms,
            title: 'Send Text Message',
            visible: props?.status_codes?.includes(Lead.status.get().do_not_call) ? false : true
        },{ 
            description: 'Transfer the selected leads to another client. The receiving client must accept the transfer.',
            id: 'transfer',
            onClick: onTransferLeads,
            title: 'Transfer to Other Client',
            visible: canTransferLeads
        },{
            description: 'Reset assignments and statuses for the selected leads, setting them back to "New".',
            id: 'resort',
            onClick: onResort,
            title: 'Resort'
        },{
            description: 'Make the selected leads visible to others in the client.',
            id: 'release',
            onClick: onSetRelease.bind(this, true),
            title: 'Release',
            visible: isAdmin && props?.released === false ? true : false
        },{
            description: 'Restrict the selected leads so only the primary can view them.',
            id: 'unrelease',
            onClick: onSetRelease.bind(this, false),
            title: 'Unrelease',
            visible: isAdmin === false || props?.released === false ? false : true
        },{
            description: 'Restore the selected leads from the archive back to the active lead pool.',
            id: 'unarchive',
            onClick: onUnarchiveLeads,
            title: 'Unarchive'
        },{
            description: 'Remove user assignments from the selected leads, making them unassigned.',
            id: 'unassign',
            onClick: onUnassignLeads,
            title: 'Unassign'
        },{
            description: 'Update details for the selected leads in bulk.',
            id: 'update',
            onClick: onUpdateDetails,
            title: 'Update'
        }];

        return (
            <LayerItem
            collapsed={false}
            lastItem={true}
            title={'Management Actions'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <span style={{
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        color: Appearance.colors.text(),
                        display: 'block',
                        fontWeight: 500,
                        fontSize: 14,
                        padding: '12px 16px 12px 16px',
                        width: '100%'
                    }}>
                        {`Use this panel to take immediate action on one or more selected leads. From assignment and status updates to outreach and archiving, these tools let you move opportunities forward or restructure your pipeline in seconds.`}
                    </span>
                    {items.filter(item => {
                        return item.visible !== false;
                    }).map((item, index, items) => {
                        return (
                            Views.entry({
                                bottomBorder: index !== items.length - 1,
                                key: index,
                                loading: loading === item.id,
                                onClick: item.onClick,
                                subTitle: item.description,
                                title: item.title
                            })
                        )
                    })}
                </div>
            </LayerItem>
        )
    }

    const getOverview = () => {
        return (
            <LayerItem
            collapsed={false}
            title={'Management Overview'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: '12px 16px 12px 16px',
                    width: '100%'
                }}>
                    <span style={{
                        color: Appearance.colors.text(),
                        display: 'block',
                        fontWeight: 500,
                        fontSize: 14,
                        width: '100%'
                    }}>
                        {`Lead management is the structured control of how prospects are tracked, handled, and progressed through your sales system. Because these actions directly affect ownership, workflow placement, and communication history—and most cannot be undone—always confirm your selections before proceeding.`}
                    </span>
                </div>
            </LayerItem>
        )
    }
    
    return (
        <Layer
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true
        }}
        title={leads.length === 1 ? 'Lead Management' : 'Batch Lead Management'}
        utils={utils}>
            {getOverview()}
            {getContent()}
        </Layer>
    )
}

export const OutboundCalls = ({ index, options, utils }) => {

    const panelID = 'outbound_calls';
    const limit = 15;
    const offset = useRef(0);
    const sorting = useRef({});

    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);

    const onCallClick = call => {
        utils.layer.open({
            abstract: Abstract.create({
                object: call,
                type: 'outbound_calls'
            }),
            Component: OutboundCallDetails,
            id: `outbound_call_details_${call.id}`,
            permissions: ['calls.details']
        });
    }

    const getContent = () => {
        if(calls.length === 0) {
            return (
                Views.entry({
                    title: 'No Outbound Calls Found',
                    subTitle: 'No outbound calls were found in the system',
                    bottomBorder: false,
                    hideIcon: true
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <thead style={{
                    width: '100%'
                }}>
                    {getFields()}
                </thead>
                <tbody style={{
                    width: '100%'
                }}>
                    {calls.map(getFields)}
                </tbody>
            </table>
        )
    }

    const getFields = (call, index, calls) => {

        let target = call || {};
        let fields = [{
            key: 'lead_full_name',
            title: 'Lead',
            value: target.lead_full_name
        },{
            key: 'start_date',
            title: 'Date',
            value: target.start_date ? moment(target.start_date).format('MMMM Do, YYYY [at] h:mma') : null
        },{
            key: 'direction',
            title: 'Direction',
            value: (
                <div style={{
                    alignItems: 'center',
                    background: Appearance.colors.softGradient(Appearance.colors.primary()),
                    border: `1px solid ${Appearance.colors.primary()}`,
                    borderRadius: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    justifyContent: 'center',
                    maxWidth: 95,
                    overflow: 'hidden',
                    textAlign: 'center',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        color: 'white',
                        fontWeight: '600',
                        width: '100%'
                    }}>{target.direction ? Utils.ucFirst(target.direction) : null}</span>
                </div>
            )
        }];

        // create table headers with custom sorting options
        if(!call) {
            return (
                <TableListHeader
                fields={fields}
                onChange={props => {
                    sorting.current = props;
                    fetchCalls();
                }}
                value={sorting.current} />
            )
        }

        // loop through result rows
        return (
            <tr
            key={index}
            className={`view-entry ${window.theme}`}
            style={{
                borderBottom: index !== calls.length - 1 && `1px solid ${Appearance.colors.divider()}`
            }}>
                {fields.map((field, index) => {
                    return (
                        <td
                        key={index}
                        className={'px-3 py-2 flexible-table-column'}
                        onClick={onCallClick.bind(this, call)}>
                            <span style={Appearance.textStyles.subTitle()}>{field.value}</span>
                        </td>
                    )
                })}
            </tr>
        )
    }

    const fetchCalls = async () => {
        try {
            setLoading(true);
            let { calls, paging } = await Request.get(utils, '/clients/', {
                offset: offset.current,
                search_text: searchText,
                type: 'outbound_calls',
                ...sorting.current
            });

            setLoading(false);
            setPaging(paging);
            setCalls(calls.map(call => OutboundCall.create(call)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the list of outbound calls. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        offset.current = 0;
        fetchCalls();
    }, [searchText]);


    useEffect(() => {
        utils.events.on(panelID, 'client_change', fetchCalls);
        utils.content.subscribe(panelID, ['outbound_calls'], {
            onFetch: fetchCalls,
            onUpdate: abstract => {
                setCalls(calls => calls.map(prevCall => {
                    return prevCall.id === abstract.getID() ? abstract.object : prevCall;
                }));
            }
        });
        return () => {
            utils.content.unsubscribe(panelID);
            utils.events.off(panelID, 'client_change', fetchCalls);
        }
    }, []);

    return (
        <Panel
        index={index}
        title={'Outbound Calls'}
        utils={utils}
        options={{
            ...options,
            loading: loading === true,
            search: {
                placeholder: 'Search by lead name or caller name...',
                onChange: setSearchText
            },
            paging: {
                data: paging,
                limit: limit,
                offset: offset.current,
                onClick: next => {
                    offset.current = next;
                    fetchCalls();
                }
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel()
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const OutboundCallsCalendar = ({ index, options, utils }) => {

    const panelID = 'outbound_calls_calendar';
    const contentContainer = useRef(null);

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLead, setSelectedLead] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [targetDate, setTargetDate] = useState(moment());
    const [timezone, setTimezone] = useState(utils.user.get().timezone);

    const setEventLoading = abstract => {
        setLoading(`${abstract.type}_${abstract.object.id}`);
    }

    const onContentUpdate = abstract => {
        setEvents(events => {
            return events.map(evt => {

                // no additional logic is required for event non-matches
                if(`${evt.type}-${evt.id}` !== abstract.getTag()) {
                    return evt;
                }

                // format event data
                return {
                    end: moment.utc(abstract.object.created_at).add(abstract.object.duration, 'seconds').local().format('YYYY-MM-DD HH:mm:ss'),
                    eventResourceEditable: true,
                    eventStartEditable: true,
                    extendedProps: {
                        event: abstract
                    },
                    id: abstract.object.id,
                    start: moment.utc(abstract.object.created_at).local().format('YYYY-MM-DD HH:mm:ss'),
                    status: abstract.object.status,
                    type: abstract.type
                }
            });
        });
    }

    const onEventClick = async abstract => {
        try {

            // start loading for abstract target and fetch details
            setEventLoading(abstract);
            let call = await OutboundCall.get(utils, abstract.object.id);

            setLoading(false);
            utils.layer.open({
                abstract: Abstract.create({
                    object: call,
                    type: 'outbound_calls'
                }),
                Component: OutboundCallDetails,
                id: `outbound_call_details_${call.id}`,
                permissions: ['outbound_calls.details']
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the details for this call. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onEventRender = target => {

        // prepare event variables and date formatting options
        let { created_at, lead_full_name, status, user } = target.object;
        let options = { exclude_date: true, include_timezone: true, timezone, utc: true };

        // prepare quick look items
        let quickLook = [{
            key: 'date',
            value: `Date: ${Utils.formatDate(created_at, options)}`
        },{
            key: 'duration',
            value: `Duration: ${Utils.parseDuration(target.object.duration)}`
        },{
            key: 'caller',
            value: `Caller: ${user?.full_name || 'Not available'}`
        }];

        // prepare name label for event, status code color, optional reschedule context, and abstract loading tag
        let color = status === 'completed' ? Appearance.colors.primary() : Appearance.colors.grey();
        let tag = `${target.type}_${target.object.id}`;

        return (
            <div
            className={`text-button mb-1`}
            onClick={onEventClick.bind(this, target)}
            title={lead_full_name || 'Name not available'}
            style={{
                background: Appearance.colors.softGradient(color),
                border: `2px solid ${color}`,
                borderRadius: 5,
                height: '100%',
                overflow: 'hidden',
                position: 'relative',
                width: '100%'
            }}>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    padding: '8px 12px 8px 12px',
                    position: 'relative'
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0
                    }}>
                        <span style={{
                            color: 'white',
                            fontSize: 12,
                            fontWeight: 700,
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>{lead_full_name || 'Name not available'}</span>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            maxWidth: '100%',
                            minWidth: 0
                        }}>
                            <span style={{
                                color: 'white',
                                fontSize: 12,
                                fontWeight: 600,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>{Utils.ucFirst(status)}</span>
                        </div>
                    </div>
                    <img
                    src={user?.avatar || 'images/avatar-placeholder.png'}
                    style={{
                        border: '2px solid white',
                        borderRadius: 15,
                        flex: 'none',
                        height: 30,
                        marginLeft: 8,
                        overflow: 'hidden',
                        width: 30
                    }} />
                </div>
                {loading === tag && (
                    <div style={{
                        bottom: 0,
                        height: 2,
                        left: 0,
                        overflow: 'hidden',
                        position: 'absolute', 
                        width: '100%'
                    }}>
                        <ProgressBar barColor={'rgba(255,255,255,0.25)'} trackColor={'#FFFFFF'}/>
                    </div>
                )}
                <div style={{
                    borderTop: `${loading === tag ? 0 : 2}px solid ${color}`,
                    display: 'none',
                    flexDirection: 'column',
                    padding: '8px 12px 8px 12px'
                }}>
                    {quickLook.map(item => {
                        return (
                            <span
                            key={item.key}
                            className={'d-block w-100'}
                            style={{
                                color: 'white',
                                fontSize: 11,
                                fontWeight: 600,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>{item.value}</span>
                        )
                    })}
                </div>
            </div>
        )
    }

    const getCalendar = () => {
        return (
            <div style={{
                paddingRight: 24,
                width: '100%'
            }}>
                <WeekCalendar
                defaultDate={targetDate}
                draggable={true}
                events={getEvents()}
                onEventRender={onEventRender}
                utils={utils} />
            </div>
        )
    }

    const getEvents = () => {
        return events.filter(event => {
            if(selectedLead && event.extendedProps.event.object.lead_id !== selectedLead.id) {
                return false;
            }
            if(selectedUser && event.extendedProps.event.object.user_id !== selectedUser.user_id) {
                return false;
            }
            return true;
        });
    }

    const fetchEvents = async () => {
        try {

            // fetch calendar events from server
            setLoading(true);
            let { calls } = await Request.get(utils, '/clients/', {
                end_date: moment(targetDate).utc().endOf('week').format('YYYY-MM-DD'),
                start_date: moment(targetDate).utc().startOf('week').format('YYYY-MM-DD'),
                type: 'outbound_calls_calendar'
            });

            setLoading(false);
            setEvents(calls.map(call => {
                return {
                    end: moment.utc(call.created_at).add(call.duration, 'seconds'),
                    eventResourceEditable: true,
                    eventStartEditable: true,
                    extendedProps: {
                        event: {
                            object: call,
                            type: 'outbound_calls'
                        }
                    },
                    id: call.id,
                    start: moment.utc(call.created_at),
                    status: call.status,
                    type: 'outbound_calls'
                }
            }));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the appointments calendar. ${e.message || 'An unknown error occurred'}`
            });
        }
    }


    useEffect(() => {
        fetchEvents();
    }, [targetDate]);

    useEffect(() => {

        utils.events.on(panelID, 'client_change', fetchEvents);
        utils.events.on(panelID, 'client_preferences_update', fetchEvents);
        
        utils.content.subscribe(panelID, ['outbound_calls'], {
            onFetch: fetchEvents,
            onUpdate: onContentUpdate
        });
        return () => {

            // remove content subscriptions and event subscriptions
            utils.content.unsubscribe(panelID);
            utils.events.off(panelID, 'client_change', fetchEvents);
            utils.events.off(panelID, 'client_preferences_update', fetchEvents);
        }
    }, [])

    return (
        <Panel
        index={index}
        utils={utils}
        title={`Outbound Calls Calendar: ${timezone?.replace('_', ' ') || ''}`}
        options={{
            ...options,
            loading: loading === true,
            onRefreshContent: {
                onClick: fetchEvents
            },
        }}>
            <PermissionsContainer
            permission={'calls.calendar'} 
            utils={utils}>
                <div className={'row p-0 m-0'}>
                    <div
                    ref={contentContainer}
                    className={'col-9 p-0'}
                    style={{
                        height: '100%'
                    }}>
                        {getCalendar()}
                    </div>
                    <div
                    className={'col-3 p-0'}
                    style={{
                        maxHeight: 940,
                        overflowY: 'scroll',
                        position: 'relative'
                    }}>
                        <div 
                        className={'p-3 mb-3'}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            width: '100%'
                        }}>
                            <LayerItem
                            collapsed={false}
                            lastItem={true}
                            title={'Date'}>
                                <DatePickerField
                                blockStyle={'week'}
                                insetLabel={'The Week of'}
                                highlightDates={date => {
                                    return date.unix() >= moment(targetDate).startOf('week').add(1, 'days') && date.unix() <= moment(targetDate).endOf('week').add(1, 'days')
                                }}
                                onDateChange={setTargetDate}
                                selected={targetDate}
                                utils={utils} />
                            </LayerItem>
                        </div>
                        <div 
                        className={'p-3 mb-3'}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            width: '100%'
                        }}>
                            <LayerItem
                            collapsed={false}
                            lastItem={true}
                            title={'Timezone'}>
                                <TimezoneLookupField
                                onChange={setTimezone}
                                utils={utils} 
                                value={timezone} />
                            </LayerItem>
                        </div>
                        <div 
                        className={'p-3 mb-3'}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            width: '100%'
                        }}>
                            <LayerItem
                            collapsed={false}
                            lastItem={true}
                            title={'Lead Filter'}>
                                <LeadLookupField
                                onChange={setSelectedLead}
                                removable={true}
                                utils={utils} 
                                value={selectedLead} />
                            </LayerItem>
                        </div>
                        <div 
                        className={'p-3 mb-3'}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            width: '100%'
                        }}>
                            <LayerItem
                            collapsed={false}
                            lastItem={true}
                            title={'User Filter'}>
                                <UserLookupField
                                onChange={setSelectedUser}
                                removable={true}
                                utils={utils} 
                                value={selectedUser} />
                            </LayerItem>
                        </div>
                    </div>
                </div>
            </PermissionsContainer>
        </Panel>
    )
}

export const OutboundCallLogs = ({ index, options, utils }) => {

    const panelID = `outbound_call_logs`;
    const limit = 15;
    const offset = useRef(0);
    const sorting = useRef({});

    const _filterProps = useRef({});
    const textTimeout = useRef(null);
    const _searchProps = useRef({ text: null });

    const [callLogs, setOutboundCalls] = useState([]);
    const [filterProps, setFilterProps] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);
    const [results, setResults] = useState([]);
    const [searchProps, setSearchProps] = useState({});
    const [selecting, setSelecting] = useState(false);
    const [selected, setSelected] = useState([]);
    const [selectedOffset, setSelectedOffset] = useState(0);
    const [selectedPaging, setSelectedPaging] = useState(null);

    const onCallClick = log => {
        utils.layer.open({
            abstract: Abstract.create({
                object: log,
                type: 'outbound_calls'
            }),
            Component: OutboundCallDetails,
            id: `outbound_call_details_${log.id}`,
            permissions: ['calls.details']
        });
    }

    const onClearCalls = () => {
        utils.alert.show({
            title: 'Clear Outbound Calls',
            message: 'Are you sure that you want to clear your list of selected calls?',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setSelected([]);
                    return;
                }
            }
        });
    }

    const onClick = call => {

        // add to list of selected leads if "selecting" is enabled
        if(selecting) {
            if(selected.find(prev => prev.id === call.id)) {
                return;
            }
            let next = update(selected, { $unshift: [call] });
            setSelected(next);
            return;
        }

        // open details for lead if "selecting" is not enabled
        utils.layer.open({
            abstract: Abstract.create({
                object: call,
                type: 'outbound_calls'
            }),
            Component: OutboundCallDetails,
            id: `outbound_call_details_${call.id}`,
            permissions: ['calls.details']
        })
    }

    const onContinueClick = evt => {
        utils.alert.dev();
    }

    const onRemoveCall = call => {
        let next = update(selected, {
            $apply: calls => calls.filter(prev => {
                return prev.id !== call.id;
            })
        });
        setSelected(next);
    }

    const onSelectAllResults = async () => {
        try {
            let results = await fetchAllResults();
            setSelected(calls => {
                return calls.concat(results.filter(call => {
                    return calls.find(c => c.id === call.id) ? false : true;
                }));
            });
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving your search results. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onShowFilters = () => {
        utils.layer.open({
            id: 'outbound_call_filters',
            Component: OutboundCallFilters.bind(this, {
                onChange: setFilterProps,
                value: filterProps
            })
        });
    }

    const onTextChange = text => {

        // clear previous search timeout if applicable
        if(textTimeout.current) {
            clearTimeout(textTimeout.current);
        }

        // start loading and update search props for text search
        setLoading(true);
        onUpdateSearchProps('text', text);
        textTimeout.current = setTimeout(fetchResults, 250);
    }

    const onUpdateSearchProps = (key, value) => {
        let nextProps = update(_searchProps.current, {
            [key]: {
                $set: value
            }
        });
        _searchProps.current = nextProps;
        setSearchProps(nextProps);
        fetchResults();
    }

    const getButtons = () => {
        return [{
            key: 'selecting',
            title: `${selecting ? 'Select One' : 'Select Batches'}`,
            style: selecting ? 'cancel' : 'default',
            onClick: () => setSelecting(val => !val)
        }];
    }

    const getCalls = () => {
        if(results.length === 0) {
            return (
                Views.entry({
                    bottomBorder: false,
                    hideIcon: true,
                    subTitle: 'No outbound calls were found in the system',
                    title: 'No Outbound Calls Found'
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <thead style={{
                    width: '100%'
                }}>
                    {getFields()}
                </thead>
                <tbody style={{
                    width: '100%'
                }}>
                    {results.map(getFields)}
                </tbody>
            </table>
        )
    }

    const getContent = () => {

        // return standard list of calls if "selecting" is not enabled
        if(selecting === false) {
            return (
                <div className={`col-12 p-0 m-0`}>
                    <div style={Appearance.styles.unstyledPanel()}>
                        {getCalls()}
                        <PageControl
                        data={paging}
                        limit={limit}
                        offset={offset.current}
                        onClick={next => {
                            offset.current = next;
                            fetchResults();
                        }} />
                    </div>
                </div>
            )
        }

        // return selection resources if "selecting" is enabled
        return (
            <>
            <div className={`col-12 col-md-6 col-lg-8 pr-md-1 p-0 m-0`}>
                <div style={Appearance.styles.unstyledPanel()}>
                    {getCalls()}
                    <PageControl
                    data={paging}
                    limit={limit}
                    offset={offset.current}
                    onClick={next => {
                        offset.current = next;
                        fetchResults();
                    }} />
                </div>
            </div>

            <div className={'col-12 col-md-6 col-lg-4 p-0 pl-md-1 m-0'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    height: '100%'
                }}>
                    <div
                    className={'px-3 py-2'}
                    style={{
                        borderBottom: `1px solid ${Appearance.colors.divider()}`
                    }}>
                        <span style={{
                            ...Appearance.textStyles.title(),
                        }}>{'Selected Calls'}</span>
                    </div>
                    <div style={{
                        flexGrow: 1
                    }}>
                        {getSelectedCalls()}
                    </div>
                    <PageControl
                    data={selectedPaging}
                    limit={limit}
                    offset={offset.current}
                    onClick={next => {
                        offset.current = next;
                        fetchResults();
                    }} />

                    <div
                    className={'row m-0'}
                    style={{
                        borderTop: `1px solid ${Appearance.colors.divider()}`,
                        padding: 12,
                        width: '100%'
                    }}>
                        {getSelectionButtons()}
                    </div>
                </div>
            </div>
            </>
        )
    }

    const getFields = (call, index, calls) => {

        let target = call || {};
        let fields = [{
            key: 'id',
            title: 'ID',
            value: target.id
        },{
            key: 'user',
            title: 'Called By',
            value: target.user ? target.user.full_name : null
        },{
            key: 'lead_full_name',
            title: 'Lead',
            value: target.lead_full_name
        },{
            key: 'created_at',
            title: 'Date',
            value: target.created_at ? Utils.formatDate(target.created_at) : null
        },{
            key: 'duration',
            title: 'Duration',
            value: target.duration ? Utils.parseDuration(target.duration) : null
        },{
            key: 'to_number',
            title: 'Phone Number',
            value: target.to_number ? Utils.formatPhoneNumber(target.to_number) : null
        },{
            key: 'status',
            title: 'Call Status',
            value: target.status ? Utils.ucFirst(target.status) : null
        },{
            key: 'lead_status_text',
            title: 'Lead Status',
            value: target.lead_status_text
        }];

        // create table headers with custom sorting options
        // conform external sort to match internal header sort if applicable
        if(!call) {
            return (
                <TableListHeader
                fields={fields}
                onChange={props => {
                    sorting.current = props;
                    fetchResults();
                    fetchOutboundCalls();
                }} 
                value={sorting.current} />
            )
        }

        // loop through result rows
        return (
            <tr
            key={index}
            className={`view-entry ${window.theme}`}
            style={{
                borderBottom: index !== calls.length - 1 && `1px solid ${Appearance.colors.divider()}`
            }}>
                {fields.map((field, index) => {
                    return (
                        <td
                        key={index}
                        className={'px-3 py-2 flexible-table-column'}
                        onClick={onClick.bind(this, call)}>
                            <span style={Appearance.textStyles.subTitle()}>{field.value}</span>
                        </td>
                    )
                })}
            </tr>
        )
    }

    const getSelectedCalls = () => {
        if(selected.length === 0) {
            return (
                <div
                className={'px-3 py-2'}
                style={{
                    alignItems: 'center',
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    display: 'flex',
                    flexDirection: 'row',
                    height: 36,
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        flexGrow: 1
                    }}>{'No Outbound Calls Selected'}</span>
                </div>
            )
        }

        return selected.filter((_, index) => {

            if(selectedPaging.number_of_pages === 1) {
                return true;
            }
            let min = (limit * selectedPaging.current_page) - limit;
            let max = limit * selectedPaging.current_page;
            return index >= min && index < max;

        }).map((call, index) => {
            return (
                <div
                key={index}
                className={'px-3 py-2'}
                style={{
                    alignItems: 'center',
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    display: 'flex',
                    flexDirection: 'row',
                    height: 36,
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        flexGrow: 1
                    }}>{call.lead_full_name}</span>
                    <img
                    className={'text-button'}
                    src={'images/red-x-icon.png'}
                    onClick={onRemoveCall.bind(this, call)}
                    style={{
                        height: 15,
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 15
                    }}/>
                </div>
            )
        });
    }

    const getSelectionButtons = () => {

        let all_selected = selected.length === callLogs.length;
        return (
            <>
            {!all_selected && (
                <div className={selected.length > 0 ? `col-12 ${all_selected ? 'col-md-6' : 'col-md-3'} p-1 px-md-1 py-md-0` : 'col-12 p-1'}>
                    <Button
                    type={'large'}
                    color={'secondary'}
                    label={selected.length > 0 ? 'Select All' : 'Select All Search Results'}
                    loading={loading === 'results'}
                    onClick={onSelectAllResults}/>
                </div>
            )}
            {selected.length > 0 && (
                <>
                <div className={`col-12 ${all_selected ? 'col-md-6' : 'col-md-3'} p-1 px-md-1 py-md-0`}>
                    <Button
                    type={'large'}
                    color={'dark'}
                    label={`Clear`}
                    onClick={onClearCalls}/>
                </div>
                <div className={'col-12 col-md-6 p-1 px-md-1 py-md-0'}>
                    <Button
                    type={'large'}
                    color={'primary'}
                    label={`Continue with ${Utils.softNumberFormat(selected.length)} ${selected.length === 1 ? 'Entry' : 'Entries'}`}
                    onClick={onContinueClick}/>
                </div>
                </>
            )}
            </>
        )
    }

    const fetchAllResults = async () => {
        return new Promise(async (resolve, reject) => {
            try {

                setLoading('results');
                await Utils.sleep(0.5);

                let { calls } = await Request.post(utils, '/clients/', {
                    type: 'outbound_call_advanced_search',
                    search_props: _searchProps.current,
                    ...sorting.current
                });

                setLoading(false);
                resolve(calls.map(call => OutboundCall.create(call)));

            } catch(e) {
                setLoading(false);
                reject(e);
            }
        });
    };

    const fetchResults = async () => {
        try {
            let { calls, paging } = await Request.post(utils, '/clients/', {
                filter_props: _filterProps.current,
                limit: limit,
                offset: offset.current,
                search_props: _searchProps.current,
                type: 'outbound_call_advanced_search',
                ...sorting.current
            });
            setLoading(false);
            setPaging(paging);
            setResults(calls.map(call => OutboundCall.create(call)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the outbound calls list. ${e.message || 'An unknown error occurred'}`
            });
        }
    };

    const fetchOutboundCalls = async () => {
        try {

            let { calls } = await Request.get(utils, '/clients/', {
                search_text: _searchProps.current.text,
                type: 'outbound_calls',
                ...sorting.current
            });
            setOutboundCalls(calls.map(call => OutboundCall.create(call)));

        } catch(e) {
            _searchProps.current.in_progress = false;
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the list of outbound calls. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        setLoading(true);
        fetchResults();
    }, [filterProps]);

    useEffect(() => {
        _filterProps.current = filterProps || {};
    }, [filterProps]);

    useEffect(() => {
        _searchProps.current = searchProps || {};
    }, [searchProps]);

    useEffect(() => {
        fetchOutboundCalls();
    }, [_searchProps.current.text]);

    useEffect(() => {
        setSelectedPaging({
            current_page: (selectedOffset / limit) + 1,
            number_of_pages: selected.length > limit ? Math.ceil(selected.length / limit) : 1
        })
    }, [selected, selectedOffset]);

    useEffect(() => {
        fetchOutboundCalls();
        utils.content.subscribe(panelID, ['outbound_calls'], {
            onFetch: () => {
                fetchResults();
                fetchOutboundCalls();
            },
            onUpdate: () => {
                fetchResults();
                fetchOutboundCalls();
            }
        });
        return () => {
            utils.content.unsubscribe(panelID);
        }
    }, []);

    return (
        <Panel
        index={index}
        title={'Outbound Calls'}
        utils={utils}
        options={{
            ...options,
            buttons: getButtons(),
            loading: loading === true,
            onRefreshContent: {
                onClick: () => {
                    setLoading(true);
                    fetchResults();
                }
            },
            search: {
                placeholder: 'Search by first or last name...',
                onChange: onTextChange,
                filters: {
                    onClick: onShowFilters,
                    values: filterProps
                }
            },
        }}>
            <div
            className={'row p-0 m-0'}
            style={{
                width: '100%'
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const Leads = ({ filter_props, id, name, props }, { index, options, utils }) => {

    const filterProps = useRef(filter_props || {});
    const hasAutoFetched = useRef(false);
    const leadAutoRefetch = useRef(null);
    const limit = 15;
    const loadingRef = useRef(false);
    const offset = useRef(0);
    const searchProps = useRef({});
    const _selecting = useRef(false);
    const sorting = useRef({ 
        sort_key: 'date', 
        sort_type: Content.sorting.type.descending 
    });
    const _transferDirection = useRef('inbound');

    const [allSelected, setAllSelected] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);
    const [results, setResults] = useState([]);
    const [selecting, setSelecting] = useState(false);
    const [selected, setSelected] = useState([]);
    const [selectedOffset, setSelectedOffset] = useState(0);
    const [selectedPaging, setSelectedPaging] = useState(null);
    const [showOptions, setShowOptions] = useState(false);
    const [transferDirection, setTransferDirection] = useState('inbound');
    const [updateAvailable, setUpdateAvailable] = useState(false);

    const onCancelTransfers = () => {
        utils.alert.show({
            title: 'Cancel Transfers',
            message: `Are you sure that you want to cancel these transfers? This will cancel all selected transfers that have not been accepted or declined.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Do Not Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onCancelTransfersConfirm();
                    return;
                }
            }
        });
    }

    const onCancelTransfersConfirm = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);
            let { count } = await Request.post(utils, '/leads/', {
                type: 'set_batch_transfer_status',
                transfer_ids: selected.map(lead => lead.transfer.id),
                status: Lead.Transfer.status.inactive
            });

            setLoading(false);
            utils.banner.show({
                title: 'Transfers Cancelled',
                message: `The transfers for ${count === 1 ? 'this Lead has' : `these Leads have`} been cancelled.`,
                onShow: () => utils.content.fetch('leads')
            })

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue cancelling these transfers. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onClearLeads = () => {
        utils.alert.show({
            title: 'Clear Leads',
            message: 'Are you sure that you want to clear your list of selected Leads?',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setSelected([]);
                    setAllSelected(false);
                    return;
                }
            }
        });
    }

    const onContentClick = () => {
        setShowOptions(false);
    }

    const onContinueClick = evt => {

        // show specialty options if the batches of leads are part of a lead transfer
        if(props && props.transfers) {
            utils.sheet.show({
                items: [{
                    key: 'accepted',
                    title: 'Accept Leads',
                    style: 'default',
                    visible: transferDirection === 'inbound'
                },{
                    key: 'declined',
                    title: 'Decline Leads',
                    style: 'destructive',
                    visible: transferDirection === 'inbound'
                },{
                    key: 'inactive',
                    title: 'Cancel Transfers',
                    style: 'destructive',
                    visible: transferDirection === 'outbound'
                }], 
                target: evt.target
            }, key => {
                if(key === 'inactive') {
                    onCancelTransfers();
                    return;
                }
                if(key === 'accepted' || key === 'declined') {
                    onSetTransferStatus(Lead.Transfer.status[key]);
                    return;
                }
            });
            return;
        }

        // fallback to presenting default lead batch options
        utils.layer.open({
            Component: BatchLeadOptions.bind(this, {
                filterProps: onFormatFilterProps(),
                leads: selected,
                onChange: onResetBatchSelection,
                props: props,
                selectAll: allSelected
            }),
            id: 'batch_lead_options'
        });
    }

    const onClientChange = () => {
        conditionalLeadFetch();
        reconnectSockets();
    }

    const onDownloadLeads = async () => {
        try {
            setLoading(true);
            let { url } = await Request.post(utils, '/leads/', {
                all_leads: true,
                type: 'download'
            });

            setLoading(false);
            window.open(url);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing your download. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onFormatFilterProps = () => {

        // no additional logic is required if no filter props are available
        if(!filterProps.current) {
            return {};
        }

        // remove unsupported request keys from props
        let props = update(filterProps.current, {
            $unset: ['referred_by_lead']
        });

        // prepare base level props
        let { poi, radius, selection } = filterProps.current.location_radius || {};
        if(poi && radius) {
            props.location_radius = {
                coordinates: {
                    lat: poi.location.latitude,
                    long: poi.location.longitude
                },
                poi: poi,
                radius: radius,
                selection: selection
            }
        }

        // determine if a referral lead id needs to be prepare
        if(filterProps.current.referred_by_lead) {
            props.referred_by_lead_id = filterProps.current.referred_by_lead.id;
        }
        return props;
    }

    const onFormatSearchProps = () => {

        // remove in_progress flag before sending search props to sever
        return update(searchProps.current || {}, {
            $unset: ['in_progress']
        });
    }

    const onLeadCandidateClick = lead => {

        // add to list of selected leads if "selecting" is enabled
        if(selecting) {
            if(selected.find(l => l.id === lead.id)) {
                return;
            }
            let nextLeads = update(selected, { $unshift: [lead] });
            setSelected(nextLeads);
            setAllSelected(false);
            return;
        }

        // fallback to a normal lead click
        onLeadClick(lead);
    }

    const onLeadClick = async target => {
        try {

            // prevent moving forward if feature is disabled for current user
            if(utils.user.permissions.get('leads.details') === false) {
                return utils.user.permissions.reject();
            }
            
            // fetch details for lead
            // only partial details are included through the filter based search for performance purposes
            // port over transfer information if applicable
            setLoading(target.id);
            let lead = await Lead.get(utils, target.id);

            // set transfer object 
            setLoading(false);
            lead.transfer = target.transfer;

            // open details and lead script if lead script is available and current user level matches valid levels
            if(lead.lead_script && [User.levels.get().setter, User.levels.get().sales_ops].includes(utils.user.get().level)) {

                // fetch details for lead script
                let script = await Lead.Script.get(utils, lead.lead_script.id);

                // send request to open multiple layers
                utils.layer.openMultiple([{
                    abstract: Abstract.create({
                        object: lead,
                        type: 'leads'
                    }),
                    Component: LeadDetails,
                    id: `lead_details_${lead.id}`,
                    permissions: ['leads.details']
                },{
                    Component: LeadScriptEditor.bind(this, {
                        utils: utils,
                        lead: lead,
                        value: script.text
                    }),
                    id: `lead_script_editor_${lead.id}`
                }]);
                return;
            }

            // open standard details for lead
            utils.layer.open({
                abstract: Abstract.create({
                    object: lead,
                    type: 'leads',
                }),
                Component: LeadDetails,
                id: `lead_details_${lead.id}`,
                permissions: ['leads.details']
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this lead. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onLeadListDataChange = evt => {
        try {

            // no additional logic is needed if the update is not meant for this panel
            let { initiated_by_user_id, key } = evt;
            if(!props.cache_key || key !== `list.${props.cache_key}` || loadingRef.current === true) {
                return;
            }

            // prevent a duplicate fetch if loading is already in progress
            if(loadingRef.current === true) {
                console.warn(`[leads.list.${props.cache_key}]: load already in progress`);
                return;
            }

            // automatically update panel contents if the change was made by the current user
            // otherwise show a button on the panel that allows the user to manually refresh the contents of the panel
            if(initiated_by_user_id === utils.user.get().user_id) {
                console.log(`[leads.list.${props.cache_key}]: requesting auto fetch for user who initiated data change`);
                conditionalLeadFetch();
            } else if(shouldAutoFetch() === true) {
                console.log(`[leads.list.${props.cache_key}]: update available`);
                setUpdateAvailable(true);
            }

        } catch(e) {
            console.error(`[on_lead_list_data_change]: ${e.message}`);
        }
    }

    const onNewLead = () => {

        // prepare new lead target and set attribution user
        let lead = Lead.new();
        lead.user = utils.user.get();

        // open lead editing layer
        utils.layer.open({
            abstract: Abstract.create({
                object: lead,
                type: 'leads'
            }),
            Component: AddEditLead.bind(this, {
                isNewTarget: true
            }),
            id: 'new_lead'
        });
    }

    const onRemoveLead = (lead, evt) => {
        
        evt.stopPropagation();
        let nextLeads = update(selected, {
            $apply: leads => leads.filter(prevLead => {
                return prevLead.id !== lead.id;
            })
        });
        setAllSelected(false);
        setSelected(nextLeads);
    }

    const onResetBatchSelection = () => {
        onSetSelectingFlag(false);
        setSelected([]);
    }

    const onResetLeadFilters = () => {
        filterProps.current = {};
        offset.current = 0;
        fetchResults();
    }

    const onSelectAllResults = async () => {
        try {

            // prevent moving forward if too many leads are targeted
            if(paging && paging.total > 50000) {
                utils.alert.show({
                    title: 'Just a Second',
                    message: 'It looks like the list that you are working with is too large. Please use filters to reduce you list to a total of 50,000 leads or less to continue.'
                });
                return;
            }

            // fetch all matching leads from the server
            let results = await fetchAllResults({ 
                cache_key: null,
                mass_selection: true 
            });

            // update selection flag and selection paging
            setAllSelected(true);
            setSelectedPaging({
                current_page: (selectedOffset / limit) + 1,
                number_of_pages: results.length > limit ? Math.ceil(results.length / limit) : 1
            });

            // update local state with selected leads
            setSelected(leads => {
                return leads.concat(results.filter(lead => {
                    return leads.find(p => p.id === lead.id) ? false : true;
                }))
            })
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving your search results. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSetSelectingFlag = val => {

        // update selecting ref
        let next = val === true || val === false ? val : !_selecting.current;
        _selecting.current = next;

        // update selecting state and fetch results
        setSelecting(next);
    }

    const onSetTransferStatus = status => {
        utils.alert.show({
            title: `${status === Lead.Transfer.status.accepted ? 'Accept' : 'Decline'} Lead`,
            message: `Are you sure that you want to ${status === Lead.Transfer.status.accepted ? 'accept' : 'decline'} these leads? ${status === Lead.Transfer.status.accepted ? `This will add all of the in-progress leads to your client workspace and remove them from the client workspace who sent them` : `This will return the leads back to the client workspace who sent them`}`,
            buttons: [{
                key: 'confirm',
                title: status === Lead.Transfer.status.accepted ? 'Accept' : 'Decline',
                style: status === Lead.Transfer.status.accepted ? 'default' : 'destructive'
            },{
                key: 'cancel',
                title: `Do Not ${status === Lead.Transfer.status.accepted ? 'Accept' : 'Decline'}`,
                style: status === Lead.Transfer.status.accepted ? 'destructive' : 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSetTransferStatusConfirm(status);
                    return;
                }
            }
        });
    }

    const onSetTransferStatusConfirm = async code => {
        try {

            // set loading flag and small timeout to prevent alert overlaps
            setLoading(true);
            await Utils.sleep(0.25);

            // prepare target leads for transfer
            let targets = selected.filter(lead => lead.transfer_id);
            if(targets.length === 0) {
                throw new Error('It looks like all the selected leads have already been accepted, declined, or deactivated');
            }

            // send transfer request to server
            let { count } = await Request.post(utils, '/leads/', {
                type: 'set_batch_transfer_status',
                transfer_ids: targets.map(lead => lead.transfer_id),
                status: code
            });

            setLoading(false);
            onSetSelectingFlag(false);
            utils.banner.show({
                title: 'Transfers Processed',
                message: `${count === 1 ? '1 lead has' : `${count} leads have`} been ${code === Lead.Transfer.status.accepted ? `accepted and added to your client workspace` : `declined and returned back to the client workspace who sent them`}`,
                onShow: utils.content.fetch.bind(this, 'lead')
            })

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${code === Lead.Transfer.status.accepted ? 'accepting' : 'declining'} these leads from your client workspace. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onShowDuplicates = () => {
        utils.layer.open({
            Component: LeadDuplicates,
            id: 'lead_duplicates',
            permissions: ['leads.duplicates']
        });
    }

    const onShowLeadFilters = () => {
        utils.layer.open({
            id: 'lead_filters',
            Component: LeadFilters.bind(this, {
                value: filterProps.current,
                onChange: result => {

                    // update filter props ref and reset offset ref
                    filterProps.current = result;
                    offset.current = 0;

                    // fetch new lead results
                    fetchResults();
                }
            })
        });
    }

    const onTextChange = text => {
        offset.current = 0;
        setLoading(true);
        onUpdateSearchProps('text', text);
    }

    const onUpdateSearchProps = (key, value) => {
        let nextProps = update(searchProps.current, {
            ...value && {
                [key]: {
                    $set: value
                }
            },
            ...value === null && {
                $unset: [key]
            }
        });
        searchProps.current = nextProps;
        fetchResults();
    }

    const getButtons = () => {

        // prepare specialty buttons if the batches of leads are part of a lead transfer
        if(props && props.transfers) {
            return [{
                key: 'toggle',
                title: `Show ${transferDirection === 'outbound' ? 'Inbound' : 'Outbound'}`,
                style: 'default',
                onClick: () => {
                    let next = transferDirection === 'outbound' ? 'inbound' : 'outbound';
                    _transferDirection.current = next;
                    setTransferDirection(next);
                    fetchResults();
                }
            },{
                key: 'selecting',
                title: `${selecting ? 'Select One' : 'Select Batches'}`,
                style: selecting ? 'cancel' : 'secondary',
                onClick: onSetSelectingFlag,
                visible: results.length > 0
            }]
        }

        // prepare items list for options click
        return [{
            key: 'download',
            onClick: onDownloadLeads,
            permissions: ['leads.actions.download'],
            title: 'Download',
            style: 'dark-grey',
            visible: false // utils.user.get().level <= User.levels.get().primary && props.cache_key === 'general' ? true : false
        },{
            key: 'duplicates',
            onClick: onShowDuplicates,
            permissions: ['leads.duplicates'],
            title: 'Manage Duplicates',
            style: 'default',
            visible: props?.manage_duplicates === true
        },{
            key: 'new',
            permissions: ['leads.actions.new'],
            title: 'New Lead',
            style: 'default',
            onClick: onNewLead
        },{
            key: 'selecting',
            title: `${selecting ? 'Select One' : 'Select Batches'}`,
            style: selecting ? 'cancel' : 'secondary',
            onClick: onSetSelectingFlag,
            visible: results.length > 0
        }];
    }

    const getContent = () => {

        // determine if results have been auto fetched
        if(hasAutoFetched.current === false) {

            // return nothing if panel supports auto fetch but the flag has not yet been updated
            if(shouldAutoFetch() === true) {
                return null;
            }

            // return placeholder if auto leads fetch has not taken place 
            return (
                <div 
                className={'text-button'}
                onClick={() => {
                    hasAutoFetched.current = true;
                    fetchResults();
                }}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 12,
                    width: '100%'
                }}>
                    <img 
                    src={window.theme === 'dark' ? 'images/lead-panel-click-white.png' : 'images/lead-panel-click-grey.png'}
                    style={{
                        height: 50,
                        marginBottom: 8,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <span style={{
                        ...Appearance.textStyles.title(),
                        marginBottom: 2,
                        textAlign: 'center'
                    }}>{'Click to Load Leads'}</span>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        maxWidth: 250,
                        textAlign: 'center',
                        whiteSpace: 'wrap'
                    }}>{'We automatically hide certain lead lists to improve the overall performance.'}</span>
                </div>
            )
        }

        // return standard list of leads if "selecting" is not enabled
        if(selecting === false) {
            return (
                <div className={`col-12 p-0 m-0`}>
                    <div style={Appearance.styles.unstyledPanel()}>
                        {getLeads()}
                        <PageControl
                        data={paging}
                        limit={limit}
                        offset={offset}
                        onClick={next => {
                            offset.current = next;
                            fetchResults();
                        }}/>
                    </div>
                </div>
            )
        }

        // return selection resources if "selecting" is enabled
        return (
            <>
            <div className={`col-12 col-md-6 col-lg-7 pr-md-1 p-0 m-0`}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    height: '100%'
                }}>
                    {getLeads()}
                    <PageControl
                    data={paging}
                    limit={limit}
                    offset={offset}
                    onClick={next => {
                        offset.current = next;
                        fetchResults();
                    }}/>
                </div>
            </div>

            <div className={'col-12 col-md-6 col-lg-5 p-0 pl-md-1 m-0'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden'
                }}>
                    <div
                    className={'px-3 py-2'}
                    style={{
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        marginTop: 1
                    }}>
                        <span style={{
                            ...Appearance.textStyles.subTitle(),
                            color: Appearance.colors.text(),
                            fontWeight: Appearance.textStyles.subTitle().fontWeight + 100
                        }}>{'Selected Leads'.toUpperCase()}</span>
                    </div>
                    <div style={{
                        flexGrow: 1
                    }}>
                        {getSelectedLeads()}
                    </div>
                    <PageControl
                    data={selectedPaging}
                    limit={limit}
                    offset={selectedOffset}
                    onClick={nextOffset => setSelectedOffset(nextOffset)}/>

                    <div
                    className={'row m-0'}
                    style={{
                        borderTop: `1px solid ${Appearance.colors.divider()}`,
                        padding: 12,
                        width: '100%'
                    }}>
                        {getSelectionButtons()}
                    </div>
                </div>
            </div>
            </>
        )
    }
    
    const getLeads = () => {
        if(results.length === 0) {
            return (
                Views.entry({
                    bottomBorder: false,
                    hideIcon: true,
                    subTitle: 'No leads were found from your search',
                    title: 'No Leads Found'
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <thead style={{
                    width: '100%'
                }}>
                    <LeadTableEntry
                    category={id}
                    key={index}
                    props={props}
                    onSortingChange={props => {
                        sorting.current = props;
                        fetchResults();
                    }}
                    sorting={sorting.current}
                    utils={utils} />
                </thead>
                <tbody style={{
                    width: '100%'
                }}>
                    {results.map((lead, index, leads) => {
                        return (
                            <LeadTableEntry
                            category={id}
                            isLastItem={selecting ? false : index === leads.length - 1}
                            key={index}
                            lead={lead}
                            loading={loading === lead.id} 
                            onLeadClick={onLeadCandidateClick}
                            props={props}
                            utils={utils} />
                        )
                    })}
                </tbody>
            </table>
        )
    }

    const getPanelName = () => {

        // return panel name as-is without modifications if panel is not meant for lead transfers
        if(!props || !props.transfers) {
            return name || 'Leads';
        }

        // return transfer name with direction if panel content has been fetched, otherwise return name without direction
        return hasAutoFetched.current === true ? `${Utils.ucFirst(transferDirection)} ${name}` : name;
    }

    const getSelectionButtons = () => {
        return (
            <>
            {allSelected === false && (
                <div className={selected.length > 0 ? `col-12 ${allSelected ? 'col-md-6' : 'col-md-3'} p-1 px-md-1 py-md-0` : 'col-12 p-1'}>
                    <Button
                    type={'large'}
                    color={'secondary'}
                    label={selected.length > 0 ? 'Select All' : 'Select All Search Results'}
                    loading={loading === 'results'}
                    onClick={onSelectAllResults}/>
                </div>
            )}
            {selected.length > 0 && (
                <>
                <div className={`col-12 ${allSelected ? 'col-md-6' : 'col-md-3'} p-1 px-md-1 py-md-0`}>
                    <Button
                    type={'large'}
                    color={'dark'}
                    label={`Clear`}
                    onClick={onClearLeads}/>
                </div>
                <div className={'col-12 col-md-6 p-1 px-md-1 py-md-0'}>
                    <Button
                    type={'large'}
                    color={'primary'}
                    label={`Continue with ${Utils.softNumberFormat(selected.length)} ${selected.length === 1 ? 'Lead' : 'Leads'}`}
                    onClick={onContinueClick}/>
                </div>
                </>
            )}
            </>
        )
    }

    const getSelectedLeads = () => {
        if(selected.length === 0) {
            return (
                <div
                className={'px-3 py-2'}
                style={{
                    alignItems: 'center',
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    display: 'flex',
                    flexDirection: 'row',
                    height: 36,
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        flexGrow: 1
                    }}>{'No Leads Selected'}</span>
                </div>
            )
        }

        return selected.filter((_, index) => {
            if(selectedPaging.number_of_pages === 1) {
                return true;
            }
            let min = (limit * selectedPaging.current_page) - limit;
            let max = limit * selectedPaging.current_page;
            return index >= min && index < max;

        }).map((lead, index) => {
            return (
                <div
                key={index}
                className={`view-entry ${window.theme} px-3 py-2`}
                onClick={onLeadClick.bind(this, lead)}
                style={{
                    alignItems: 'center',
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    display: 'flex',
                    flexDirection: 'row',
                    height: 36,
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        flexGrow: 1
                    }}>{lead.full_name || 'Customer name not available'}</span>
                    <img
                    className={'text-button'}
                    src={'images/red-x-icon.png'}
                    onClick={onRemoveLead.bind(this, lead)}
                    style={{
                        height: 15,
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 15
                    }}/>
                </div>
            )
        })
    }

    const getSubTitleProps = () => {
        if(updateAvailable) {
            return {
                onClick: fetchResults,
                style: {
                    ...Appearance.textStyles.subTitle(),
                    color: Appearance.colors.green,
                    fontWeight: 700
                },
                text: 'New Content Available'
            }
        }
        return {
            text: lastUpdated ? `Last Updated: ${Utils.formatDate(lastUpdated)}` : 'Awaiting content...'
        }
    }

    const conditionalLeadFetch = () => {

        // determine if panel allows an auto fetch of the lead list
        if(shouldAutoFetch() === true) {
            hasAutoFetched.current = true;
            fetchResults();
        }
    }

    const connectToSockets = async () => {
        try {
            await utils.sockets.persistOn('leads', 'on_lead_list_data_change', onLeadListDataChange);
        } catch(e) {
            console.error(e.message);
        }
    }

    const disconnectFromSockets = async () => {
        try {
            await utils.sockets.off('leads', 'on_lead_list_data_change', onLeadListDataChange);
        } catch(e) {
            console.error(e.message);
        }
    }

    const fetchAllResults = async data => {
        return new Promise(async (resolve, reject) => {
            try {
                setLoading('results');
                await Utils.sleep(0.5);

                let response = await Request.post(utils, '/leads/', {
                    filter_props: onFormatFilterProps(),
                    search_props: onFormatSearchProps(),
                    type: 'advanced_search',
                    ...props.cache_key === 'transfers' && {
                        transfer_direction: _transferDirection.current,
                    },
                    ...sorting.current,
                    ...props,
                    ...data
                });

                setLoading(false);
                resolve(response.leads);

            } catch(e) {
                setLoading(false);
                reject(e);
            }
        });
    };

    const fetchResults = async () => {
        try {

            // clear pending auto-refetch timeout if applicable
            if(leadAutoRefetch.current) {
                clearTimeout(leadAutoRefetch.current);
            }

            // set loading flag and submit request to server
            setLoading(true);
            let { last_updated, leads, paging } = await Request.post(utils, '/leads/', {
                filter_props: onFormatFilterProps(),
                limit: limit,
                offset: offset.current,
                search_props: onFormatSearchProps(),
                type: 'advanced_search',
                ...props.cache_key === 'transfers' && {
                    transfer_direction: _transferDirection.current,
                },
                ...sorting.current,
                ...props
            });
           
            // end loading and update local state with results
            setLastUpdated(last_updated && moment.utc(last_updated).local());
            setLoading(false);
            setPaging(paging);
            setUpdateAvailable(false);

            // update local state with formatted lead objects
            setResults(leads.map(lead => ({
                ...lead,
                adjusted_date: lead.adjusted_date && moment.utc(lead.adjusted_date).local(),
                created: lead.created && moment.utc(lead.created).local()
            })));

            // set timeout to re-fetch leads after 5 minutes have passed
            leadAutoRefetch.current = setTimeout(() => {
                console.log('auto-refetching leads...');
                conditionalLeadFetch();
            }, 300000);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the ${getPanelName().toLowerCase()} list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const reconnectSockets = async () => {
        try {
            await utils.sockets.off('leads', 'on_lead_list_data_change', onLeadListDataChange);
            await utils.sockets.on('leads', 'on_lead_list_data_change', onLeadListDataChange);
        } catch(e) {
            console.error(e.message);
        }
    }

    const setDefaultSortOrder = () => {
        if(props?.transfers) {
            sorting.current = { 
                sort_key: props && props.transfers ? 'transfer_date' : 'created' ,
                sort_type: Content.sorting.type.descending
            };
            return;
        }
        if(id === 'new_leads') {
            sorting.current = { 
                sort_key: 'adjusted_date' ,
                sort_type: Content.sorting.type.descending
            };
            return;
        }
        sorting.current = { 
            sort_key: 'created' ,
            sort_type: Content.sorting.type.descending
        };
    }

    const shouldAutoFetch = () => {

        // allow the panel to auto fetch leads if the user has manually shown the content
        if(hasAutoFetched.current === true) {
            return true;
        }

        // fallback to only allowing auto fetch for specific panels
        return ['archived_leads', 'do_not_call_leads', 'follow_up_leads', 'out_of_service_area_leads', 'transfer_leads', 'unreleased_leads'].includes(id) ? false : true;
    }

    useEffect(() => {
        loadingRef.current = loading;
    }, [loading]);

    useEffect(() => {
        setSelectedPaging({
            current_page: (selectedOffset / limit) + 1,
            number_of_pages: selected.length > limit ? Math.ceil(selected.length / limit) : 1
        })
    }, [selected, selectedOffset]);

    useEffect(() => {
        if(selecting === false) {
            setSelected([]);
        }
    }, [selecting]);

    useEffect(() => {

        // set default sort order for list type
        setDefaultSortOrder();

        // connect to lead list subscriptions
        connectToSockets();

        // determine if panel warrants and auto fetch of the lead list
        conditionalLeadFetch();

        // setup event and content listeners 
        utils.events.on(id, 'client_change', onClientChange);
        utils.events.on(id, 'client_preferences_update', conditionalLeadFetch);

        return () => {
            disconnectFromSockets();
            return () => {
                utils.events.off(id, 'client_change', onClientChange);
                utils.events.off(id, 'client_preferences_update', conditionalLeadFetch);
            }
        }
    }, []);

    return (
        <Panel
        index={index}
        title={getPanelName()}
        utils={utils}
        options={{
            ...options,
            buttons: getButtons(),
            loading: loading === true,
            onRefreshContent: {
                onClick: fetchResults,
                pulse: {
                    color: Appearance.colors.green,
                    enabled: updateAvailable
                }
            },
            total: paging && paging.total,
            search: hasAutoFetched.current === true && {
                onChange: text => onTextChange(text && text.length > 0 ? text : null),
                placeholder: 'Search by id, customer name, phone number, or assignment name...',
                filters: {
                    onClick: onShowLeadFilters,
                    onReset: onResetLeadFilters,
                    values: filterProps.current
                }
            },
            subTitle: getSubTitleProps()
        }}>
            <div
            onClick={showOptions ? onContentClick : null}
            className={'row p-0 m-0'}
            style={{
                width: '100%'
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const LeadLocations = ({ index, options, utils }) => {

    const panelID = 'lead_locations';
    const filterContainer = useRef(null);
    const [loading, setLoading] = useState(null);
    const [filters, setFilters] = useState(Object.values(Lead.status.get()));
    const [locationData, setLocationData] = useState(null);
    const [region, setRegion] = useState(null);

    const onLeadClick = async props => {
        try {
            let lead = await Lead.get(utils, props.id);
            utils.layer.open({
                abstract: Abstract.create({
                    object: lead,
                    type: 'leads'
                }),
                Component: LeadDetails,
                id: `lead_details_${lead.id}`,
                permissions: ['leads.details']
            })
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this lead. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const getFeatures = () => {
        if(!locationData) {
            return null;
        }
        return {
            id: 'lead_locations',
            region: region,
            onClick: onLeadClick,
            icons: [{
                key: 'location-icon-grey',
                path: 'images/location-icon-grey.png'
            }],
            data: {
                ...locationData,
                features: locationData.features.filter(feature => {
                    return filters.includes(feature.properties?.status);
                })
            },
            layer: {
                type: 'symbol',
                layout: {
                    'icon-size': 0.2,
                    'icon-anchor': 'center',
                    'icon-image': 'location-icon-grey',
                    'icon-allow-overlap': true
                },
                paint: {
                    'icon-color': [ 'get', 'color' ]
                }
            },
            onHover: feature => {
                return {
                    icon: {
                        imageStyle: {
                            backgroundColor: Appearance.colors.transparent,
                            borderRadius: 0
                        }
                    },
                    subTitle: feature.properties.status_text,
                    title: feature.properties.full_name
                }
            }
        }
    }

    const getFilters = () => {
        return (
            <StatusCodeFilters
            categories={['leads']}
            onChange={setFilters} 
            storage={'lead'}
            utils={utils}/>
        )
    }

    const fetchLocations = async () => {
        try {
            let { data, region } = await Request.get(utils, '/leads/', {
                type: 'locations'
            });

            setRegion(region);
            setLocationData(data);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the Leads map. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    useEffect(() => {
        fetchLocations();
        utils.events.on(panelID, 'client_change', fetchLocations);
        utils.events.on(panelID, 'client_preferences_update', fetchLocations);
        return () => {
            utils.events.off(panelID, 'client_change');
            utils.events.off(panelID, 'client_preferences_update', fetchLocations);
        }

    }, []);

    return (
        <Panel
        index={index}
        title={'Lead Locations'}
        utils={utils}
        options={{
            ...options,
            loading: loading,
        }}>
            <div className={'row p-0 m-0'}>
                <div
                className={'col-12 col-md-8 col-lg-9 col-xl-10 p-0'}
                style={{
                    height: '100%'
                }}>
                    <Map
                    isZoomEnabled={true}
                    isScrollEnabled={true}
                    useShadows={false}
                    features={getFeatures()}
                    style={{
                        width: '100%',
                        height:  filterContainer.current ? filterContainer.current.clientHeight : 350
                    }}/>
                </div>
                <div
                ref={filterContainer}
                className={'col-12 col-md-4 col-lg-3 col-xl-2 p-0 px-md-2 py-md-0'}
                style={{
                    position: 'relative',
                    borderLeft: `1px solid ${Appearance.colors.divider()}`
                }}>
                    {getFilters()}
                </div>
            </div>
        </Panel>
    )
}

export const AddEditFollowUpCall = ({ isNewTarget, onClose }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? 'new_follow_up_call' : `edit_follow_up_call_${abstract.getID()}`;
    const [callLog, setOutboundCall] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onDoneClick = async () => {
        try {
            setLoading('done');
            await Utils.sleep(0.25);
            await validateRequiredFields(getFields);
            setLayerState('close');
        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} this ${callLog.method === 'email' ? 'email' : 'phone call'}. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onUpdateTarget = props => {
        let edits = abstract.object.set(props)
        setOutboundCall(edits);
    }

    const getFields = () => {

        if(!callLog) {
            return [];
        }

        let items = [{
            key: 'follow_up_date',
            title: 'Recall Date and Time',
            items: [{
                key: 'follow_up_start_date',
                required: false,
                title: 'Call Date and Time',
                description: 'When would you like to schedule a follow up call? Follow up calls are not required.',
                component: 'date_duration_picker',
                value: callLog.follow_up_start_date,
                onChange: date => onUpdateTarget({ follow_up_start_date: date }),
                props: {
                    requireInteraction: true
                }
            },{
                key: 'follow_up_end_date',
                required: false,
                title: 'Duration',
                description: 'How much time would you like to allocate for the follow up call?',
                component: 'duration_picker',
                visible: callLog.follow_up_start_date ? true : false,
                value: callLog.follow_up_end_date ? moment(callLog.follow_up_end_date).unix() - moment(callLog.follow_up_start_date).unix() > 0 : false,
                onChange: seconds => {
                    onUpdateTarget({ follow_up_end_date: moment(callLog.follow_up_start_date).add(seconds, 'seconds') })
                },
                props: {
                    seconds: callLog.follow_up_end_date ? moment(callLog.follow_up_end_date).unix() - moment(callLog.follow_up_start_date).unix() : null
                }
            },{
                key: 'notes',
                title: 'Notes',
                required: false,
                description: 'Add any information in this space that has not already been covered in the fields above.',
                component: 'textview',
                value: callLog.notes,
                onChange: text => onUpdateTarget({ notes: text }),
            }]
        }]

        return items;
    }

    const setupTarget = () => {
        let edits = abstract.object.open();
        setOutboundCall(edits);
    }

    useEffect(() => {
        setupTarget();
    }, []);

    return (
        <Layer
        id={layerID}
        title={isNewTarget ? `New Follow Up Call` : `Editing ${abstract.getTitle()}`}
        index={index}
        utils={utils}
        options={{
            ...options,
            loading: loading === true,
            layerState: layerState,
            sizing: 'medium',
            onCloseLayer: () => onClose(callLog)
        }}
        buttons={[{
            key: 'done',
            text: isNewTarget ? 'Done' : 'Save Changes',
            color: 'primary',
            loading: loading === 'done',
            onClick: onDoneClick
        }]}>
            <AltFieldMapper
            utils={utils}
            fields={getFields()} />
        </Layer>
    )
}

export const AddEditLead = ({ isNewTarget, onAddLead, onUpdateLead }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? `new_lead` : `edit_lead_${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState('init');

    const getButtons = () => {
        return [{
            key: 'lifewipe_support_content',
            items: ['global_data.new_lead']
        },{
            key: 'done',
            text: isNewTarget ? 'Done' : 'Save Changes',
            color: 'primary',
            loading: loading === 'submit',
            onClick: () => setLoading('submit')
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={isNewTarget ? `New Lead` : `Editing ${abstract.getTitle()}`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <AddEditLeadContent
            abstract={abstract}
            isNewTarget={isNewTarget}
            loading={loading}
            onAddLead={onAddLead}
            onUpdateLead={onUpdateLead}
            setLayerState={setLayerState}
            setLoading={setLoading}
            utils={utils} />
        </Layer>
    )
}

export const AddEditLeadContent = ({ abstract, connect, editTargets, isNewTarget, loading, onAddLead, onUpdateLead, extendedProps, renderExtraFields = [], setLayerState, setLoading, utils }) => {

    const [edits, setEdits] = useState({});
    const [homeownerStatusCodes, setHomeownerStatusCodes] = useState([]);
    const [maritalStatusCodes, setMaritalStatusCodes] = useState([]);
    const [occupationalStatusCodes, setOccupationalStatusCodes] = useState([]);

    const canEditLeadCredit = () => {

        let client = utils.client.get();
        let user = utils.user.get();

        // allow admins to make changes to any client
        if(user.level <= User.levels.get().admin) {
            return true;
        }
        // allow directors and primarys to change lead credit for their own client
        if(user.client_id === client.id) {
            return user.level <= User.levels.get().primary || user.level === User.levels.get().setter;
        }
        return false;
    }

    const onDoneClick = async props => {
        try {

            // sleep briefly and validate fields
            await validateRequiredFields(getFields);
            await Utils.sleep(0.25);

            // create new target
            if(isNewTarget) {

                // submit changes
                await abstract.object.submit(utils, props);
                setLoading(false);
                utils.banner.show({
                    title: 'Lead Created',
                    message: `The lead for ${abstract.object.full_name} has been created`,
                    onShow: () => {
                        if(typeof(setLayerState) === 'function') {
                            setLayerState('close');
                        }
                    }
                });

                // notify subscribers of new lead if applicable
                if(typeof(onAddLead) === 'function') {
                    onAddLead(abstract.object);
                }
                return;
            }

            // update target
            let { smart_share_transfer_completed = false } = await abstract.object.update(utils, extendedProps);
            setLoading(false);

            // show confirmation if marketing is not enabled
            if(connect !== true) {
                utils.banner.show({
                    title: 'Lead Updated',
                    message: `The lead for ${abstract.object.full_name} has been updated. ${smart_share_transfer_completed ? 'Per your client workspace preferences, this out of service area lead has been automatically transferred out of your client workspace.' : ''}`,
                    onShow: () => {
                        if(typeof(setLayerState) === 'function') {
                            setLayerState('close');
                        }
                    }
                });
            }

            // notify subscribers of new lead if applicable
            if(typeof(onUpdateLead) === 'function') {
                onUpdateLead(abstract.object);
            }

        } catch(e) {

            // check for do not call list conflicts
            if(e.code === 409) {
                utils.alert.show({
                    title: 'Just a Second',
                    message: `There was an issue creating this lead from your client workspace. ${e.message || 'An unknown error occurred'}`,
                    buttons: [{
                        key: 'confirm',
                        title: 'Continue',
                        style: 'destructive'
                    },{
                        key: 'cancel',
                        title: 'Cancel',
                        style: 'default'
                    }],
                    onClick: key => {
                        if(key === 'confirm') {
                            onDoneClick({
                                ...props,
                                ...e.response.duplicate === true && {
                                    duplicate_override: true
                                },
                                ...e.response.do_not_call === true && {
                                    do_not_call_override: true
                                }
                            });
                            return;
                        }
                        setLoading(false);
                    }
                });
                return;
            }

            // fallback to standard request error
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} this lead. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onTextMessageClick = async () => {
        try {
            utils.layer.open({
                id: 'lead_text_message_manager',
                Component: LeadTextMessageManager.bind(this, {
                    targets: [{
                        id: abstract.getID(),
                        phone_number: abstract.object.edits.phone_number
                    }]
                })
            });
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing the text message process. ${e.message || 'An unknown error occured'}`
            })
        }
    }

    const onUpdateTarget = props => {
        let edits = abstract.object.set(props);
        setEdits(edits);
    }

    const getContent = () => {
        if(loading === 'init') {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 15
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 50,
                        width: 50
                    }}/>
                </div>
            )
        }
        return (
            <AltFieldMapper
            connect={connect}
            fields={getFields()} 
            utils={utils}/>
        )
    }

    const getFields = () => {

        if(!edits) {
            return [];
        }

        // prepare default fields for mapper
        let fields = [{
            key: 'customer',
            title: 'Customer',
            items: [{
                key: 'first_name',
                title: 'First Name',
                description: 'The first name for the primary customer on this lead',
                component: 'textfield',
                value: edits.first_name,
                onChange: text => onUpdateTarget({ first_name: text })
            },{
                key: 'last_name',
                title: 'Last Name',
                description: 'The last name for the primary customer on this lead',
                component: 'textfield',
                value: edits.last_name,
                required: false,
                onChange: text => onUpdateTarget({ last_name: text })
            },{
                key: 'phone_number',
                required: !edits.email_address && !edits.address ? true : false,
                title: 'Phone Number',
                description: 'The phone number for the primary customer on this lead. Each lead must provide a phone number, email address, OR physical address. Please provide all three fields if the information is available.',
                component: 'textfield',
                value: edits.phone_number,
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number',
                    appendContent: connect && edits.phone_number && (
                        <img
                        className={'text-button'}
                        src={'images/text-message-icon-green.png'}
                        onClick={onTextMessageClick}
                        style={{
                            height: 20,
                            marginLeft: 8,
                            width: 20
                        }} />
                    )
                }
            },{
                key: 'email_address',
                required: !edits.phone_number && !edits.address ? true : false,
                title: 'Email Address',
                description: 'The email address for the primary customer on this lead. Each lead must provide a phone number, email address, OR physical address. Please provide all three fields if the information is available.',
                component: 'textfield',
                value: edits.email_address,
                onChange: text => onUpdateTarget({ email_address: text }),
                props: {
                    format: 'email'
                }
            },{
                key: 'address',
                required: !edits.phone_number && !edits.email_address ? true : false,
                title: 'Physical Address',
                description: 'The physical address for the primary customer on this lead. Each lead must provide a phone number, email address, OR physical address. Please provide all three fields if the information is available.',
                component: 'address_lookup',
                onChange: response => onUpdateTarget(response),
                value: edits.address && {
                    address: edits.address,
                    location: edits.location
                }
            },{
                key: 'homeowner_status',
                required: false,
                title: 'Homeowner Status',
                description: `What is the status of this customer's home ownership? You can skip this field if you do not know this information.`,
                component: 'list',
                value: edits.homeowner_status ? edits.homeowner_status.text : null,
                items: homeownerStatusCodes,
                onChange: item => onUpdateTarget({ homeowner_status: item })
            },{
                key: 'marital_status',
                required: false,
                title: 'Marital Status',
                description: `What is the status of this customer's relationship with their spouse or partner? You can skip this field if you do not know this information.`,
                component: 'list',
                value: edits.marital_status ? edits.marital_status.text : null,
                items: maritalStatusCodes,
                onChange: item => onUpdateTarget({ marital_status: item })
            },{
                key: 'occupational_status',
                required: false,
                title: 'Occupational Status',
                description: `What is the status of this customer's employement? You can skip this field if you do not know this information.`,
                component: 'list',
                value: edits.occupational_status ? edits.occupational_status.text : null,
                items: occupationalStatusCodes,
                onChange: item => onUpdateTarget({ occupational_status: item })
            }]
        },{
            key: 'spouse',
            title: 'Spouse',
            items: [{
                key: 'spouse_first_name',
                required: false,
                title: 'First Name',
                connect_title: 'Spouse First Name',
                description: 'The first name for the secondary customer on this lead',
                component: 'textfield',
                value: edits.spouse_first_name,
                onChange: text => onUpdateTarget({ spouse_first_name: text })
            },{
                key: 'spouse_last_name',
                required: false,
                title: 'Last Name',
                connect_title: 'Spouse Last Name',
                description: 'The last name for the secondary customer on this lead',
                component: 'textfield',
                value: edits.spouse_last_name,
                onChange: text => onUpdateTarget({ spouse_last_name: text })
            },{
                key: 'spouse_phone_number',
                required: false,
                title: 'Phone Number',
                connect_title: 'Spouse Phone Number',
                description: 'The phone number for the secondary customer on this lead. Leave this field blank if the primary and secondary customer share the same phone number.',
                component: 'textfield',
                value: edits.spouse_phone_number,
                onChange: text => onUpdateTarget({ spouse_phone_number: text }),
                props: {
                    format: 'phone_number',
                }
            },{
                key: 'spouse_email_address',
                required: false,
                title: 'Email Address',
                connect_title: 'Spouse Email Address',
                description: 'The email address for the secondary customer on this lead. Leave this field blank if the primary and secondary customer share the same email address.',
                component: 'textfield',
                value: edits.spouse_email_address,
                onChange: text => onUpdateTarget({ spouse_email_address: text }),
                props: {
                    format: 'email'
                }
            }]
        },{
            key: 'lead',
            title: 'About this Lead',
            items: [{
                key: 'lead_type',
                connect: false,
                title: 'Type',
                description: 'Choosing a lead type helps us sort your leads into specific categories.',
                component: 'lead_type_picker',
                value: edits.lead_type ? edits.lead_type.text : null,
                onChange: lead_type => onUpdateTarget({ lead_type: lead_type })
            },{
                key: 'lead_sub_type',
                required: false,
                connect: false,
                title: 'Sub-Type',
                description: 'Choosing a sub lead type helps us sort your leads into deeper, more specific categories.',
                component: 'lead_sub_type_picker',
                value: edits.lead_sub_type ? edits.lead_sub_type.text : null,
                onChange: lead_sub_type => onUpdateTarget({ lead_sub_type: lead_sub_type })
            },{
                key: 'lead_script',
                required: false,
                connect: false,
                title: 'Script',
                description: 'Lead scripts are used by Setters when they reach out to a lead. The script provides a customized narrative to help move the converstation in a productive direction.',
                component: 'lead_script_picker',
                value: edits.lead_script ? edits.lead_script.title : null,
                onChange: script => onUpdateTarget({ lead_script: script })
            }]
        },{
            key: 'users',
            title: 'Users and Credit',
            items: [{
                key: 'user',
                required: false,
                connect: false,
                visible: canEditLeadCredit(),
                title: 'Lead Credit',
                description: 'This should be the person adding the Lead to the LifeWipe. You may change this to whomever you would like to associate this entry with.',
                component: 'user_lookup',
                value: edits.user,
                onChange: user => onUpdateTarget({ user: user }),
                props: {
                    placeholder: 'Search by first or last name...'
                }
            },{
                key: 'assignment_user',
                required: false,
                connect: false,
                title: 'Assigned To',
                description: 'You can assign this Lead to a specific employee. This is not required and can be done at a later date.',
                component: 'user_lookup',
                value: edits.assignment_user,
                onChange: user => onUpdateTarget({ assignment_user: user }),
                props: {
                    placeholder: 'Search by first or last name...'
                }
            },{
                key: 'setter_user',
                required: false,
                connect: false,
                title: 'Setter',
                description: 'You can assign this Lead to a specific Setter. This is not required and can be done at a later date.',
                component: 'user_lookup',
                value: edits.setter_user,
                onChange: user => onUpdateTarget({ setter_user: user }),
                props: {
                    placeholder: 'Search by first or last name...',
                    levels: [ User.levels.get().setter ]
                }
            },{
                key: 'enrollment_user',
                visible: false,
                required: false,
                connect: false,
                title: 'Survey Credit',
                description: 'Should a Sales Operative receieve credit for this lead? If so, search for their account below',
                component: 'user_lookup',
                value: edits.enrollment_user,
                onChange: user => onUpdateTarget({ enrollment_user: user }),
                props: {
                    placeholder: 'Search by first or last name...',
                    levels: [
                        User.levels.get().sales_ops
                    ]
                }
            }]
        },{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'client',
                required: isNewTarget && utils.user.get().level <= User.levels.get().admin,
                visible: isNewTarget && utils.user.get().level <= User.levels.get().admin,
                connect: false,
                title: 'Client',
                description: 'Each lead must be assigned to a specific Client. This will determine who can interact with this lead.',
                component: 'client_lookup',
                value: edits.client,
                onChange: client => onUpdateTarget({ client: client })
            },{
                key: 'priority',
                required: false,
                title: 'Priority',
                description: 'Should this lead be prioritized over other leads?',
                component: 'bool_list',
                value: edits.priority,
                onChange: val => onUpdateTarget({ priority: val })
            },{
                key: 'out_of_service_area',
                required: false,
                title: 'Out of Service Area',
                description: 'Does this lead fall outside of the typical areas that you would service?',
                component: 'bool_list',
                value: edits.out_of_service_area,
                onChange: val => onUpdateTarget({ out_of_service_area: val })
            },{
                key: 'tags',
                required: false,
                title: 'Tags',
                description: 'Attaching one or more tags to a lead can help organize your lead catalog. Type a word and press enter when you are done',
                component: 'tag_lookup',
                value: edits.tags,
                onChange: tags => onUpdateTarget({ tags: tags })
            },{
                key: 'timezone',
                required: false,                
                title: 'Timezone',
                description: 'The timezone for this lead. This is used to determine the proper time of day for contact and to ensure appointment scheduling reflects their local timezone.',
                component: 'timezone_lookup',
                value: edits.timezone,
                onChange: timezone => onUpdateTarget({ timezone: timezone })
            },{
                key: 'notes',
                required: false,
                connect: false,
                title: 'Comments',
                description: 'Use this area to attach any information that has not already been covered.',
                component: 'textview',
                value: edits.notes,
                onChange: text => onUpdateTarget({ notes: text })
            }]
        }];

        // determine if extra fields were provided from the connect dialer
        if(connect === true && renderExtraFields.length > 0) {
            fields.push({
                id: 'extra_fields',
                title: 'Extra Fields',
                items: renderExtraFields
            });
        }
        return fields;
    }

    const setupTarget = async () => {
        try {

            // fetch client edit targets from the server if needed
            // edit targets are already present if the parent component is the marketing connect
            let targets = editTargets;
            if(!targets) {
                setLoading('init');
                await Utils.sleep(0.25);
                targets = await Request.get(utils, '/leads/', {
                    id: abstract.getID(),
                    type: 'edit_targets'
                });
            }

            // set edit targets for editing process
            let { default_lead_type, default_lead_script, homeowner_status_codes, marital_status_codes, occupational_status_codes } = targets;
            setHomeownerStatusCodes(homeowner_status_codes);
            setMaritalStatusCodes(marital_status_codes);
            setOccupationalStatusCodes(occupational_status_codes);

            // open edits for abstract target
            let edits = abstract.object.open();

            // declare currently selected client
            let client = utils.client.get();

            // set client if target is not new and a client is not set
            if(isNewTarget === false && !abstract.object.client) {
                let next = abstract.object.client_id === client.id ? client : await Client.get(utils, abstract.object.client_id);
                edits = abstract.object.set({ client: next });
            }
            
            // determine if pre-edit actions are needed for new abstract targets
            if(isNewTarget) {

                // set current client for target if target is new
                edits = abstract.object.set({ client });

                // set default lead type and lead script if applicable
                if(default_lead_type || default_lead_script) {
                    edits = abstract.object.set({
                        lead_script: default_lead_script,
                        lead_type: default_lead_type
                    })
                }
            }

            // end loading and set edits
            setLoading(false);
            setEdits(edits);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue setting up this lead. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        if(loading === 'submit') {
            onDoneClick();
        }
    }, [loading]);

    useEffect(() => {
        setupTarget();
    }, [abstract.object]);

    return getContent();
}

export const AddEditLeadScriptRebuttal = ({ isNewTarget }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? 'new_lead_script_rebuttal' : `edit_lead_script_rebuttal_${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [rebuttal, setRebuttal] = useState(null);

    const onDoneClick = async () => {
        try {

            setLoading('done');
            await Utils.sleep(0.25);
            await validateRequiredFields(getFields);

            // create target
            if(isNewTarget) {
                await abstract.object.submit(utils);
                setLoading(false);
                utils.banner.show({
                    title: 'Rebuttal Created',
                    message: `Your new rebuttal has been created and is ready to use.`,
                    onShow: () => setLayerState('close')
                });
                return;
            }

            // update target
            await abstract.object.update(utils);
            setLoading(false);
            utils.banner.show({
                title: 'Rebuttal Updated',
                message: `This rebuttal has been updated and will be available during the next use of a lead script.`,
                onShow: () => setLayerState('close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} this rebuttal. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onUpdateTarget = props => {
        let edits = abstract.object.set(props)
        setRebuttal(edits);
    }

    const getFields = () => {
        if(!rebuttal) {
            return [];
        }
        let items = [{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'title',
                title: 'Title',
                description: 'The title is used to represent this rebuttal across your client workspace.',
                component: 'textfield',
                value: rebuttal.title,
                onChange: text => onUpdateTarget({ title: text }),
            },{
                key: 'content',
                title: 'Content',
                description: 'The content is the information presented to the marketer when interacting with this lead script rebuttal.',
                component: 'textview',
                value: rebuttal.content,
                onChange: text => onUpdateTarget({ content: text }),
            }]
        }]

        return items;
    }

    const setupTarget = () => {
        let edits = abstract.object.open();
        setRebuttal(edits);
    }

    useEffect(() => {
        setupTarget();
    }, []);

    return (
        <Layer
        id={layerID}
        title={isNewTarget ? `New Lead Script Rebuttal` : `Editing ${abstract.getTitle()}`}
        index={index}
        utils={utils}
        options={{
            ...options,
            loading: loading === true,
            layerState: layerState,
            sizing: 'medium',
        }}
        buttons={[{
            key: 'done',
            text: isNewTarget ? 'Done' : 'Save Changes',
            color: 'primary',
            loading: loading === 'done',
            onClick: onDoneClick
        }]}>
            <AltFieldMapper
            utils={utils}
            fields={getFields()} />
        </Layer>
    )
}

export const AssignLeads = ({ leads, onClose }, { index, options, utils }) => {

    const layerID = 'assign_leads';

    const [count, setCount] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [targets, setTargets] = useState(leads);
    const [user, setUser] = useState(null);

    const onAssignLeads = () => {
        if(!user) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'Please select a user before moving on.'
            });
            return;
        }
        if(count && count > targets.length) {
            utils.alert.show({
                title: 'Just a Second',
                message: `You have selected ${targets.length} ${targets.length === 1 ? 'lead' : 'leads'} and requested a custom amount of ${count} ${count === 1 ? 'assignment' : 'assignments'}. Please choose an assignment amount less than or equal to ${targets.length} or leave the field blank to assign all the selected leads.`
            });
            return;
        }

        utils.alert.show({
            title: 'Assign Leads',
            message: `Are you sure that you want to assign ${count ? `${count} ${count === 1 ? 'lead' : 'leads'}` : (targets.length === 1 ? 'the selected lead' : 'all the selected leads')} to ${user.full_name}?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onAssignLeadsConfirm();
                    return;
                }
            }
        });
    }

    const onAssignLeadsConfirm = async () => {
        try {
            setLoading(true);

            // slice targets array down to count and set next selection of leads
            let submitted = count || targets.length;
            let ids = targets.map(lead => lead.id).slice(0, submitted);
            let next = targets.slice(submitted, targets.length - 1);

            // submit selection to server
            let response = await Request.post(utils, '/leads/', {
                ids: ids,
                type: 'batch_assign_user',
                user_id: user.user_id
            });

            setLoading(false);
            setCount(null);
            setTargets(next);

            // notify user that background mode is enabled
            let message = response.background ? `We have received your request to assign ${Utils.softNumberFormat(submitted)} ${submitted === 1 ? 'lead' : 'leads'} to ${user.full_name} and will notify you when it has completed. The estimated time to create your assignments is about ${Utils.parseDuration(response.estimated_duration)}` : `We have assigned ${Utils.softNumberFormat(submitted)} ${submitted === 1 ? 'lead' : 'leads'} to ${user.full_name}`;

            // determine if an additional assignment cycle should be requested
            if(count && count > 0 && next.length > 0) {
                utils.alert.show({
                    title: 'All Done!',
                    message: `${message}. Would you like to make another assignment?`,
                    buttons: [{
                        key: 'confirm',
                        title: 'Yes',
                        style: 'default'
                    },{
                        key: 'cancel',
                        title: 'No',
                        style: 'cancel'
                    }],
                    onClick: key => {
                        if(key === 'confirm') {
                            return;
                        }
                        setLayerState('close');
                        if(typeof(onClose) === 'function') {
                            onClose();
                        }
                    }
                });
                return;
            }

            // fallback to default confirmation alert
            utils.banner.show({
                title: 'Leads Assigned',
                message: message,
                onShow: () => {
                    setLayerState('close');
                    if(typeof(onClose) === 'function') {
                        onClose();
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue creating these assignments. ${e.message || 'An unknown error occurred'}`
            });
        }
    }


    const getButtons = () => {
        return [{
            key: 'submit',
            text: `Assign ${count && count > 0 ? `${Utils.softNumberFormat(count)} out of ` : ''}${Utils.softNumberFormat(targets.length)} ${targets.length === 1 ? 'lead' : 'leads'}`,
            color: 'primary',
            loading: loading === 'submit',
            onClick: onAssignLeads
        }];
    }

    const getFields = () => {
        return [{
            description: 'Assigning a lead to a user will allow that user to access and interact with the lead. This is a great way to distribute leads across your team or to give specific users access to specific leads.',
            key: 'options',
            title: 'Assignment Options',
            items: [{
                key: 'user',
                title: 'User',
                description: 'This will be the user that we assign to the selected leads.',
                component: 'user_lookup',
                value: user,
                onChange: setUser
            },{
                key: 'count',
                required: false,
                title: 'Custom Amount',
                description: 'If needed, you can assign a preset amount of leads to this user. We start from the beginning of your list of selected leads and assign the amount of leads that you specify. If left blank, we will assign all selected leads to the user that you choose.',
                component: 'textfield',
                value: count,
                onChange: val => setCount(isNaN(val) ? null : parseInt(val)),
                visible: leads.length > 1,
                props: {
                    format: 'integer'
                }
            }]
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Assign Leads to User'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
        </Layer>
    )
}

export const BatchUpdateLeadDetails = ({ ids, onClose }, { index, options, utils }) => {

    const layerID = 'batch_update_lead_details';
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [homeownerStatusCodes, setHomeownerStatusCodes] = useState([]);
    const [maritalStatusCodes, setMaritalStatusCodes] = useState([]);
    const [occupationalStatusCodes, setOccupationalStatusCodes] = useState([]);
    const [props, setProps] = useState({});

    const onSubmitSelections = async () => {
        try {
            // prepare request payload
            let payload = {
                client_id: props.client && props.client.id,
                assignment_user_id: props.assignment_user && props.assignment_user.user_id,
                enrollment_user_id: props.enrollment_user && props.enrollment_user.user_id,
                homeowner_status: props.homeowner_status && props.homeowner_status.code,
                lead_script: props.lead_script && props.lead_script.id,
                lead_sub_type: props.lead_sub_type && props.lead_sub_type.id,
                lead_type: props.lead_type && props.lead_type.id,
                marital_status: props.marital_status && props.marital_status.code,
                setter_user_id: props.setter_user && props.setter_user.user_id,
                priority: props.priority ? props.priority.id === 'yes' : null,
                occupational_status: props.occupational_status && props.occupational_status.code,
                out_of_service_area: props.out_of_service_area ? props.out_of_service_area.id === 'yes' : null,
                user_id: props.user && props.user.user_id,
                survey_credit: props.survey_credit && props.survey_credit.id,
                status: props.status && props.status.code,
                tags: props.tags && props.tags.map(tag => tag.id)
            }

            // check that at least one valid value was provided for batch updates
            let values = Object.values(payload).filter(value => Array.isArray(value) ? value.length > 0 : (value !== null && value !== undefined));
            if(values.length === 0) {
                throw new Error('Please fill out at least one area before submitting your changes');
            }

            // submit request to server
            setLoading('submit');
            await Utils.sleep(0.25);
            await Request.post(utils, '/leads/', {
                type: 'batch_update',
                ids: ids,
                props: payload
            });

            setLoading(false);
            utils.banner.show({
                title: 'Leads Updated',
                message: `We have made the changes to the ${ids.length} ${ids.length === 1 ? 'lead' : 'leads'} that you had selected.`,
                onShow: () => {

                    // close layer and notify layer subscribers 
                    setLayerState('close');
                    if(typeof(onClose) === 'function') {
                        onClose();
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue submitting your updates. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUpdateTarget = async props => {
        setProps(prev => ({
            ...prev,
            ...props
        }));
    }

    const canEditLeadCredit = () => {

        let client = utils.client.get();
        let user = utils.user.get();

        // allow admins to make changes to any client
        if(user.level <= User.levels.get().admin) {
            return true;
        }
        // allow directors and primarys to change lead credit for their own client
        if(user.client_id === client.id) {
            return user.level <= User.levels.get().primary || user.level === User.levels.get().setter;
        }
        return false;
    }

    const getButtons = () => {
        return [{
            key: 'submit',
            text: 'Submit Changes',
            color: 'primary',
            onClick: onSubmitSelections,
            loading: loading === 'submit'
        }]
    }

    const getFields = () => {
        return [{
            key: 'customer',
            title: 'Customer',
            items: [{
                key: 'homeowner_status',
                required: false,
                title: 'Homeowner Status',
                component: 'list',
                value: props.homeowner_status && props.homeowner_status.text,
                items: homeownerStatusCodes,
                onChange: item => onUpdateTarget({ homeowner_status: item })
            },{
                key: 'marital_status',
                required: false,
                title: 'Marital Status',
                component: 'list',
                value: props.marital_status && props.marital_status.text,
                items: maritalStatusCodes,
                onChange: item => onUpdateTarget({ marital_status: item })
            },{
                key: 'occupational_status',
                required: false,
                title: 'Occupational Status',
                component: 'list',
                value: props.occupational_status && props.occupational_status.text,
                items: occupationalStatusCodes,
                onChange: item => onUpdateTarget({ occupational_status: item })
            }]
        },{
            key: 'lead',
            title: 'About this Lead',
            items: [{
                key: 'lead_type',
                required: false,
                title: 'Type',
                component: 'lead_type_picker',
                value: props.lead_type && props.lead_type.text,
                props: { canAddNewTarget: false },
                onChange: lead_type => onUpdateTarget({ lead_type: lead_type })
            },{
                key: 'lead_sub_type',
                required: false,
                title: 'Sub-Type',
                component: 'lead_sub_type_picker',
                value: props.lead_sub_type && props.lead_sub_type.text,
                props: { canAddNewTarget: false },
                onChange: lead_sub_type => onUpdateTarget({ lead_sub_type: lead_sub_type })
            },{
                key: 'lead_script',
                required: false,
                title: 'Script',
                component: 'lead_script_picker',
                value: props.lead_script && props.lead_script.title,
                props: { canAddNewTarget: false },
                onChange: script => onUpdateTarget({ lead_script: script })
            }]
        },{
            key: 'users',
            title: 'Users and Credit',
            items: [{
                key: 'user',
                required: false,
                visible: canEditLeadCredit(),
                title: 'Lead Credit',
                component: 'user_lookup',
                value: props.user,
                onChange: user => onUpdateTarget({ user: user }),
                props: {
                    placeholder: 'Search by first or last name...'
                }
            },{
                key: 'assignment_user',
                required: false,
                title: 'Assigned To',
                component: 'user_lookup',
                value: props.assignment_user,
                onChange: user => onUpdateTarget({ assignment_user: user }),
                props: {
                    placeholder: 'Search by first or last name...'
                }
            },{
                key: 'setter_user',
                required: false,
                title: 'Setter',
                component: 'user_lookup',
                value: props.setter_user,
                onChange: user => onUpdateTarget({ setter_user: user }),
                props: {
                    placeholder: 'Search by first or last name...',
                    levels: [ User.levels.get().setter ]
                }
            },{
                key: 'enrollment_user',
                required: false,
                title: 'Survey Credit',
                component: 'user_lookup',
                value: props.enrollment_user,
                onChange: user => onUpdateTarget({ enrollment_user: user }),
                props: {
                    placeholder: 'Search by first or last name...',
                    levels: [
                        User.levels.get().sales_ops
                    ]
                }
            }]
        },{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'priority',
                required: false,
                title: 'Priority',
                component: 'list',
                value: props.priority && props.priority.title,
                onChange: item => onUpdateTarget({ priority: item }),
                items: [{
                    id: 'yes',
                    title: 'Yes'
                },{
                    id: 'no',
                    title: 'No'
                }]
            },{
                key: 'out_of_service_area',
                required: false,
                title: 'Out of Service Area',
                component: 'list',
                value: props.out_of_service_area && props.out_of_service_area.title,
                onChange: item => onUpdateTarget({ out_of_service_area: item }),
                items: [{
                    id: 'yes',
                    title: 'Yes'
                },{
                    id: 'no',
                    title: 'No'
                }]
            },{
                key: 'status',
                required: false,
                title: 'Status',
                component: 'list',
                value: props.status && props.status.title,
                onChange: item => onUpdateTarget({ status: item }),
                items: utils.client.status_codes.get().map(entry => ({
                    ...entry,
                    id: entry.code,
                    title: entry.text
                }))
            },{ 
                key: 'tags',
                required: false,
                title: 'Tags',
                component: 'tag_lookup',
                value: props.tags,
                onChange: tags => onUpdateTarget({ tags: tags })
            },{
                component: 'timezone_picker',
                key: 'timezone',
                onChange: timezone => onUpdateTarget({ timezone: timezone }),
                required: false,
                title: 'Timezone',
                value: props.timezone
            }]
        }]
    }

    const setupTarget = async () => {
        try {
            let { homeowner_status_codes, marital_status_codes, occupational_status_codes } = await Request.get(utils, '/leads/', {
                type: 'edit_targets'
            });

            setHomeownerStatusCodes(homeowner_status_codes);
            setMaritalStatusCodes(marital_status_codes);
            setOccupationalStatusCodes(occupational_status_codes);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing the update process. ${e.message || 'An unknown error occurred'}`,
                onClick: () => setLayerState('close')
            });
        }
    }

    useEffect(() => {
        setupTarget();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Batch Update Lead Details'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            <AltFieldMapper
            utils={utils}
            fields={getFields()} />
        </Layer>
    )
}

export const OutboundCallFilters = ({ onChange, value = {} }, { index, options, utils }) => {

    const layerID = 'outbound_call_filters';
    const [didFetchFilterOptions, setDidFetchFilterOptions] = useState(false);
    const [layerState, setLayerState] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchProps, setSearchProps] = useState(value || {});
    const [users, setUsers] = useState([]);

    const onUpdateSearchProps = props => {
        setSearchProps(prev => ({
            ...prev,
            ...props
        }));
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            key: 'reset',
            text: 'Reset Filters',
            onClick: () => {
                setLayerState('close');
                if(typeof(onChange) === 'function') {
                    onChange({});
                }
            }
        },{
            color: 'primary',
            key: 'apply',
            text: 'Apply Filters',
            onClick: () => {
                setLayerState('close');
                if(typeof(onChange) === 'function') {
                    onChange(searchProps);
                }
            }
        }];
    }

    const getFilterContent = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: 100,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 40,
                        width: 40
                    }}/>
                </div>
            )
        }

        let providerItems = getProviderItems();
        let fields = [{
            key: 'preferences',
            items: [{
                component: 'multiple_list',
                items: users,
                key: 'users',
                onChange: items => onUpdateSearchProps({ 'users': items?.map(item => item.id) }),
                required: false,
                title: 'Caller',
                value: searchProps.users && users.filter(val => searchProps.users.includes(val.id)),
            },{
                component: 'multiple_list',
                items: providerItems,
                key: 'providers',
                onChange: items => onUpdateSearchProps({ 'providers': items?.map(item => item.id) }),
                required: false,
                title: 'Provider',
                value: searchProps.providers && providerItems.filter(val => searchProps.providers.includes(val.id)),
            }]
        }].map(section => {
            section.items = section.items.filter(item => item.items ? item.items.length > 0 : true);
            return section;
        });

        return (
            <AltFieldMapper
            utils={utils}
            fields={fields} />
        )
    }

    const getProviderItems = () => {
        return [{
            id: 'twilio_sdk',
            title: 'Connect Dialer'
        },{
            id: 'vapi',
            title: 'Vapi AI'
        }];
    }

    const fetchFilterTargets = async () => {
        try {

            await Utils.sleep(0.5);
            let { users } = await Request.get(utils, '/clients/', {
                type: 'outbound_call_filter_targets'
            })

            // update state for list of client workspace users
            setUsers(users.map(target => {
                return {
                    id: target.user_id,
                    title: target.full_name
                }
            }));
            
            setLoading(false);
            setDidFetchFilterOptions(true);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the filter options. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchFilterTargets();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Outbound Call Filters'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState
        }}>
            {getFilterContent()}
        </Layer>
    )
}

export const OutboundCallDetails = ({ abstract, index, options, utils }) => {

    const layerID = `outbound_call_details_${abstract.getID()}`;
    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [vapiCall, setVapiCall] = useState(null);

    const onLeadClick = async () => {
        try {

            // set loading flag and fetch lead details
            setLoading('lead');
            let target = await Lead.get(utils, lead.id);

            // end loading and show lead details layer
            setLoading(false);
            utils.layer.open({
                abstract: Abstract.create({
                    object: target,
                    type: 'leads'
                }),
                Component: LeadDetails,
                id: `lead_details_${target.id}`,
                permissions: ['leads.details']
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this lead. ${e.message || 'An unknown error occurred'}`
            });
        }
    }
    
    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'metrics',
                permissions: ['calls.actions.metrics'],
                title: 'View Metrics',
                style: 'default'
            }],
            target: evt.target
        }, key => {
            if(key === 'metrics') {
                onViewMetrics();
                return;
            }
        });
    }

    const onViewMetrics = () => {
        utils.alert.dev();
    }

    const getButtons = () => {
        return [{
            color: 'secondary',
            key: 'options',
            onClick: onOptionsClick,
            text: 'Options'
        }];
    }

    const getFields = () => {

        // prepare field mapper items
        let call = abstract.object;
        let items = [{
            key: 'date',
            permissions: ['calls.details.dates'],
            title: 'Date and Time',
            items: [{
                key: 'created_at',
                title: 'Date',
                value: call.created_at ? Utils.formatDate(call.created_at) : null
            },{
                key: 'duration',
                title: 'Duration',
                value: call.duration ? Utils.parseDuration(call.duration) : null
            }]
        },{
            key: 'additional',
            lastItem: false,
            permissions: ['calls.details.ext'],
            title: 'Additional Information',
            items: [{
                key: 'call_sid',
                title: 'Call SID',
                value: call.call_sid
            },{
                key: 'to_number',
                title: 'To Phone Number',
                value: call.to_number ? Utils.formatPhoneNumber(call.to_number) : null
            },{
                key: 'from_number',
                title: 'From Phone Number',
                value: call.from_number ? Utils.formatPhoneNumber(call.from_number) : null,
                visible: call.from_number ? true : false
            },{
                key: 'source',
                title: 'Source',
                value: call.metadata ? Utils.ucFirst(call.metadata.source) : Utils.ucFirst(call.provider)
            },{
                key: 'status',
                title: 'Status',
                value: call.status ? Utils.ucFirst(call.status) : null
            }]
        }];

        // add vapi call details if applicable
        if(vapiCall) {
            items.push({
                id: 'vapi',
                lastItem: false,
                title: 'Vapi Call Details',
                items: [{
                    id: 'assistant_id',
                    title: 'Assistant ID',
                    value: vapiCall.assistant_id
                },{
                    id: 'call_id',
                    title: 'Call ID',
                    value: vapiCall.call_id
                },{
                    id: 'cost',
                    title: 'Cost',
                    value: vapiCall.cost ? Utils.toCurrency(vapiCall.cost) : null
                },{
                    id: 'ended_reason',
                    title: 'Ended Reason',
                    value: vapiCall.ended_reason
                }]
            });
        }
        
        // prevent moving forward if no valid items were provided
        let count = formatFields(utils, items).length;
        if(count === 0) {
            return null;
        }

        return (
            <FieldMapper 
            sections={items} 
            utils={utils} />
        )
    }

    const getLead = () => {
        if(!lead) {
            return null;
        }
        return (
            <LayerItem 
            collapsed={false}
            title={'Lead'}>
                <div style={Appearance.styles.unstyledPanel()}>
                    {Views.entry({
                        bottomBorder: false,
                        icon: {
                            path: lead.lead_type?.icon.url,
                            imageStyle: {
                                backgroundColor: null
                            }
                        },
                        loading: loading === 'lead',
                        onClick: onLeadClick,
                        subTitle: lead.phone_number || lead.email_address || 'No contact information provided',
                        title: lead.full_name || 'Customer name not available',
                    })}
                </div>
            </LayerItem>
        )
    }

    const getSystemEvents = () => {
        return (
            <SystemEventsLayerItem 
            abstract={abstract} 
            permissions={['calls.details.system_events']}
            utils={utils} />
        )
    }

    const getTranscription = () => {
        let transcript = abstract.object.metadata?.transcription_text;
        return transcript?.length > 0 && (
            <LayerItem
            collapsed={false}
            title={'Transcription'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: '12px 16px 12px 16px',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.key(),
                        whiteSpace: 'normal',
                        width: '100%'
                    }}>{transcript}</span>
                </div>
            </LayerItem>
        )
    }

    const fetchDetails = async () => {
        try {

            setLoading(true);
            let { lead, vapi_call } = await Request.get(utils, '/clients/', {
                id: abstract.getID(),
                type: 'outbound_call_details'
            });

            setLoading(false);
            setLead(lead ? Lead.create(lead) : null);
            setVapiCall(vapi_call);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the details for this call. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        setTimeout(fetchDetails, 250);
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            extension: {
                abstract: abstract,
                permissions: {
                    delete: ['appointments.notes.actions.delete'],
                    edit: ['appointments.notes.actions.edit'],
                    new: ['appointments.notes.actions.new'],
                    view: ['appointments.notes']
                },
                type: 'notes'
            },
            loading: loading === true,
            sizing: 'medium'
        }}>
            {getLead()}
            {getFields()}
            {getTranscription()}
            {getSystemEvents()}
        </Layer>
    )
}

export const Connect = ({ index, options, utils }) => {

    const layerID = 'connect';
    const limit = 15;

    const extendedProps = useRef({});
    const leadEdits = useRef(null);
    const leadsHeader = useRef(null);
    const offset = useRef(0);
    const rebuttals = useRef(null);
    const searchText = useRef(null);
    const sorting = useRef(null);

    const [client, setClient] = useState(null);
    const [callStartDate, setCallStartDate] = useState(null);
    const [defaultScript, setDefaultScript] = useState(null);
    const [editTargets, setEditTargets] = useState(null);
    const [leads, setLeads] = useState([]);
    const [leadsHeaderHeight, setLeadsHeaderHeight] = useState(0);
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState(null);
    const [paging, setPaging] = useState(null);
    const [rebuttalsHeight, setRebuttalsHeight] = useState(0);
    const [recalls, setRecalls] = useState([]);
    const [selectedLead, setSelectedLead] = useState(null);
    const [selectedLeadIndex, setSelectedLeadIndex] = useState(null);
    const [showRecalls, setShowRecalls] = useState(false);
    const [status, setStatus] = useState({});
    const [statusCodes, setStatusCodes] = useState([]);

    const onArchiveClick = () => {
        utils.alert.show({
            title: 'Archive Lead',
            message: 'Are you sure that you want to archive this lead? This lead will no longer show up within your pool of leads.',
            buttons: [{
                key: 'confirm',
                title: 'Archive',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onArchiveConfirm();
                    return;
                }
            }
        });
    }

    const onArchiveConfirm = async () => {
        try {

            setLoading('archive');
            await Utils.sleep(0.25);
            await Request.post(utils, '/leads/', {
                active: false,
                id: selectedLead.id,
                type: 'set_archive_status'
            });

            // filter lead out of leads list
            let targets = leads.filter(lead => lead.id !== selectedLead.id);
            setLeads(targets);

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Lead Archived',
                message: `The lead for ${selectedLead.full_name} has been archived`,
                onShow: () => {
                    utils.content.fetch('leads');
                    onSetNextLeadConfirm(targets);
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue archiving this lead. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onBookAppointmentClick = async () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: selectedLead,
                type: 'leads'
            }),
            Component: BookAppointmentFromLead.bind(this, {
                onBookAppointment: () => {

                    // filter out leads that are no longer actionable
                    let targets = leads.filter(lead => lead.id !== selectedLead.id);
                    setLeads(targets);

                    // confirm move to next lead
                    onSetNextLeadConfirm(targets);
                }
            }),
            id: `book_appointment_${selectedLead.id}`
        });
    }

    const onChangeRecallToggle = item => {
        setShowRecalls(item.id === 'recalls');
        setSelectedLead(null);
        setSelectedLeadIndex(0);
    }

    const onLeadClick = index => {
        setSelectedLeadIndex(index);
    }

    const onRequestSetAppointment = async () => {
        return new Promise(resolve => {
            if(status.id !== Lead.status.get().set) {
                resolve();
                return;
            }
            let target = Abstract.create({
                object: selectedLead,
                type: 'leads'
            });
            utils.layer.open({
                abstract: target,
                Component: BookAppointmentFromLead.bind(this, {
                    onCloseLayer: resolve
                }),
                id: `book_appointment_${selectedLead.id}`
            });
        })
    }

    const onRequestRecall = async () => {
        return new Promise(resolve => {
            if(status.id !== Lead.status.get().recall) {
                resolve();
                return;
            }

            utils.alert.show({
                title: 'Schedule Recall',
                message: 'Would you like to setup a time and date for the recall?',
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key !== 'confirm') {
                        resolve();
                        return;
                    }

                    let call = OutboundCall.new();
                    call.assign_to = utils.user.get();
                    call.author = utils.user.get();
                    call.direction = 'outbound';
                    call.end_date = moment();
                    call.lead = selectedLead;
                    call.method = 'phone';
                    call.start_date = moment(callStartDate);

                    utils.layer.open({
                        id: 'new_follow_up_call',
                        abstract: Abstract.create({
                            type: 'outbound_calls',
                            object: call
                        }),
                        Component: AddEditFollowUpCall.bind(this, {
                            isNewTarget: true,
                            onClose: resolve
                        })
                    });
                }
            });
        })
    }

    const onSaveClick = async () => {

        // check if a recall should be scheduled
        let recall = await onRequestRecall();

        // update extended props for lead update
        extendedProps.current = {
            client: client,
            call_start_date: callStartDate,
            connect: true,
            notes: notes,
            status: status.id,
            ...recall && {
                call_follow_up_end_date: recall.follow_up_end_date,
                call_follow_up_notes: recall.notes,
                call_follow_up_start_date: recall.follow_up_start_date
            }
        }
        setLoading('submit');
    }

    const onSearchTextChange = text => {

        // update offset and search text refs
        offset.current = 0;
        searchText.current = text;

        // fetch content based on which content toggle is active
        fetchConnectContent();
    }

    const onSetLeadTarget = () => {
        try {
            
            // set next lead selection
            let targets = showRecalls ? recalls : leads;
            if(selectedLeadIndex >= 0 && targets.length > 0) {

                // throw an error if unable to locate next lead
                let target = targets[selectedLeadIndex || 0];
                if(!target) {
                    throw new Error('unable to locate next lead/recall target');
                }

                // declare lead and set state values
                let lead = showRecalls ? target.lead : target;
                setClient(lead.client);
                setCallStartDate(moment().format('YYYY-MM-DD HH:mm:ss'));
                setNotes(lead.notes);
                setSelectedLead(lead);

                // reset default status code to client preference
                setStatus({
                    id: editTargets.connect_default_status.value,
                    title: editTargets.connect_default_status.text
                }); 
            }

        } catch(e) {
            setSelectedLead(null);
            console.error(e.message);
        }
    }

    const onSetNextLead = async () => {
        try {

            // check if status was changed to set and request that a appointment be set if applicable
            await onRequestSetAppointment();

            // filter out leads that are no longer actionable
            let targets = leads.filter(lead => lead.status.code === Lead.status.get().assigned);
            setLeads(targets);

            // add to list of recalls if status was set to recall
            // full recall object not needed since data is fetched when focus is changed to recalls
            if(status.id === Lead.status.get().recall) {
                setRecalls(prev => update(prev, {
                    $push: [{
                        call: {},
                        lead: selectedLead
                    }]
                }));
            }

            // confirm move to next lead
            onSetNextLeadConfirm(targets);

        } catch(e) {
            console.error(e.message);
        }
    }

    const onSetNextLeadConfirm = targets => {

        // prevent moving forward if no more leads are available
        if((selectedLeadIndex + 1) > targets.length) {
            utils.alert.show({
                title: 'Congratulations!',
                message: 'You have reached the end of your assigned leads list.'
            });
            return;
        }

        // advance to next lead if no leads were filtered out
        // the index auto advancese when the previous lead was filtered out
        if(targets.length === leads.length) {
            setSelectedLeadIndex(val => val + 1);
        }
    }

    const onSortingChange = props => {
        sorting.current = props;
        setLoading('sorting');
        fetchConnectContent();
    }
    
    const getContent = () => {
        return (
            <>
            <RebuttalsCollection
            ref={rebuttals}
            lead={selectedLead}
            title={true}
            utils={utils}
            style={{
                marginBottom: 15
            }}/>

            <div className={'row m-0'}>
                <div className={'col-12 col-xl-5 p-0 pb-3 p-xl-0'}>
                    {getLeadsList()}
                </div>
                <div
                className={'col-12 col-xl-7 p-0 pl-xl-3'}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: getLayerSizingHeight('fullscreen') - rebuttalsHeight - 64,
                        overflowY: 'scroll',
                        width: '100%'
                    }}>
                        {getLeadDetails()}
                        {getLeadTarget() && getLeadScript()}
                    </div>
                </div>
            </div>
            </>
        )
    }

    const getPrependValue = (lead, recall) => {
        if(recall && moment(recall.date) < moment()) {
            return (
               <div style={{
                   width: 10,
                   height: 10,
                   minWidth: 10,
                   minHeight: 10,
                   borderRadius: 5,
                   backgroundColor: Appearance.colors.red,
                   overflow: 'hidden',
                   marginRight: 8
               }} />
           )
        }
        if(lead.priority) {
            return (
               <div style={{
                   width: 10,
                   height: 10,
                   minWidth: 10,
                   minHeight: 10,
                   borderRadius: 5,
                   backgroundColor: Appearance.colors.primary(),
                   overflow: 'hidden',
                   marginRight: 8
               }} />
           )
        }
        return null;
    }

    const getFields = (lead, recall) => {
        return [{
            key: 'full_name',
            title: 'Name',
            value: lead.full_name || 'Customer name not available',
            prepend: getPrependValue(lead, recall)
        },{
            key: 'follow_up_start_date',
            title: 'Recall Date',
            value: recall && Utils.formatDate(recall.date),
            visible: showRecalls && recall ? true : false
        },{
            key: 'phone_number',
            title: 'Phone Number',
            sortable: false,
            value: lead.phone_number ? Utils.formatPhoneNumber(lead.phone_number) : 'Phone number not available'
        },{
            key: 'lead_type',
            title: 'Lead Type',
            value: lead.lead_type && (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row'
                }}>
                    {lead.lead_type.icon && (
                        <img
                        src={lead.lead_type.icon.url}
                        style={{
                            width: 20,
                            height: 20,
                            objectFit: 'contain',
                            marginRight: 8
                        }} />
                    )}
                    <span>{lead.lead_type.text}</span>
                </div>
            )
        },{
            key: 'status',
            title: 'Status',
            sortable: false,
            value: getLeadStatus(utils, lead)
        },{
            key: 'timezone',
            title: 'Timezone',
            value: lead.timezone
        }].filter(entry => {
            return entry.visible !== false;
        });
    }

    const getHeaderFields = () => {
        return [{
            key: 'full_name',
            title: 'Name'
        },{
            key: 'follow_up_start_date',
            title: 'Recall Date',
            visible: showRecalls ? true : false
        },{
            key: 'phone_number',
            title: 'Phone Number',
            sortable: false
        },{
            key: 'lead_type',
            title: 'Lead Type'
        },{
            key: 'status',
            title: 'Status'
        },{
            key: 'timezone',
            title: 'Timezone'
        }];
    }

    const getLeads = () => {
        if(leads.length === 0) {
            return (
                Views.entry({
                    bottomBorder: false,
                    hideIcon: true,
                    subTitle: 'No leads are currently assigned to your account',
                    title: 'No Leads Found'
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <tbody style={{
                    width: '100%'
                }}>
                    <TableListHeader
                    fields={getHeaderFields()}
                    flexible={false}
                    minColumnWidth={125}
                    onChange={onSortingChange} />
                    {leads.map((lead, index) => {
                        return (
                            <tr
                            key={index}
                            className={`view-entry ${window.theme} ${selectedLead && selectedLead.id === lead.id ? 'selected' : ''}`}
                            onClick={onLeadClick.bind(this, index)}
                            style={{
                                borderBottom: index !== leads.length - 1 ? `1px solid ${Appearance.colors.divider()}` : null
                            }}>
                                {getFields(lead).map((field, index) => {
                                    return (
                                        <td
                                        key={index}
                                        className={'px-3 py-2'}>
                                            <div style={{
                                                alignItems: 'center',
                                                display: 'flex',
                                                flexDirection: 'row'
                                            }}>
                                                {field.prepend}
                                                <span style={{
                                                    ...Appearance.textStyles.subTitle(),
                                                    width: '100%'
                                                }}>{field.value}</span>
                                            </div>
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        )
    }

    const getLeadDetails = () => {
        let abstract = getLeadTarget();
        if(loading === true) {
            return (
                <div 
                className={'pb-0 pb-xl-2'}
                ref={leadEdits}
                style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    height: 125,
                    justifyContent: 'center',
                    width: '100%'
                }}>
                    <LottieView
                    loop={true}
                    autoPlay={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 50,
                        width: 50
                    }}/>
                </div>
            )
        }
        if(!abstract) {
            return (
                <div 
                ref={leadEdits}
                style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    width: '100%'
                }}>
                    {Views.entry({
                        bottomBorder: false,
                        hideIcon: true,
                        subTitle: 'Details for the selected lead will appear here once a lead is selected from the list',
                        title: 'No Lead Selected'
                    })}
                </div>
            )
        }
        return (
            <div
            ref={leadEdits}
            style={{
                width: '100%'
            }}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    marginBottom: 16
                }}>
                    <div className={'row m-0 px-3 pb-1 pt-3'}>
                        <div className={'col-12 col-xl-8 px-0 pt-0 pb-2 pr-xl-2 pb-xl-0'}>
                            {getLeadDetailsContent()}
                        </div>
                        <div className={'col-12 col-xl-4 p-0 pl-xl-2 pr-xl-3 pt-xl-2 pb-xl-4'}>
                            {abstract && loading !== true && (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.subHeader(),
                                        marginBottom: 4
                                    }}>{'Comments'}</span>
                                    <TextView 
                                    containerStyle={{
                                        height: '100%'
                                    }}
                                    fieldStyle={{
                                        height: '100%'
                                    }}
                                    onChange={setNotes} 
                                    placeholder={'Notes'}
                                    rootStyle={{
                                        flexGrow: 1
                                    }} />
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        marginBottom: 10,
                                        marginTop: 16
                                    }}>
                                        <OutboundCallButton
                                        lead={abstract.object}
                                        style={{
                                            marginBottom: 8,
                                            width: '100%'
                                        }}
                                        title={`Call ${abstract.object.phone_number}`}
                                        utils={utils} />
                                        <Button
                                        color={'dark'}
                                        containerStyle={{
                                            marginBottom: 8,
                                            width: '100%'
                                        }}
                                        label={'Book Appointment'}
                                        onClick={onBookAppointmentClick}
                                        type={'large'} />
                                        <Button
                                        color={'secondary'}
                                        containerStyle={{
                                            marginBottom: 8,
                                            width: '100%'
                                        }}
                                        label={'Continue to Next Lead'}
                                        loading={loading === 'submit'}
                                        onClick={onSaveClick}
                                        type={'large'} />
                                        <Button
                                        color={'danger'}
                                        containerStyle={{
                                            width: '100%'
                                        }}
                                        label={'Archive Lead'}
                                        loading={loading === 'archive'}
                                        onClick={onArchiveClick} 
                                        type={'large'} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const getLeadDetailsContent = () => {
        let abstract = getLeadTarget();
        return (
            <AddEditLeadContent
            abstract={abstract}
            connect={true}
            editTargets={editTargets}
            extendedProps={extendedProps.current}
            loading={loading}
            isNewTarget={false}
            onUpdateLead={onSetNextLead}
            renderExtraFields={[{
                component: 'list',
                disablePlaceholder: true,
                key: 'status',
                items: statusCodes,
                onChange: setStatus,
                required: false,
                title: 'Status',
                value: status && status.title
            }]}
            setLoading={setLoading}
            utils={utils} />
        )
    }

    const getLeadsList = () => {
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                <div ref={leadsHeader}>
                    <div 
                    className={'p-3'}
                    style={{
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%'
                    }}>
                        <TextField
                        icon={'search'}
                        onChange={onSearchTextChange} 
                        placeholder={'Search by customer name or phone number...'}
                        useDelay={true}
                        value={searchText.current}/>
                        <ListField
                        disablePlaceholder={true}
                        items={[{
                            id: 'leads',
                            title: 'Leads'
                        },{
                            id: 'recalls',
                            title: 'Recalls'
                        }]}
                        placeholder={'Select a list to view...'}
                        onChange={onChangeRecallToggle} 
                        style={{
                            marginLeft: 12,
                            maxWidth: 250 
                        }}
                        value={showRecalls ? 'Recalls' : 'Leads'}/>
                    </div>
                </div>
                {getTargetContent()}
            </div>
        )
    }

    const getLeadScript = () => {
        let target = selectedLead && selectedLead.lead_script || defaultScript;
        if(!target || loading === true) {
            return null;
        }
        return (
            <div
            className={`custom-scrollbars ${window.theme}`}
            style={{
                ...Appearance.styles.unstyledPanel(),
                flexGrow: 1,
                padding: '8px 12px 8px 12px',
                width: '100%',
                height: '100%',
                overflowY: 'scroll'
            }}>
                {formatLeadScriptContent({
                    lead: selectedLead,
                    editable: false,
                    text: target.text,
                    utils: utils
                })}
            </div>
        )
    }

    const getLeadTarget = () => {
        return selectedLead && Abstract.create({
            type: 'leads',
            object: selectedLead
        });
    }

    const getRecalls = () => {
        if(recalls.length === 0) {
            return (
                Views.entry({
                    title: 'No Recalls Found',
                    subTitle: 'No recalls are currently assigned to your account',
                    hideIcon: true,
                    bottomBorder: false
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <tbody style={{
                    width: '100%'
                }}>
                    <TableListHeader
                    fields={getHeaderFields()}
                    flexible={false}
                    onChange={onSortingChange} />
                    {recalls.map((entry, index) => {
                        return (
                            <tr
                            key={index}
                            className={`view-entry ${window.theme} ${selectedLead && selectedLead.id === entry.lead.id ? 'selected' : ''}`}
                            onClick={onLeadClick.bind(this, index)}
                            style={{
                                borderBottom: index !== recalls.length - 1 ? `1px solid ${Appearance.colors.divider()}` : null
                            }}>
                                {getFields(entry.lead, entry.outbound_call).map((field, index) => {
                                    return (
                                        <td
                                        key={index}
                                        className={'px-3 py-2'}>
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center'
                                            }}>
                                                {field.prepend}
                                                <span style={{
                                                    ...Appearance.textStyles.subTitle(),
                                                    width: '100%'
                                                }}>{field.value}</span>
                                            </div>
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        )
    }

    const getTargetContent = () => {
        
        if(loading === true) {
            return (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    padding: 24
                }}>
                    <LottieView
                    loop={true}
                    autoPlay={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        width: 50,
                        height: 50
                    }}/>
                </div>
            )
        }
        return (
            <>
            <div style={{
                width: '100%',
                maxHeight: getLayerSizingHeight('fullscreen') - ToolbarHeight - rebuttalsHeight - leadsHeaderHeight - 45,
                overflowY: 'scroll'
            }}>
                {showRecalls === false && getLeads()}
                {showRecalls === true && getRecalls()}
            </div>
            {paging && (
                <PageControl
                data={paging}
                limit={limit}
                offset={offset.current}
                onClick={next => {
                    offset.current = next;
                    setLoading('paging');
                    fetchConnectContent();
                }} />
            )}
            </>
        )
    }

    const fetchConnectContent = () => {
        if(showRecalls) {
            fetchRecalls();
        } else {
            fetchLeads();
        }
    }

    const fetchLeads = async () => {
        try {
            // add delay if search text is present
            if(searchText.current) {
                await Utils.sleep(0.25);
            }

            // send request to server
            let { leads, paging } = await Request.get(utils, '/leads/', {
                limit: 250,
                search_props: {
                    general_assignment: utils.user.get().user_id,
                    text: searchText.current
                },
                type: 'connect',
                ...sorting.current
            });

            // end loading for ui and set paging
            setLoading(false);
            setPaging(paging);

            // format recalls and set selected target index
            setLeads(leads.map(lead => Lead.create(lead)));
            if(leads.length > 0 && selectedLeadIndex === null) {
                setSelectedLeadIndex(0);
            }

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the list of leads. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const fetchRecalls = async () => {
        try {
            // add delay if search text is present
            if(searchText.current) {
                await Utils.sleep(0.25);
            }

            // send request to server
            let { paging, recalls } = await Request.get(utils, '/leads/', {
                limit: 250,
                search_text: searchText.current,
                type: 'recalls',
                ...sorting.current
            });

            // end loading for ui and set paging
            setLoading(false);
            setPaging(paging);

            // format recalls and set selected target index
            setRecalls(recalls.map(recall => {
                recall.outbound_call.date = moment(recall.outbound_call.date);
                recall.lead = Lead.create(recall.lead);
                return recall;
            }));
            if(recalls.length > 0 && selectedLeadIndex === null) {
                setSelectedLeadIndex(0);
            }

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the list of recalls. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const fetchEditTargets = async () => {
        try {
            let results = await Request.get(utils, '/leads/', {
                type: 'edit_targets'
            });

            // update state with edit targets
            setEditTargets(results);

            // set dfefault script and lead status code
            setDefaultScript(results.default_lead_script);
            setStatus({
                id: results.connect_default_status.value,
                title: results.connect_default_status.text
            });

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue setting up the lead editing process. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const setupStatusCodes = async () => {

        // prepare list of status codes for client
        let codes = utils.client.status_codes.get().filter(entry => {
            return entry.capabilities.leads;
        }).map(entry => ({
            id: entry.code,
            title: entry.text
        }));
        setStatusCodes(codes); 
    }

    useEffect(() => {
        if(editTargets) {
            setLoading(true);
            fetchConnectContent();
        }
    }, [editTargets, showRecalls]);

    useEffect(() => {
        if(leadsHeader.current) {
            setLeadsHeaderHeight(leadsHeader.current.clientHeight);
        }
    }, [leadsHeader.current]);

    useEffect(() => {
        if(rebuttals.current) {
            setRebuttalsHeight(rebuttals.current.clientHeight);
        }
    }, [rebuttals.current]);

    useEffect(() => {
        onSetLeadTarget();
    }, [leads, recalls, selectedLeadIndex, showRecalls]);

    useEffect(() => {
        fetchEditTargets();
        setupStatusCodes();
    }, []);

    return (
        <Layer
        id={layerID}
        index={index}
        title={'Marketing Connect'}
        utils={utils}
        style={{
            padding: 15
        }}
        options={{
            ...options,
            loading: loading === true,
            onCloseLayer: utils.content.fetch.bind(this, 'lead'),
            sizing: 'fullscreen'
        }}>
            {getContent()}
        </Layer>
    )
}

export const EnrollLeadSurvey = ({ abstract, index, options, utils }) => {

    const layerID = `enroll_lead_survey_${abstract.getID()}`;
    const [loading, setLoading] = useState(true);
    const [layerState, setLayerState] = useState(null);
    const [surveys, setSurveys] = useState([]);
    const [survey, setSurvey] = useState(null);

    const onEnrollSurvey = async () => {
        if(!survey) {
            utils.alert.show({
                title: 'Just a Second',
                message: `Please choose a Survey before moving on`
            });
            return;
        }

        try {
            setLoading(true);
            await Request.post(utils, '/leads/', {
                type: 'enroll_survey',
                id: abstract.getID(),
                survey_id: survey.id
            });

            setLoading(false);
            utils.content.fetch('lead_surveys');

            utils.banner.show({
                title: 'Survey Enrolled',
                message: `The ${abstract.getTitle()} has been enrolled in the Survey "${survey.name}"`,
                onShow: () => setLayerState('close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue enrolling in this Survey. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'done',
            onClick: onEnrollSurvey,
            text: 'Enroll'
        }];
    }

    const getSurveyList = () => {
        return (
            <select
            className={`custom-select ${window.theme}`}
            value={survey ? survey.name : 'Choose a Survey'}
            style={{
                background: `${Appearance.colors.textField()} url("images/custom-select-list-arrows-${window.theme === 'dark' ? 'white' : 'black'}.png") no-repeat right 0.75rem center`,
                backgroundSize: '10px',
                marginBottom: 8
            }}
            onChange={e => {
                let id = Utils.attributeForKey.select(e, 'id');
                setSurvey(surveys.find(survey => survey.id === parseInt(id)));
            }}>
                <option disabled={true}>{'Choose a Survey'}</option>
                {surveys.map((survey, index) => {
                    return (
                        <option key={index} id={survey.id}>{survey.name}</option>
                    )
                })}
            </select>
        )
    }
    const fetchSurveys = async () => {
        try {
            let { surveys } = await Request.get(utils, '/clients/', {
                type: 'surveys',
                id: abstract.getID()
            })
            setLoading(false);
            setSurveys(surveys.map(survey => Survey.create(survey)));

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the surveys for your client workspace. ${e.message || 'An unknown error occurred'}`,
                onClick: () => setLayerState('close')
            })
        }
    }

    useEffect(() => {
        fetchSurveys();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Survey Enrollment'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            {getSurveyList()}
        </Layer>
    )
}

export const ImportLeads = ({ index, options, utils }) => {

    const layerID = 'import_leads';
    const limit = 5;

    const [customValues, setCustomValues] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [file, setFile] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [leads, setLeads] = useState(null);
    const [loading, setLoading] = useState(true);
    const [maps, setMaps] = useState(null);
    const [offset, setOffset] = useState(0);
    const [paging, setPaging] = useState(null);
    const [selectedMap, setSelectedMap] = useState({ props: {} });
    const [selectedDocument, setSelectedDocument] = useState(null);

    const onBackToUpload = () => {
        utils.alert.show({
            title: 'Just a Second',
            message: 'Are you sure that you want to return to the upload screen? Any unsaved changes will be lost.',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setFile(null);
                    setLeads(null);
                    setSelectedDocument(null);
                    return;
                }
            }
        });
    }

    const onConfirmUpload = async () => {
        try {

            setLoading('done');
            await Utils.sleep(0.25);

            // check that at least one map selection has been made
            let keys = selectedMap ? Object.keys(selectedMap.props) : [];
            if(keys.length === 0) {
                throw new Error('It looks like you have not made any selections for your import. Click at least one drop down menu above a column to let us know what to do with the information in that column before moving on.');
            }

            // submit selections to server
            let { count, status } = await Request.post(utils, '/leads/', {
                type: 'import',
                document_id: selectedDocument.id,
                mapping: selectedMap.props
            });

            setLoading(false);
            utils.banner.show({
                title: 'Leads Imported',
                message: status === 'queued' ? 'Your lead import has been submitted. We will notify you when the import process has completed.' : `Your lead import has been completed. We were able to import ${count} ${count === 1 ? 'lead' : 'leads'} into your client workspace.`,
                onShow: () => {
                    utils.content.fetch(['leads', 'lead_types', 'lead_sub_types', 'status', 'lead_imports']);
                    setLayerState('close');
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue submitting your lead import preferences. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onCreateNewCustomColumn = async (index, title) => {
        try {
            setLoading(true);
            await Utils.sleep(0.5);
            let { id } = await Request.post(utils, '/clients/', {
                type: 'new_lead_column',
                title: title
            });

            setLoading(false);
            onUpdateHeaderMapValues(index, { id });
            setCustomValues(values => {
                return values.concat([{ 
                    id: id,
                    removable: true, 
                    title: title 
                }])
            });

        } catch(e) {
            setLoading(false);
            onUpdateHeaderMapValues(index);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue creating your custom column. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onHeaderListItemChange = (index, item) => {

        // prevent moving forward if no item was selected
        if(!item || !item.id) {
            return;
        }

        // set selected item if item is not requesting a custom column configuration
        if(item.id !== 'custom') {
            onUpdateHeaderMapValues(index, item);
            return;
        }

        // request a column name from the user
        let title = null;
        utils.alert.show({
            title: 'Custom Column',
            message: 'What would you like to call this column of data?',
            content: (
                <div style={{
                    paddingBottom: 12,
                    paddingLeft: 12,
                    paddingRight: 12,
                    width: '100%'
                }}>
                    <TextField
                    placeholder={'Column Name'}
                    onChange={text => title = text}/>
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Done',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm' && title) {
                    onCreateNewCustomColumn(index, title);
                    return;
                }
            }
        });
    }

    const onNewMapClick = () => {
        let name = null;
        utils.alert.show({
            title: 'Save Layout as a Preset',
            message: 'We can save your current import layout as a preset that you can use during a future import. What would you like to call your preset?',
            content: (
                <div style={{
                    width: '100%',
                    padding: 12
                }}>
                    <TextField
                    placeholder={'Preset Name'}
                    onChange={text => name = text} />
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Save',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(name && key === 'confirm') {
                    onSaveMapLayout(name);
                    return;
                }
            }
        });
    }

    const onRemoveCustomLeadColumn = item => {
        utils.alert.show({
            title: 'Remove Column',
            message: `Are you sure that you want to remove the "${item.title}" column? This can not be undone.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onRemoveCustomLeadColumnConfirm(item.id);
                    return;
                }
            }
        });
    }

    const onRemoveCustomLeadColumnConfirm = async id => {
        try {
            setLoading(true);
            await Request.post(utils, '/clients/', {
                id: id,
                type: 'remove_lead_column'
            });

            setLoading(false);
            setCustomValues(values => values.filter(value => value.id !== id));
            
        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue removing your column. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onRemoveDocument = (doc, evt) => {
        evt.stopPropagation();
        utils.alert.show({
            title: 'Remove Imported Document',
            message: 'When removing a document, we can remove all the leads that were imported from the document as well. Would you like to remove the document and the leads associated with it or keep the leads in your client workspace? Neither of these actions can be undone.',
            buttons: [{
                key: 'document',
                title: 'Remove Document',
                style: 'destructive'
            },{
                key: 'leads',
                title: 'Remove Document and Leads',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(['document', 'leads'].includes(key)) {
                    onRemoveDocumentConfirm(doc, key === 'leads');
                    return;
                }
            }
        });
    }

    const onRemoveDocumentConfirm = async (doc, removeLeads) => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);
            await Request.post(utils, '/leads/', {
                document_id: doc.id,
                remove_leads: removeLeads,
                type: 'remove_import_document'
            });

            setLoading(false);
            setDocuments(docs => docs.filter(prev => prev.id !== doc.id));
            utils.banner.show({
                title: 'Document Removed',
                message: `This document has been removed ${removeLeads ? 'along with all leads imported from this document.' : 'but the leads have been preserved in your client workspace.'}`,
                onShow: utils.content.fetch.bind(this, ['leads', 'lead_imports'])
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue removing this document from your history of imported documents. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSaveMapLayout = async name => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);

            let keys = selectedMap ? Object.keys(selectedMap.props) : [];
            if(keys.length === 0) {
                throw new Error('It looks like you have not made any selections for your layout. Click at least one of the drop down menus above a column to let us know what to do with the information in that column.');
            }

            // submit selection to server
            let { id } = await Request.post(utils, '/leads/', {
                type: 'new_lead_import_map',
                custom: customValues,
                name: name,
                props: selectedMap.props
            });

            setLoading(false);
            setMaps(maps => update(maps, {
                $push: [{
                    ...selectedMap,
                    id: id,
                    name: name
                }],
                $apply: maps => maps.sort((a,b) => {
                    return a.name.localeCompare(b.name)
                })
            }));

            utils.banner.show({
                title: 'Preset Saved',
                message: 'Your layout preset has been saved for future use.'
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue saving your layout preset. ${e.message || 'An unknown error occurred'}.`
            });
        }
    }

    const onSelectDocument = doc => {
        setFile(null);
        setSelectedDocument(prev => prev && prev.id === doc.id ? null : doc);
    }

    const onUpdateHeaderMapValues = (index, item) => {
        setSelectedMap(target => {
            return {
                ...target,
                props: update(target.props, {
                    [index]: {
                        $set: {
                            index: index,
                            id: item && item.id
                        }
                    }
                })
            };
        });
    }

    const onUploadDocument = async () => {
        try {

            // verify that a file has been selected or a previously uploaded document has been selected before moving forward
            if(!file && !selectedDocument) {
                throw new Error('Please select a file from your device or a previously uploaded document before moving forward.');
            }

            // set loading flag and sleep briefly 
            setLoading('next');
            await Utils.sleep(0.25);

            // submit file to server for processing
            let response = await Request.post(utils, '/leads/', {
                document_id: selectedDocument && selectedDocument.id,
                file: file,
                type: 'new_import_document'
            });

            // end loading and set leads, documents, and selected document in state
            setLoading(false);
            setLeads(response.leads);
            setSelectedDocument(response.document);

            // update documents state if a file was selected from the device
            if(file) {
                setDocuments(docs => {
                    return update(docs, { 
                        $unshift: [{
                            ...response.document,
                            date: moment(response.document.date)
                        }] 
                    });
                });
            }

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue reading your document. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getButtons = () => {
        if(leads) {
            return [{
                key: 'back',
                text: 'Back',
                color: 'dark',
                onClick: onBackToUpload
            },{
                key: 'done',
                text: 'Done',
                color: 'primary',
                loading: loading === 'done',
                onClick: onConfirmUpload
            }];
        }
        return [{
            color: 'primary',
            key: 'next',
            loading: loading === 'next',
            onClick: onUploadDocument,
            text: 'Continue'
        }];
    }

    const getContent = () => {
        if(leads) {
            return getLeadsPreview();
        }
        return getDefaultContent();
    }

    const getDefaultContent = () => {
        return (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <LayerItem 
                collapsed={false}
                lastItem={false}
                title={'About Lead Imports'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        padding: '12px 16px 12px 16px',
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.title(),
                            display: 'block',
                            fontWeight: 500,
                            whiteSpace: 'normal',
                            width: '100%'
                        }}>{documents.length > 0 ? `Please choose a document from the list below or click the "Browse" button to select a spreadsheet from your device to continue. You'll have a chance to select which fields you want to include in your lead import on the next screen.` : `Click the "Browse" button to select a spreadsheet from your device to continue. You'll have a chance to select which fields you want to include in your lead import on the next screen.`}</span>
                    </div>
                </LayerItem>
                <LayerItem 
                collapsed={false}
                lastItem={documents.length === 0}
                title={'Upload a File'}>
                    <FilePickerField
                    utils={utils}
                    value={file}
                    fileTypes={['xls', 'xlsx']}
                    onChange={file => {
                        setFile(file);
                        setSelectedDocument(null);
                    }} />
                </LayerItem>
                {getDocuments()}
            </div>
        )
    }

    const getDocuments = () => {
        return documents.length > 0 && (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Uploaded Documents'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    textAlign: 'left'
                }}>
                    {documents.map((doc, index) => {
                        return (
                            Views.entry({
                                badge: {
                                    text: selectedDocument && selectedDocument.id === doc.id ? 'Selected' : null,
                                    color: Appearance.colors.primary()
                                },
                                bottomBorder: index !== documents.length - 1,
                                hideOnClickArrow: true,
                                icon: {
                                    path: window.theme === 'dark' ? 'images/xls-icon-white.png' : 'images/xls-icon-grey.png',
                                    style: {
                                        boxShadow: null
                                    },
                                    imageStyle: {
                                        backgroundColor: Appearance.colors.transparent,
                                        borderRadius: 0,
                                        objectFit: 'contain'
                                    }
                                },
                                key: index,
                                onClick: onSelectDocument.bind(this, doc),
                                rightContent: getDocumentAccessoryIcon(doc),
                                subTitle: Utils.formatDate(doc.date),
                                title: doc.name
                            })
                        )
                    })}
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        offset={offset}
                        onClick={next => setOffset(next)} />
                    )}
                </div>
            </LayerItem>
        )
    }

    const getDocumentAccessoryIcon = doc => {

        // only return option to remove document for the client's primary
        let { id } = utils.client.get();
        let { client_id, level } = utils.user.get();
        let canEditDocuments = level <= User.levels.get().admin || (id === client_id && level <= User.levels.get().primary);
        return canEditDocuments && (
            <img
            className={'text-button'}
            src={'images/red-x-icon.png'}
            onClick={onRemoveDocument.bind(this, doc)}
            style={{
                width: 25,
                height: 25,
                objectFit: 'contain',
                marginLeft: 8
            }}/>
        )
    }

    const getHeaders = () => {
        return [{
            id: 'custom',
            title: '* Custom Column *'
        },{
            id: 'assignment_user',
            title: 'Assigned User'
        },{
            id: 'locality',
            title: 'City'
        },{
            id: 'notes',
            title: 'Comments'
        },{
            id: 'date',
            title: 'Date Created'
        },{
            id: 'email_address',
            title: 'Email Address'
        },{
            id: 'first_name',
            title: 'First Name'
        },{
            id: 'last_name',
            title: 'Last Name'
        },{
            id: 'lead_credit_user',
            title: 'Lead Credit'
        },{
            id: 'lead_script',
            title: 'Lead Script'
        },{
            id: 'lead_sub_type',
            title: 'Lead Sub Type'
        },{
            id: 'lead_type',
            title: 'Lead Type'
        },{
            id: 'homeowner_status',
            title: 'Homeowner Status'
        },{
            id: 'marital_status',
            title: 'Marital Status'
        },{
            id: 'setter_user',
            title: 'Setter'
        },{
            id: 'out_of_service_area',
            title: 'Out of Service Area (Yes/No)'
        },{
            id: 'occupational_status',
            title: 'Occupational Status'
        },{
            id: 'phone_number',
            title: 'Phone Number'
        },{
            id: 'priority',
            title: 'Priority'
        },{
            id: 'survey_credit',
            title: 'Survey'
        },{
            id: 'spouse_email_address',
            title: 'Spouse Email Address'
        },{
            id: 'spouse_first_name',
            title: 'Spouse First Name'
        },{
            id: 'spouse_last_name',
            title: 'Spouse Last Name'
        },{
            id: 'spouse_phone_number',
            title: 'Spouse Phone Number'
        },{
            id: 'administrative_area_level_1',
            title: 'State/Province'
        },{
            id: 'status',
            title: 'Status'
        },{
            id: 'street_address_1',
            title: 'Street Address'
        },{
            id: 'enrollment_user',
            title: 'Survey Credit'
        },{
            id: 'tags',
            title: 'Tags'
        },{
            id: 'postal_code',
            title: 'Zipcode'
        }].sort((a,b) => {
            return a.title.localeCompare(b.title);
        });
    }

    const getHeaderList = index => {

        // declare headers and merge in custom elements
        let headers = getHeaders().concat(customValues).sort((a,b) => a.title.localeCompare(b.title));
        let selected = selectedMap.props[index] ? headers.find(header => header.id === selectedMap.props[index].id) : null;

        return (
            <CustomListField
            containerStyle={{ 
                minWidth: 175 
            }}
            key={index}
            items={headers}
            onChange={onHeaderListItemChange.bind(this, index)} 
            onRemoveItem={onRemoveCustomLeadColumn}
            placeholder={'None'}
            removable={true}
            value={selected} />
        )
    }

    const getLeadsPreview = () => {
        return (
            <div style={{
                alignItems: 'flex-start',
                display: 'flex',
                flexDirection: 'row',
                width: '100%'
            }}>
                <div 
                className={`custom-scrollbars ${window.theme}`}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginRight: 12,
                    maxHeight: window.innerHeight - 110,
                    maxWidth: 350,
                    overflowY: 'scroll',
                    padding: 12,
                    textAlign: 'left'
                }}>
                    <LayerItem title={'How to Import Leads'}>
                        <span style={{
                            ...Appearance.textStyles.subTitle(),
                            whiteSpace: 'normal'
                        }}>{`${leads.length === 25 ? 'The first 25 leads' : 'All the leads'} from your document are shown to the right. Click the drop down menu above each column and choose how you want the column to be used during the import process. This will let us know how to use the information in the column. Any column where no selection has been made with the drop down menu will be skipped during the import process. Before submitting your leads, check that none of the columns are repeated as each column name can only be used one time.`}</span>
                    </LayerItem>

                    <LayerItem title={'Including Dates and Times'}>
                        <span style={{
                            ...Appearance.textStyles.subTitle(),
                            whiteSpace: 'normal'
                        }}>{`To accommodate clients nationally and internationally, we store dates and times using the ISO Standard Date format. For today's date and time, that format would look like this ${moment().format('YYYY-MM-DD HH:mm:ss')}. This format uses four digits for the year, two digits for the month, two digits for the day, two digits for the hour using a 24 hour scale, two digits for the minute, and two digits for the second.`}</span>
                    </LayerItem>

                    <LayerItem 
                    lastItem={true}
                    title={'Assigning Users'}>
                        <span style={{
                            ...Appearance.textStyles.subTitle(),
                            whiteSpace: 'normal'
                        }}>{`The lead Import Wizard supports automatic assignments for the primary assignment, lead credit, setter, and survey credit. To make these assignments, include the user id of the user in the column you would like to assign. You can find the user id for anyone in your client workspace by clickng on their user account on the Users->Accounts page.`}</span>
                    </LayerItem>
                </div>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <div style={{
                        alignItems: 'center',
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        display: 'flex',
                        flexDirection: 'row',
                        padding: 15
                    }}>
                        <ListField
                        disablePlaceholder={true}
                        placeholder={'Layout Presets'}
                        value={selectedMap.name}
                        onChange={item => {
                            setSelectedMap(item && maps.find(entry => entry.id === item.id));
                        }}
                        items={maps.map(entry => ({
                            id: entry.id,
                            title: entry.name
                        }))}/>
                        <div style={{
                            width: 25,
                            height: 25,
                            minWidth: 25,
                            minHeight: 25,
                            marginLeft: 8
                        }}>
                            <img
                            className={'text-button'}
                            src={'images/plus-button-green-small.png'}
                            onClick={onNewMapClick}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                            }} />
                        </div>
                    </div>
                    <div
                    className={`custom-scrollbars ${window.theme}`}
                    style={{
                        overflow: 'scroll',
                        maxWidth: '100%',
                        maxHeight: window.innerHeight - 200
                    }}>
                        <table
                        className={'px-3 py-2 m-0'}
                        style={{
                            width: '100%'
                        }}>
                            <tbody style={{
                                width: '100%'
                            }}>
                                <tr
                                key={index}
                                style={{
                                    borderBottom: `1px solid ${Appearance.colors.divider()}`
                                }}>
                                    {leads[0].map((_, index) => {
                                        return (
                                            <td
                                            key={index}
                                            className={'px-3 py-2'}
                                            style={{
                                                minWidth: 175
                                            }}>
                                                {getHeaderList(index)}
                                            </td>
                                        )
                                    })}
                                </tr>
                                {leads.map((fields, index) => {
                                    return (
                                        <tr
                                        key={index}
                                        style={{
                                            borderBottom: `1px solid ${Appearance.colors.divider()}`
                                        }}>
                                            {fields.map((field, index) => {
                                                return (
                                                    <td
                                                    key={index}
                                                    className={'px-3 py-2'}>
                                                        <span style={Appearance.textStyles.subTitle()}>{field}</span>
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )
    }

    const fetchCustomColumns = async () => {
        try {
            let { columns } = await Request.get(utils, '/clients/', {
                type: 'custom_lead_columns'
            });
            setCustomValues(columns.map(column => ({
                ...column,
                removable: true
            })));

        } catch(e) {
            console.error(e.message);
        }
    }

    const fetchResources = async () => {
        try {
            let { documents, maps, paging } = await Request.get(utils, '/leads/', {
                type: 'import_resources',
                limit: limit,
                offset: offset
            });

            setLoading(false);
            setMaps(maps);
            setPaging(paging);
            setDocuments(documents.map(doc => ({ 
                ...doc, 
                date: moment(doc.date)
            })));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the list of documents for your client workspace. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close')
            });
        }
    }

    useEffect(() => {
        fetchResources();
    }, [offset]);

    useEffect(() => {
        fetchCustomColumns();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Lead Import Wizard'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: leads ? 'fullscreen' : 'medium'
        }}>
            {getContent()}
        </Layer>
    )
}

export const LeadClients = ({ abstract, index, options, utils }) => {

    const layerID = `lead_clients${abstract.getID()}`;
    const [loading, setLoading] = useState(true);
    const [layerState, setLayerState] = useState(null);
    const [leads, setLeads] = useState([]);

    const onLeadClick = lead => {
        utils.layer.open({
            abstract: Abstract.create({
                object: lead,
                type: 'leads'
            }),
            Component: LeadDetails,
            id: `lead_details_${lead.id}`,
            permissions: ['leads.details']
        });
    }

    const onAddNewLead = () => {

        let lead = Lead.new();
        lead.client = abstract.object;
        lead.user = utils.user.get();

        utils.layer.open({
            id: 'new_lead',
            abstract: Abstract.create({
                type: 'leads',
                object: lead
            }),
            Component: AddEditLead.bind(this, {
                isNewTarget: true,
                onAddLead: nextLead => {
                    setLeads(leads => update(leads, {
                        $unshift: [nextLead]
                    }))
                }
            })
        });
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'new',
            onClick: onAddNewLead,
            text: 'Add a Lead'
        }];
    }

    const fetchLeads = async () => {
        try {
            let { leads } = await Request.get(utils, '/leads/', {
                type: 'leads',
                id: abstract.getID()
            })
            setLoading(false);
            setLeads(leads.map(lead => Lead.create(lead)));

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the leads for this lead. ${e.message || 'An unknown error occurred'}`,
                onClick: () => setLayerState('close')
            })
        }
    }

    useEffect(() => {
        fetchLeads();
        utils.content.subscribe(layerID, ['leads'], {
            onFetch: fetchLeads,
            onUpdate: fetchLeads
        });
        return () => {
            utils.content.unsubscribe(layerID);
        }
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={`Leads from ${abstract.getTitle()}`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            {leads.length > 0
                ?
                <div style={{
                    borderRadius: 10,
                    border: `1px solid ${Appearance.colors.divider()}`
                }}>
                    {leads.map((lead, index) => {

                        let badge = getLeadStatus(utils, lead);
                        return (
                            Views.entry({
                                key: index,
                                title: lead.full_name,
                                subTitle: `Added ${moment(lead.added).format('MMMM Do, YYYY [at] h:mma')}`,
                                badge: badge ? {
                                    text: badge.title,
                                    color: badge.color
                                } : null,
                                hideIcon: true,
                                singleItem: leads.length === 1,
                                firstItem: index === 0,
                                lastItem: index === leads.length - 1,
                                bottomBorder: true,
                                onClick: onLeadClick.bind(this, lead)
                            })
                        )
                    })}
                </div>
                :
                loading
                    ?
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: 75
                    }}>
                        <LottieView
                        loop={true}
                        autoPlay={true}
                        source={window.theme === 'dark' ? 'files/lottie/dots-white.json' : 'files/lottie/dots-grey.json'}
                        style={{
                            width: 50,
                            height: 50
                        }}/>
                    </div>
                    :
                    <div style={{
                        borderRadius: 10,
                        border: `1px solid ${Appearance.colors.divider()}`
                    }}>
                        {Views.entry({
                            bottomBorder: false,
                            hideIcon: true,
                            subTitle: 'No leads were found in the system',
                            title: 'No Leads Found'
                        })}
                    </div>
            }
        </Layer>
    )
}

export const LeadOutboundCalls = ({ abstract, index, options, utils }) => {

    const layerID = `lead_outbound_calls_${abstract.getID()}`;
    const [loading, setLoading] = useState(true);
    const [layerState, setLayerState] = useState(null);
    const [lead, setLead] = useState(abstract.object);
    const [callLogs, setOutboundCalls] = useState([]);

    const getStatus = () => {
        if(abstract.object.status !== Lead.status.get().do_not_call) {
            return null;
        }
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                marginBottom: 12
            }}>
                {Views.entry({
                    bottomBorder: false,
                    icon: {
                        path: 'images/do-not-call-red.png'
                    },
                    subTitle: 'This lead has been marked as "Do Not Call"',
                    title: 'Do Not Call'
                })}
            </div>
        )
    }

    const fetchOutboundCalls = async () => {
        try {
            let { outbound_calls } = await Request.get(utils, '/leads/', {
                type: 'outbound_calls',
                id: abstract.getID()
            })
            setLoading(false);
            setOutboundCalls(outbound_calls.map(log => OutboundCall.create(log)));

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the call logs for this lead. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close')
            })
        }
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'do_not_call',
                permissions: abstract.object.status === Lead.status.get().do_not_call ? ['leads.actions.do_not_call.delete'] : ['leads.actions.do_not_call.new'],
                title: abstract.object.status === Lead.status.get().do_not_call ? 'Remove Do Not Call Tag' : 'Mark as Do Not Call',
                style: abstract.object.status === Lead.status.get().do_not_call ? 'default' : 'destructive'
            }],
            target: evt.target
        }, key => {
            if(key === 'do_not_call') {
                onSetDoNotCall();
                return;
            }
        })
    }

    const onSetDoNotCall = () => {
        utils.alert.show({
            title: abstract.object.status === Lead.status.get().do_not_call ? 'Remove Do Not Call Tag' : 'Mark as Do Not Call',
            message: `Are you sure that you want to ${abstract.object.status === Lead.status.get().do_not_call ? 'remove the "Do Not Call" tag from this Lead' : 'mark this Lead as "Do Not Call"'}?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: abstract.object.status === Lead.status.get().do_not_call ? 'default' : 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: abstract.object.status === Lead.status.get().do_not_call ? 'cancel' : 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSetDoNotCallConfirm();
                    return;
                }
            }
        })
    }

    const onSetDoNotCallConfirm = async () => {
        try {

            setLoading(true);
            await Utils.sleep(0.25);
            let { status } = await Request.post(utils, '/leads/', {
                id: abstract.getID(),
                status: abstract.object.status === Lead.status.get().do_not_call ? Lead.status.get().new : Lead.status.get().do_not_call,
                type: 'set_status'
            });

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Status Updated',
                message: `This lead has been ${abstract.object.status === Lead.status.get().do_not_call ? 'un-tagged as "Do Not Call"' : 'has been tagged as "Do Not Call"'}`
            });

            // update status object, remove sale date, and notify subscribers of data change
            abstract.object.sale_date = null;
            abstract.object.status = status;
            utils.content.update(abstract);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating this lead ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onOutboundCallClick = call => {
        utils.layer.open({
            abstract: Abstract.create({
                object: call,
                type: 'outbound_calls'
            }),
            Component: OutboundCallDetails,
            id: `outbound_call_details_${call.id}`,
            permissions: ['calls.details']
        });
    }

    const onDeleteOutboundCall = log => {
        utils.alert.show({
            title: 'Delete Call',
            message: 'Are you sure that you want to delete this call? This action can not be undone',
            buttons: [{
                key: 'confirm',
                title: 'Delete',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onDeleteOutboundCallConfirm(log);
                }
            }
        })
    }

    const onDeleteOutboundCallConfirm = async log => {
        try {

            setLoading(true);
            await Utils.sleep(0.25);
            await Request.post(utils, '/appointments/', {
                type: 'delete_outbound_call',
                id: log.id
            });

            setLoading(false);
            utils.content.fetch('outbound_calls');

            utils.banner.show({
                title: 'Call Log Deleted',
                message: 'This call log has been deleted',
                onShow: () => setLayerState('close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting this call log. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getButtons = () => {
        return [{
            color: 'secondary',
            key: 'options',
            onClick: onOptionsClick,
            text: 'Options'
        }];
    }

    useEffect(() => {

        fetchOutboundCalls();
        utils.content.subscribe(layerID, ['outbound_calls', 'leads', 'lead_status'], {
            onFetch: fetchOutboundCalls,
            onUpdate: abstract => {
                switch(abstract.type) {
                    case 'outbound_calls':
                    setOutboundCalls(callLogs => {
                        return callLogs.map(callLog => {
                            return callLog.id === abstract.getID() ? abstract.object : callLog
                        })
                    })
                    break;

                    case 'leads':
                    setLead(lead => {
                        return lead.id === abstract.getID() ? abstract.object : lead
                    });
                    break;

                    case 'lead_status':
                    setLead(lead => {
                        if(lead.id === abstract.getID()) {
                            lead.status = abstract.object.status;
                        }
                        return lead;
                    });
                    break;
                }
            }
        });
        return () => {
            utils.content.unsubscribe(layerID);
        }
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={`Outbound Calls for Lead #${abstract.getID()}`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            {callLogs.length > 0
                ?
                <>
                {getStatus()}
                <div style={{
                    borderRadius: 10,
                    border: `1px solid ${Appearance.colors.divider()}`
                }}>
                {callLogs.map((call, index) => {
                    return (
                        Views.entry({
                            bottomBorder: index !== callLogs.length - 1,
                            hideIcon: true,
                            key: index,
                            onClick: onOutboundCallClick.bind(this, call),
                            subTitle: moment(call.created_at).format('MMMM Do, YYYY [at] h:mma'),
                            title: call.lead_full_name
                        })
                    )
                })}
                </div>
                </>
                :
                loading
                    ?
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: 75
                    }}>
                        <LottieView
                        loop={true}
                        autoPlay={true}
                        source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                        style={{
                            width: 50,
                            height: 50
                        }}/>
                    </div>
                    :
                    <div style={{
                        borderRadius: 10,
                        border: `1px solid ${Appearance.colors.divider()}`
                    }}>
                        {Views.entry({
                            title: 'No Call Logs Found',
                            subTitle: 'No call logs were found in the system',
                            bottomBorder: false,
                            hideIcon: true
                        })}
                    </div>
            }
        </Layer>
    )
}

export const LeadDetails = ({ abstract, index, options, utils }) => {

    const layerID = `lead_details_${abstract.getID()}`;
    const eventsLimit = 5;

    const [appointment, setAppointment] = useState(null);
    const [client, setClient] = useState(abstract.object.client);
    const [customerResponse, setCustomerResponse] = useState(null);
    const [events, setEvents] = useState([]);
    const [eventsOffset, setEventsOffset] = useState(0);
    const [eventsPaging, setEventsPaging] = useState(null);
    const [hasTextMessages, setHasTextMessages] = useState(false);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lead, setLead] = useState(abstract.object);
    const [tags, setTags] = useState(null);
    const [twilioCalls, setOutboundCalls] = useState([]);
    const [unstructuredContent, setUnstructuredContent] = useState([]);

    const onAppointmentClick = async () => {
        try {
            let target = await Appointment.get(utils, appointment.id);
            utils.layer.open({
                abstract: Abstract.create({
                    object: target,
                    type: 'appointments'
                }),
                Component: AppointmentDetails,
                id: `appointment_details_${target.id}`,
                permissions: ['appointments.details']
            });

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onArchiveClick = () => {
        utils.alert.show({
            title: `Archive List`,
            message: `This lead was added to your Archive list. This means that this Lead will not show up on any Client lead lists or reports.`,
            buttons: [{
                key: 'unarchive',
                title: 'Unarchive',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: async key => {
                if(key === 'unarchive') {
                    try {
                        await Utils.sleep(0.5);
                        onSetArchiveStatus();
                    } catch(e) {
                        console.error(e.message);
                    }
                    return;
                }
            }
        })
    }

    const onBookAppointmentClick = () => {
        try {

            // prevent booking for archived
            if(abstract.object.active === false) {
                throw new Error('This lead is currently on the Archived list and is not available for a Appointment')
            }

            // prevent booking for do not call
            if(abstract.object.status.code === Lead.status.get().do_not_call) {
                throw new Error('This lead is currently on the Do Not Call list and is not available for a Appointment')
            }

            // prevent booking for leads that are being transfered
            if(abstract.object.transfer && abstract.object.transfer.status.code === Lead.Transfer.status.in_progress) {
                throw new Error('This lead is currently part of a Lead Transfer and is not available for a Appointment')
            }

            // present appointment booking options
            utils.layer.open({
                id: `book_appointment_${abstract.getID()}`,
                abstract: abstract,
                Component: BookAppointmentFromLead.bind(this, {})
            })

        } catch(e) {
            utils.alert.show({
                title: 'Just a Second',
                message: e.message || 'An unknown error occurred'
            })
        }
    }

    const onCallClick = call => {
        utils.layer.open({
            abstract: Abstract.create({
                type: 'outbound_calls',
                object: call
            }),
            Component: OutboundCallDetails,
            id: `outbound_call_details_${call.id}`,
            permissions: ['calls.details']
        });
    }

    const onDeleteClick = () => {
        utils.alert.show({
            title: 'Delete Lead',
            message: 'Are you sure that you want to delete this lead from your client workspace? This will also remove any appointments, appointment requests, and call logs where this lead is attached. This can not be undone.',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onDeleteConfirm();
                    return;
                }
            }
        });
    }

    const onDeleteConfirm = async () => {
        try {
            setLoading('options');
            await Utils.sleep(0.25);

            let { appointments } = await Request.post(utils, '/leads/', {
                id: abstract.getID(),
                type: 'delete'
            });

            setLoading(false);
            setTimeout(setLayerState.bind(this, 'close'), 250);

            // show confirmation alert to user where only the lead was delete if applicable
            if(appointments === 0) {
                utils.banner.show({
                    title: 'Lead Deleted',
                    message: 'This lead has been deleted from your client workspace.',
                    onShow: utils.content.fetch.bind(this, 'lead')
                });
                return;
            }

            // show confirmation alert to user with removal totals
            utils.banner.show({
                title: 'Lead Deleted',
                message: `This lead has been deleted along with ${appointments} ${appointments === 1 ? 'appointment' : 'appointments'}.`,
                onShow: utils.content.fetch.bind(this, 'lead')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting this lead. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onDoNotCall = () => {
        utils.alert.show({
            title: `${abstract.object.status.code === Lead.status.get().do_not_call ? 'Remove from' : 'Add to'} Do Not Call List`,
            message: `Are you sure that you want to ${abstract.object.status.code === Lead.status.get().do_not_call ? 'remove this Lead from' : 'add this Lead to'} the Do Not Call List?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onDoNotCallConfrim();
                    return;
                }
            }
        });
    }

    const onDoNotCallClick = () => {
        utils.alert.show({
            title: `Do Not Call List`,
            message: `This lead was added to your Do Not Call list. This means that this Lead will not show up on any Client lead lists or reports and should not be contacted.`,
            buttons: [{
                key: 'remove',
                title: 'Remove from Do Not Call',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: async key => {
                if(key === 'remove') {
                    try {
                        await Utils.sleep(0.5);
                        onDoNotCall();
                    } catch(e) {
                        console.error(e.message);
                    }
                    return;
                }
            }
        });
    }

    const onDoNotCallConfrim = async () => {
        try {
            setLoading('options');
            await Utils.sleep(0.25);

            // prepare next status and submit request to server
            let next_status = abstract.object.status.code === Lead.status.get().do_not_call ? Lead.status.get().new : Lead.status.get().do_not_call;
            let { status } = await Request.post(utils, '/leads/', {
                id: abstract.getID(),
                status: next_status,
                type: 'set_status'
            });

            // end loading, update sale date and status object, and update local state
            setLoading(false);
            abstract.object.sale_date = null;
            abstract.object.status = status;
            setLead(abstract.object);

            // show confirmation alert
            utils.banner.show({
                title: 'Status Updated',
                message: `This lead has been ${status.code === Lead.status.get().do_not_call ? 'added to' : 'removed from'} the Do Not Call List`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue adding thins Lead to the Do Not Call List. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onDisqualify = () => {
        utils.alert.show({
            title: 'Disqualify Lead',
            message: 'Are you sure that you want to disqualify this Lead?',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onDisqualifyConfirm();
                    return;
                }
            }
        });
    }

    const onDisqualifyConfirm = async () => {
        try {
            setLoading('options');
            await Utils.sleep(0.25);

            // submit request to server
            let { status } = await Request.post(utils, '/leads/', {
                id: abstract.getID(),
                status: Lead.status.get().unqualified,
                type: 'set_status'
            })

            // end loading, update status and sale date, and notify subscribers of data change
            setLoading(false);
            abstract.object.sale_date = null;
            abstract.object.status = status;
            utils.content.update(abstract);

            // show confirmation alert
            utils.banner.show({
                title: 'Lead Disqualified',
                message: 'This lead has been disqualified'
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating this lead ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onEditClick = () => {
        utils.layer.open({
            id: `edit_lead_${abstract.getID()}`,
            abstract: abstract,
            Component: AddEditLead.bind(this, {
                isNewTarget: false
            })
        });
    }

    const onLeadScriptClick = async () => {
        try {

            let script = await Lead.Script.get(utils, lead.lead_script.id);
            utils.layer.open({
                id: `lead_script_editor_${lead.id}`,
                Component: LeadScriptEditor.bind(this, {
                    lead: lead,
                    utils: utils,
                    value: script.text
                })
            });

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this lead script. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onLeadTransferClick = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);
            let { lead_transfer } = await Request.get(utils, '/leads/', {
                type: 'lead_transfer_details',
                transfer_id: lead.transfer.id
            });

            setLoading(false);
            utils.layer.open({
                id: `lead_transfer_details_${lead.transfer.id}`,
                abstract: Abstract.create({
                    type: 'lead_transfer',
                    object: Lead.Transfer.create(lead_transfer)
                }),
                Component: LeadTransferDetails
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this lead transfer. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onNewSystemEvent = data => {
        try {
            setEvents(events => {
                return update(events, {
                    $unshift: [SystemEvent.create(data)]
                });
            });
        } catch(e) {
            console.error(e.message);
        }
    }

    const onOptionsClick = evt => {

        // only allow lead transfering for administrators and the primary of the current client
        let client = utils.client.get();
        let canTransferLeads = utils.user.get().level === User.levels.get().admin || (client.primary && utils.user.get().user_id === client.primary.user_id) ? true : false;

        utils.sheet.show({
            items: [{
                key: 'outbound_calls',
                permissions: ['calls.details'],
                title: 'Call Logs',
                style: 'default'
            },{
                key: 'disqualify',
                permissions: ['leads.actions.disqualify'],
                title: 'Disqualify',
                style: 'destructive'
            },{
                key: 'delete',
                permissions: ['leads.actions.delete'],
                title: 'Delete',
                style: 'destructive'
            },{
                key: 'edit',
                permissions: ['leads.actions.edit'],
                title: 'Edit',
                style: 'default'
            },{
                key: 'status',
                permissions: ['leads.actions.status'],
                title: 'Set Status',
                style: 'default'
            },{
                key: 'more',
                title: 'More Options',
                style: 'cancel'
            }],
            sort: false,
            target: evt.target
        }, key => {
            if(key === 'delete') {
                onDeleteClick();
                return;
            }
            if(key === 'edit') {
                onEditClick();
                return;
            }
            if(key === 'outbound_calls') {
                utils.layer.open({
                    abstract: abstract,
                    Component: LeadOutboundCalls,
                    id: `lead_outbound_calls_${abstract.getID()}`
                });
                return;
            }
            if(key === 'disqualify') {
                onDisqualify();
                return;
            }
            if(key === 'more') {
                utils.layer.open({
                    Component: BatchLeadOptions.bind(this, {
                        leads: [abstract.object],
                        onChange: fetchDetails
                    }),
                    id: 'batch_lead_options'
                });
                return;
            }
            if(key === 'status') {
                utils.layer.open({
                    abstract: abstract,
                    Component: SetLeadStatus,
                    id: `set_lead_status_${abstract.getID()}`
                });
                return;
            }
        });
    }

    const onOutOfServiceAreaClick = () => {
        utils.alert.show({
            title: `Out of Service Area`,
            message: `This lead was added to your Out of Service Area list. You can change this by editing the lead and making a new selection for the area labeled "Out of Service Area".`,
            buttons: [{
                key: 'edit',
                title: 'Edit Lead',
                style: 'default',
                visible: lead.client_id === utils.client.get().id
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'edit') {
                    onEditClick();
                    return;
                }
            }
        });
    }

    const onPriorityClick = () => {
        utils.alert.show({
            title: `Priority List`,
            message: `This lead was added to your Priority list. Leads marked as a priority will have a green dot next to them when shown with other leads. You can change this by editing the lead and making a new selection for the area labeled "Priority".`,
            buttons: [{
                key: 'edit',
                title: 'Edit Lead',
                style: 'default',
                visible: lead.client_id === utils.client.get().id
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'edit') {
                    onEditClick();
                    return;
                }
            }
        });
    }

    const onSurveyClick = async id => {
        try {

            // set loading flag and fetch survey details
            setLoading(true);
            let survey = await Survey.get(utils, id);

            // end loading and show survey details layer
            setLoading(false);
            utils.layer.open({
                abstract: Abstract.create({
                    object: survey,
                    type: 'surveys'
                }),
                Component: SurveyDetails,
                id: `survey_details_${id}`,
                permissions: ['surveys.details']
            });
        
        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retreiving the details for this survey. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSetRelease = release => {
        utils.alert.show({
            title: `${release ? 'Release' : 'Unrelease'}`,
            message: `Are you sure that you want to ${release ? 'release' : 'unrelease'} this Lead? ${release ? 'This will open this lead up to the rest of the client.' : 'This will prevent others in the client from viewing this lead.'}`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: release ? 'default' : 'destructive'
            },{
                key: 'cancel',
                title: `Do Not ${release ? 'Release': 'Unrelease'}`,
                style: release ? 'destructive' : 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSetReleaseConfirm(release);
                    return;
                }
            }
        })
    }

    const onSetReleaseConfirm = async release => {
        try {
            setLoading('options');
            await Utils.sleep(0.25);
            await Request.post(utils, '/leads/', {
                type: 'set_release',
                id: abstract.getID(),
                release: release
            });

            setLoading(false);
            abstract.object.released = release;
            utils.content.update(abstract);

            setLead(abstract.object);
            utils.banner.show({
                title: 'Release Updated',
                message: `This lead has been ${release ? 'released and is available for others to view.' : 'unreleased and is no longer available for others to view.'}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the release status for this lead. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSendTextMesssage = async () => {
        try {
            utils.layer.open({
                id: 'lead_text_message_manager',
                Component: LeadTextMessageManager.bind(this, {
                    leads: [ abstract.object ]
                })
            });
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing the text message process. ${e.message || 'An unknown error occured'}`
            })
        }
    }

    const onSetArchiveStatus = () => {
        utils.alert.show({
            title: `${lead.active ? 'Archive' : 'Unarchive'} Lead`,
            message: `Are you sure that you want to ${lead.active ? 'archive' : 'unarchive'} this Lead? ${lead.active ? 'This lead will no longer show up within your pool of leads' : 'This lead will be added back to your pool of leads'}.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: lead.active ? 'destructive' : 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: lead.active ? 'default' : 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSetArchiveStatusConfrim();
                    return;
                }
            }
        })
    }

    const onSetArchiveStatusConfrim = async () => {
        try {

            setLoading('options');
            await Utils.sleep(0.25);

            let status = !abstract.object.active;
            await Request.post(utils, '/leads/', {
                type: 'set_archive_status',
                id: abstract.getID(),
                active: status
            });

            abstract.object.active = status;
            utils.content.update(abstract);

            setLoading(false);
            setLead(abstract.object);

            utils.content.fetch('leads')
            utils.banner.show({
                title: 'Lead Archive Status Updated',
                message: `This lead has been ${status ? 'unarchived' : 'archived'}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the archive status for this lead. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onSystemEventClick = evt => {
        utils.layer.open({
            id: `system_event_details_${evt.id}`,
            abstract: Abstract.create({
                type: 'system_event',
                object: evt
            }),
            Component: SystemEventDetails
        })
    }

    const onTransferLeads = () => {

        let client = null;
        utils.alert.show({
            title: 'Transfer Lead',
            message: `You may transfer this lead to another client workspace if you feel that you no longer need it. We'll send a request to the other client workspace that they can accept or decline. If accepted, the lead will be removed from your client workspace and added to the recipient's client workspace. If declined, the lead will be returned back to your client workspace.`,
            content: (
                <div style={{
                    padding: 12,
                    width: '100%'
                }}>
                    <ClientLookupField
                    utils={utils}
                    icon={'search'}
                    inline={true}
                    globalVisibility={true}
                    onChange={result => client = result}
                    containerStyle={{
                        width: '100%'
                    }}/>
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Send Lead',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm' && client) {
                    onTransferLeadsConfirm(client);
                    return;
                }
            }
        })
    }

    const onTransferLeadsConfirm = async client => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);
            let { transfer_data } = await Request.post(utils, '/leads/', {
                type: 'new_transfer',
                destination_client_id: client.id,
                ids: [ abstract.getID() ]
            });

            setLoading(false);
            abstract.object.transfer = transfer_data;
            utils.content.update(abstract);

            utils.banner.show({
                title: 'Lead Transferred',
                message: `We have sent this lead to the ${client.name} client. We'll notify you when they accept or decline this transfer.`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue transferring your lead. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const getBottomFields = () => {

        if(!lead) {
            return null;
        }

        let items = [{
            key: 'users',
            permissions: ['leads.details.users'],
            title: 'Assignments and Credit',
            items: [{
                key: 'assignment_user',
                title: 'Assigned To',
                value: lead.assignment_user ? lead.assignment_user.full_name : null
            },{
                key: 'user',
                title: 'Lead Credit',
                selected: lead.user,
                value: lead.user ? lead.user.full_name : null
            },{
                key: 'setter_user',
                title: 'Setter',
                value: lead.setter_user ? lead.setter_user.full_name : null
            },{
                key: 'enrollment_user',
                title: 'Survey Credit',
                selected: lead.enrollment_user,
                value: lead.enrollment_user ? lead.enrollment_user.full_name : null
            }]
        },{
            key: 'details',
            lastItem: false,
            permissions: ['leads.details.ext'],
            title: 'Additional Details',
            items: [{
                key: 'archived',
                title: 'Archived',
                value: lead.active ? 'No' : 'Yes'
            },{
                key: 'notes',
                title: 'Comments',
                value: lead.notes
            },{
                key: 'created',
                title: 'Created',
                value: lead.created ? Utils.formatDate(lead.created) : null
            },{
                key: 'client',
                visible: utils.user.get().level <= User.levels.get().admin,
                title: 'Client',
                value: client ? client.name : null
            },{
                key: 'out_of_service_area',
                title: 'Out of Service Area',
                value: lead.out_of_service_area ? 'Yes' : 'No',
            },{
                key: 'priority',
                title: 'Priority',
                value: lead.priority ? 'Yes' : 'No'
            },{
                key: 'adjusted_date',
                title: 'Resorted',
                value: lead.adjusted_date ? Utils.formatDate(lead.adjusted_date) : null
            },{
                key: 'status',
                title: 'Sale Date',
                value: lead.sale_date && Utils.formatDate(lead.sale_date),
                visible: lead.sale_date ? true : false
            },{
                color: lead.status && lead.status.color,
                key: 'status',
                title: 'Status',
                value: lead.status ? lead.status.text : null
            },{
                key: 'tags',
                title: 'Tags',
                value: tags ? Utils.oxfordImplode(tags.map(tag => tag.text)) : null
            },{
                key: 'timezone',
                title: 'Timezone',
                value: lead.timezone
            }]
        }];

        // prevent moving forward if no valid items were provided
        let count = formatFields(utils, items).length;
        if(count === 0) {
            return null;
        }

        return (
            <FieldMapper
            sections={items}
            utils={utils}/>
        )
    }

    const getButtons = () => {

        let clientID = utils.client.get().id;

        // determine if an inbound lead transfer is active
        if(lead.transfer && lead.transfer.status.code === Lead.Transfer.status.in_progress && lead.transfer.to_client === clientID) {
            return [{
                color: 'secondary',
                key: 'lead_transfer',
                loading: loading === 'options',
                onClick: onLeadTransferClick,
                text: 'Lead Transfer Options'
            }];
        }

        // no buttons are required if the clients do no match
        if(lead.client_id !== clientID) {
            return null;
        }

        // return default buttons
        return [{
            color: 'secondary',
            key: 'options',
            loading: loading === 'options',
            onClick: onOptionsClick,
            text: 'Options'
        },{
            color: 'primary',
            key: 'book_appointment',
            loading: loading === 'book_appointment',
            onClick: onBookAppointmentClick,
            permissions: ['leads.actions.set_appointment'],
            text: 'Set Appointment'
        }];
    }

    const getCalls = () => {
    
        // prevent moving forward if feature is disabled for current user
        //if(utils.user.permissions.get('leads.details.calls') === false) {
            //return null;
        //}

        // render a placeholder if the first fetch for call logs is active
        if(loading === 'init') {
            return (
                <LayerItem title={'Outbound Calls'}>
                    <div style={Appearance.styles.unstyledPanel()}>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            padding: 15
                        }}>
                            <LottieView
                            autoPlay={true}
                            loop={true}
                            source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                            style={{
                                height: 50,
                                width: 50
                            }}/>
                        </div>
                    </div>
                </LayerItem>
            )
        }
        return (
            <LayerItem 
            collapsed={false}
            title={'Outbound Calls'}>
                <div style={Appearance.styles.unstyledPanel()}>
                    {twilioCalls.length === 0 && (
                        Views.entry({
                            bottomBorder: false,
                            hideIcon: true,
                            subTitle: 'No outbound calls have been logged for this appointment',
                            title: 'No Calls Found'
                        })
                    )}
                    {twilioCalls.map((call, index) => {
                        return (
                            Views.entry({
                                badge: {
                                    color: Appearance.colors.primary(),
                                    text: Utils.parseDuration(call.duration)
                                },
                                bottomBorder: index !== twilioCalls.length - 1,
                                hideIcon: true,
                                key: index,
                                onClick: onCallClick.bind(this, call),
                                subTitle: moment(call.created_at).format('MMMM Do, YYYY [at] h:mma'),
                                title: call.user?.full_name || 'Caller name not available'
                            })
                        )
                    })}
                </div>
            </LayerItem>
        )
    }

    const getHeaderBanner = () => {

        // generate list of available banners
        let clientID = utils.client.get().id;
        let fields = [{
            key: 'archived',
            title: 'Archived',
            value: lead.active !== true,
            color: Appearance.colors.red,
            message: 'This lead has been added to the archived list',
            onClick: onArchiveClick
        },{
            key: 'do_not_call',
            title: 'Do Not Call',
            value: lead.status.code === Lead.status.get().do_not_call,
            color: Appearance.colors.darkRed,
            message: 'This lead has been added to the do not call list',
            onClick: onDoNotCallClick
        },{
            key: 'appointment_details',
            title: appointment && `Appointment Scheduled`,
            value: appointment ? true : false,
            color: appointment && appointment.status.color,
            message: appointment && Utils.formatDate(appointment.start_date, { include_timezone: true, timezone:appointment.timezone, utc: true }),
            onClick: onAppointmentClick,
            rightContent: appointment && (
                <AltBadge 
                content={appointment.status} 
                invert={true} />
            )
        },{
            key: 'lead_transfer_inbound',
            title: 'Inbound Lead Transfer',
            value: lead.transfer && lead.transfer.status.code === Lead.Transfer.status.in_progress && lead.transfer.from_client_id !== clientID ? true : false,
            color: Appearance.colors.darkGrey,
            message: 'This lead has been transfered to your client workspace from another client workspace',
            onClick: onLeadTransferClick
        },{
            key: 'lead_transfer_outbound',
            title: 'Outbound Lead Transfer',
            value: lead.transfer && lead.transfer.status.code === Lead.Transfer.status.in_progress && lead.transfer.from_client_id === clientID ? true : false,
            color: Appearance.colors.darkGrey,
            message: 'This lead has a pending outbound transfer to another client workspace',
            onClick: onLeadTransferClick
        },{
            key: 'out_of_service_area',
            title: 'Out of Service Area',
            value: lead.out_of_service_area === true && lead.client_id === utils.client.get().id,
            color: Appearance.colors.lightGrey,
            message: `This lead has been marked as out of your client workspace service area`,
            onClick: onOutOfServiceAreaClick
        },{
            key: 'priority',
            title: 'Priority',
            value: lead.priority === true,
            color: Appearance.colors.primary(),
            message: 'This lead has been marked as a client workspace priority',
            onClick: onPriorityClick
        },{
            key: 'released',
            title: 'Unreleased',
            value: lead.released === false,
            color: Appearance.colors.lightGrey,
            message: 'This lead has not been released to the rest of the client workspace.',
            onClick: onSetRelease
        }];

        // prevent moving forward if no banners are available to show
        if(!fields.find(field => field.value === true)) {
            return null;
        }

        return (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                marginBottom: 12,
                width: '100%'
            }}>
                {fields.filter(field => {
                    return field.value === true;
                }).map((field, index, fields) => (
                    <div
                    key={index}
                    className={'text-button'}
                    onClick={field.onClick}
                    style={{
                        alignItems: 'center',
                        background: Appearance.colors.softGradient(field.color),
                        border: `2px solid ${field.color}`,
                        borderRadius: 10,
                        display: 'flex',
                        flexDirection: 'row',
                        marginBottom: index === fields.length - 1 ? 12 : 8,
                        minWidth: 0,
                        overflow: 'hidden',
                        padding: '6px 10px 6px 10px',
                        width: '100%'
                    }}>
                        <img
                        src={'images/alert-icon-white-small.png'}
                        style={{
                            height: 30,
                            marginRight: 8,
                            minHeight: 30,
                            minWidth: 30,
                            width: 30
                        }} />
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            minWidth: 0
                        }}>
                            <span style={{
                                ...Appearance.textStyles.title(),
                                color: 'white',
                                fontWeight: 800
                            }}>{field.title}</span>
                            <span style={{
                                ...Appearance.textStyles.subTitle(),
                                color: 'white',
                                fontWeight: 600
                            }}>{field.message}</span>
                        </div>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row',
                            marginLeft: 8
                        }}>
                            {field.rightContent}
                            <img
                            src={'images/next-arrow-white-small.png'}
                            style={{
                                height: 12,
                                objectFit: 'contain',
                                width: 12
                            }} />
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    const getMessages = () => {

        // determine if user has permission to view this content
        if(hasTextMessages === false || utils.user.permissions.get('leads.details.sms') === false) {
            return null;
        }

        return (
            <LayerItem
            collapsed={false}
            title={'Text Messages'}>
                <LeadTextMessages
                abstract={abstract}
                style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}
                utils={utils} />
            </LayerItem>
        )
    }

    const getSystemEvents = () => {
        return (
            <SystemEventsLayerItem 
            abstract={abstract} 
            permissions={['leads.details.system_events']}
            utils={utils} />
        )
    }

    const getTopFields = () => {

        if(!lead) {
            return null;
        }

        let items = [{
            key: 'customer',
            permissions: ['leads.details.customer'],
            title: 'Customer Details',
            items: [{
                key: 'email_address',
                title: 'Email Address',
                value: lead.email_address
            },{
                key: 'first_name',
                title: 'First Name',
                value: lead.first_name
            },{
                key: 'homeowner_status',
                title: 'Homeowner Status',
                value: lead.homeowner_status ? lead.homeowner_status.text : null
            },{
                key: 'id',
                title: 'ID',
                value: lead.id
            },{
                key: 'last_name',
                title: 'Last Name',
                value: lead.last_name
            },{
                key: 'marital_status',
                title: 'Marital Status',
                value: lead.marital_status ? lead.marital_status.text : null
            },{
                key: 'occupational_status',
                title: 'Occupational Status',
                value: lead.occupational_status ? lead.occupational_status.text : null
            },{
                key: 'phone_number',
                title: 'Phone Number',
                value: lead.phone_number && Utils.formatPhoneNumber(lead.phone_number)
            }]
        },{
            key: 'spouse',
            permissions: ['leads.details.spouse'],
            title: 'Spouse Details',
            items: [{
                key: 'spouse_email_address',
                title: 'Email Address',
                value: lead.spouse_email_address
            },{
                key: 'spouse_first_name',
                title: 'First Name',
                value: lead.spouse_first_name
            },{
                key: 'spouse_last_name',
                title: 'Last Name',
                value: lead.spouse_last_name
            },{
                key: 'spouse_phone_number',
                title: 'Phone Number',
                value: lead.spouse_phone_number && Utils.formatPhoneNumber(lead.spouse_phone_number)
            }]
        }];

        if(customerResponse) {
            items = items.concat([{
                key: 'customer_response',
                permissions: ['leads.details.customer_response'],
                title: lead.lead_type && lead.lead_type.id === Lead.Type.static.get().recruiting ? 'Recruiting Survey Response' : 'Customer Survey Response',
                items: [{
                    key: 'date',
                    title: 'Date Submitted',
                    value: Utils.formatDate(customerResponse.date)
                }].concat(customerResponse.responses.map((item, index) => {
                    return {
                        key: index,
                        onClick: item.url ? window.open.bind(this, item.url) : null,
                        title: `${index + 1}. ${item.title}`,
                        value: item.answer
                    }
                }))
            }]);
        }

        items = items.concat([{
            key: 'location',
            permissions: ['leads.details.location'],
            title: 'Location',
            visible: lead.address || lead.location ? true : false,
            items: [{
                component: 'map',
                key: 'location',
                static: lead.static_map_image_url,
                title: 'Location',
                valid: lead.location ? true : false,
                value: lead.location,
                visible: lead.location ? true : false
            },{
                key: 'address',
                title: Utils.isAddressComplete(lead.address) ? 'Address' : 'Approximate Location',
                value: lead.address ? Utils.formatAddress(lead.address) : null
            },{
                key: 'maps',
                onClick: () => {
                    let address = Utils.formatAddress(lead.address);
                    window.open(`https://www.google.com/maps/place/${encodeURIComponent(address)}`)
                },
                title: 'Directions',
                value: 'Click to View',
                visible: Utils.isAddressComplete(lead.address)
            }]
        },{
            key: 'lead',
            lastItem: false,
            permissions: ['leads.details.general'],
            title: 'About this Lead',
            items: [{
                key: 'released',
                title: 'Released',
                value: lead.released ? 'Yes' : 'No'
            },{
                key: 'lead_type',
                title: 'Script',
                value: lead.lead_script ? lead.lead_script.title : null,
                onClick: lead.lead_script ? onLeadScriptClick : null
            },{
                key: 'lead_sub_type',
                title: 'Sub-Type',
                value: lead.lead_sub_type ? lead.lead_sub_type.text : null
            },{
                key: 'lead_type',
                title: 'Type',
                value: lead.lead_type && (
                    <div style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-end'
                    }}>
                        <span>{lead.lead_type.text}</span>
                        {lead.lead_type.icon && (
                            <img
                            src={lead.lead_type.icon.url}
                            style={{
                                height: 20,
                                marginLeft: 8,
                                objectFit: 'contain',
                                width: 20
                            }} />
                        )}
                    </div>
                )
            }]
        }]);

        // prevent moving forward if no valid items were provided
        let count = formatFields(utils, items).length;
        if(count === 0) {
            return null;
        }

        return (
            <FieldMapper
            sections={items}
            utils={utils}/>
        )
    }

    const getUnstructuredContent = () => {

        // no additional logic is required if no unstructured content id is available
        if(!lead.unstructured_content_id) {
            return null;
        }

        // prevent moving forward if feature is disabled for current user
        if(utils.user.permissions.get('leads.details.custom') === false) {
            return null;
        }

        // prepare section title
        let title = lead.lead_type.id === Lead.Type.static.get().survey_monkey_lead_generation ? 'Custom Survey Monkey Details' : 'Custom Details';

        // show loading component if applicable
        if(unstructuredContent.length === 0) {
            return (
                <LayerItem title={title}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        height: 100,
                        justifyContent: 'center',
                        width: '100%'
                    }}>
                        <LottieView
                        autoPlay={true}
                        loop={true}
                        source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                        style={{
                            width: 40,
                            height: 40
                        }}/>
                    </div>
                </LayerItem>
            )
        }

        // prepare field mapper items
        let items = [{
            key: 'unstructured_content',
            title: title,
            items: unstructuredContent.map(item => ({
                key: item.id,
                title: item.title,
                value: item.value
            })).sort((a,b) => {
                return a.title.localeCompare(b.title);
            })
        }];

        return (
            <FieldMapper
            sections={items}
            utils={utils}/>
        )
    }

    const fetchEvents = async () => {
        try {
            setLoading('system_events');
            let { events, paging } = await Request.get(utils, '/utils/', {
                limit: eventsLimit,
                offset: eventsOffset,
                target_id: abstract.getID(),
                target_type: abstract.type,
                type: 'system_events'
            });

            setLoading(false);
            setEventsPaging(paging);
            setEvents(events.map(evt => SystemEvent.create(evt)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the system events list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const fetchDetails = async () => {
        try {

            let { appointment, customer_response, client, has_text_messages, lead, tags, outbound_calls, unstructured_content } = await Request.get(utils, '/leads/', {
                id: abstract.getID(),
                type: 'ext_details'
            });

            // set and format customer response if applicable
            setCustomerResponse(customer_response && {
                ...customer_response,
                date: moment(customer_response.date)
            });

            // set client, appointment details, and unstructured custom content
            setClient(Client.create(client));
            setAppointment(appointment && Appointment.create(appointment));
            setUnstructuredContent(unstructured_content);
            setHasTextMessages(has_text_messages);

            // set lead client referral
            abstract.object.client = lead.client;
            setLead(abstract.object);

            // set formatted tags
            abstract.object.tags = tags;
            setTags(tags);

            // update state with formatted call logs
            setOutboundCalls(outbound_calls.map(call => OutboundCall.create(call)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retreiving some of the information for this lead. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const connectToSockets = async () => {
        try {

            // create system event subscriptions
            await utils.sockets.emit('system', 'join_events', { tag: abstract.getTag()} );
            await utils.sockets.on('system', 'on_new_event', onNewSystemEvent);

        } catch(e) {
            console.error(e.message);
        }
    }

    const disconnectFromSockets = async () => {
        try {
            await utils.sockets.off('system', 'on_new_event', onNewSystemEvent);
        } catch(e) {
            console.error(e.message);
        }
    }

    useEffect(() => {
        fetchEvents();
    }, [eventsOffset]);

    useEffect(() => {

        fetchDetails();
        connectToSockets();

        utils.content.subscribe(layerID, ['appointments', 'appointment_status', 'leads', 'lead_status'], {
            onFetch: fetchDetails,
            onSetClient: (next_abstract, client) => {
                if(next_abstract.getID() === abstract.getID()) {
                    setLead(next_abstract.object);
                    setClient(client);
                }
            },
            onUpdate: abstract => {
                switch(abstract.type) {
                    case 'appointments':
                    case 'appointment_status':
                    fetchDetails();
                    break;

                    case 'leads':
                    setLead(lead => lead.id === abstract.getID() ? abstract.object : lead);
                    break;

                    case 'lead_status':
                    setLead(lead => {
                        if(lead.id === abstract.getID()) {
                            lead.status = abstract.object.status;
                        }
                        return lead;
                    });
                    break;
                }
            },
        });
        return () => {
            utils.content.unsubscribe(layerID);
            disconnectFromSockets();
        }
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            extension: {
                abstract: abstract,
                permissions: {
                    delete: ['leads.notes.actions.delete'],
                    edit: ['leads.notes.actions.edit'],
                    new: ['leads.notes.actions.new'],
                    view: ['leads.notes']
                },
                type: 'notes'
            },
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            {getHeaderBanner()}
            {getMessages()}
            {getTopFields()}
            {getUnstructuredContent()}
            {getBottomFields()}
            {getCalls()}
            {getSystemEvents()}
        </Layer>
    )
}

export const LeadDuplicates = ({ index, options, utils }) => {

    const layerID = 'lead_duplicates';
    const limit = 10;
    const offset = useRef(0);

    const [filters, setFilters] = useState({});
    const [groups, setGroups] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);
    const [selected, setSelected] = useState({});

    const onArchiveLead = () => {
        utils.alert.show({
            title: 'Archive Lead',
            message: 'Are you sure that you want to archive these leads? These leads will no longer show up within your available pool of leads',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onArchiveLeadConfrim();
                    return;
                }
            }
        })
    }

    const onArchiveLeadConfrim = async () => {
        try {

            setLoading(true);
            await Utils.sleep(0.25);

            await Request.post(utils, '/leads/', {
                ids: Object.keys(selected).reduce((array, id) => {
                    if(selected[id]) {
                        array.push(id);
                    }
                    return array;
                }, []),
                type: 'batch_archive_duplicates'
            });

            setLoading(false);
            setLayerState('close');
            utils.banner.show({
                title: 'Leads Archived',
                message: 'The leads that you selected have been archived. You can find these leads in the "Archived Leads" section of LifeWipe if you need to reverse your decision'
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue archiving these leads. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onCheckboxChange = (id, checked, evt) => {
        evt.stopPropagation();
        setSelected(ids => {
            return update(ids, {
                [id]: {
                    $set: checked
                }
            });
        });
    }

    const onLeadClick = async id => {
        return new Promise(async (resolve, reject) => {
            try {
                let lead = await Lead.get(utils, id);
                utils.layer.open({
                    abstract: Abstract.create({
                        object: lead,
                        type: 'leads'
                    }),
                    Component: LeadDetails,
                    id: `lead_details_${id}`,
                    permissions: ['leads.details']
                });
                resolve();
            } catch(e) {
                utils.alert.show({
                    title: 'Oops!',
                    message: `There was an issue retrieving the details for this lead. ${e.message || 'An unknown error occurred'}`
                });
                reject(e);
            }
        })
    }

    const onSelectAll = async () => {
        try {
            setLoading(true);
            let { selected } = await Request.get(utils, '/leads/', {
                type: 'duplicates',
                select_all: true
            });

            setLoading(false);
            setSelected(() => {
                return selected.reduce((object, id) => {
                    object[id] = true;
                    return object;
                }, {});
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue selecting all duplicate leads. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onShowLeadFilters = () => {
        utils.layer.open({
            id: 'lead_filters',
            Component: LeadFilters.bind(this, {
                value: filters,
                onChange: result => {
                    offset.current = 0;
                    setFilters(result);
                },
                
            })
        });
    }

    const getButtons = () => {

        // prepare list of buttons
        let buttons = [{
            color: 'light',
            key: 'filters',
            onClick: onShowLeadFilters,
            text: 'Filters'
        },{
            color: 'dark',
            key: 'select_all',
            onClick: onSelectAll,
            text: 'Select All'
        }];

        // provide options to deselect all and archive selected if at least one lead is selected
        if(Object.keys(selected).length > 0) {
            buttons.push({
                color: 'danger',
                key: 'archive',
                onClick: onArchiveLead,
                permissions: ['leads.duplicates.actions.edit'],
                text: 'Archive Selected',
            });
        }
        return buttons;
    }

    const fetchLeads = async () => {
        try {
            setLoading(true);
            let { groups, paging, selected: ids } = await Request.get(utils, '/leads/', {
                filters: filters,
                limit: limit,
                offset: offset.current,
                type: 'duplicates'
            });

            setLoading(false);
            setPaging(paging);
            setGroups(groups);
            setSelected(selected => {
                ids.forEach(id => {
                    if(typeof(selected[id]) !== 'boolean') {
                        selected[id] = true;
                    }
                });
                return selected;
            });

        } catch(e) {
            setLoading(false); 
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retreiving the list of duplicate leads. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchLeads();
    }, [filters]);

    useEffect(() => {
        utils.content.subscribe(layerID, ['leads', 'lead_status'], {
            onFetch: fetchLeads,
            onUpdate: abstract => {
                setGroups(groups => {
                    return groups.map(group => {
                        group.leads = group.leads.map(lead => {
                            if(lead.id === abstract.getID()) {
                                switch(abstract.type) {
                                    case 'leads':
                                    return abstract.object;

                                    case 'lead_status':
                                    lead.status = abstract.object.status;
                                    break;
                                }
                            }
                            return lead;
                        });
                        return group;
                    });
                });
            }
        });
        return () => {
            utils.content.unsubscribe(layerID);
        }
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Duplicate Leads'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading
        }}>
            <div style={{
                maxHeight: window.innerHeight / 2,
                overflowY: 'scroll'
            }}>
                {groups && groups.length === 0 && (
                    <div 
                    key={index}
                    style={{
                        ...Appearance.styles.unstyledPanel()
                    }}>
                        {Views.entry({
                            bottomBorder: false,
                            hideIcon: true,
                            subTitle: 'There were no duplicate leads were found using your search criteria',
                            title: 'No Leads Found'
                        })}
                    </div>
                )}
                {groups && groups.map((group, index) => {
                    return (
                        <div 
                        key={index}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            border: `2px solid ${Appearance.colors.red}`,
                            marginBottom: index !== groups.length - 1 ? 12 : 0
                        }}>
                            <div style={{
                                background: Appearance.colors.softGradient(Appearance.colors.red),
                                padding: '6px 12px 6px 12px'
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.title(),
                                    color: 'white'
                                }}>{`${group.leads.length - 1} ${group.leads.length - 1 === 1 ? 'Duplicate' : 'Duplicates'}`}</span>
                            </div>
                            {group.leads.map((lead, index) => {
                                return (
                                    <AsyncViewComponent
                                    badge={{
                                        color: group.max_events === lead.id ? Appearance.colors.primary() : (window.theme === 'dark' ? Appearance.colors.lightGrey : Appearance.colors.darkGrey),
                                        text: lead.events > 0 ? `${lead.events} Changes` : null
                                    }}
                                    bottomBorder={index !== group.length - 1}
                                    hideIcon={true}
                                    key={index}
                                    loading={loading === lead.id}
                                    onClick={onLeadClick.bind(this, lead.id)}
                                    rightContent={(
                                        <div style={{
                                            alignItems: 'center',
                                            display: 'flex',
                                            flexDirection: 'row'
                                        }}>
                                            <div style={{
                                                marginRight: 8,
                                                width: 100
                                            }}>
                                                {getLeadStatus(utils, lead, { editable: false })}
                                            </div>
                                            <Checkbox
                                            checked={selected[lead.id]}
                                            color={Appearance.colors.red}
                                            onChange={onCheckboxChange.bind(this, lead.id)} />
                                        </div>
                                    )}
                                    subTitle={lead.phone_number}
                                    title={lead.full_name}  />
                                )
                            })}
                        </div>
                    )
                })}
            </div>
            {paging && (
                <PageControl
                data={paging}
                limit={limit}
                offset={offset}
                onClick={next => {
                    offset.current = next;
                    fetchLeads();
                }} />
            )}
        </Layer>
    )
}

export const LeadFilters = ({ onChange, value = {} }, { index, options, utils }) => {

    const layerID = 'lead_filters';

    const [administrativeAreaLevels, setAdministrativeAreaLevels] = useState([]);
    const [assignmentUsers, setAssignmentUsers] = useState([]);
    const [enrollmentUsers, setEnrollmentUsers] = useState([]);
    const [layerState, setLayerState] = useState(false);
    const [leadCreditUsers, setLeadCreditUsers] = useState([]);
    const [leadSubTypes, setLeadSubTypes] = useState([]);
    const [leadTypes, setLeadTypes] = useState([]);
    const [localities, setLocalities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [homeownerStatusCodes, setHomeownerStatusCodes] = useState([]);
    const [maritalStatusCodes, setMaritalStatusCodes] = useState([]);
    const [marketingUsers, setMarketingUsers] = useState([]);
    const [occupationalStatusCodes, setOccupationalStatusCodes] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [surveys, setSurveys] = useState([]);
    const [searchProps, setSearchProps] = useState(value || {});
    const [statusCodes, setStatusCodes] = useState([]);
    const [tags, setTags] = useState([]);
    const [timezones, setTimezones] = useState([]);
    const [userCount, setUserCount] = useState(0);

    const onUpdateSearchProps = props => {
        setSearchProps(prev => ({
            ...prev,
            ...props
        }));
    }

    const getButtons = () => {
        return [{
            key: 'reset',
            text: 'Reset Filters',
            color: 'dark',
            onClick: () => {
                setLayerState('close');
                if(typeof(onChange) === 'function') {
                    onChange({});
                }
            }
        },{
            key: 'apply',
            text: 'Apply Filters',
            color: 'primary',
            onClick: () => {
                setLayerState('close');
                if(typeof(onChange) === 'function') {
                    onChange(searchProps);
                }
            }
        }];
    }

    const getFilterContent = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    height: 100,
                    justifyContent: 'center',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        width: 40,
                        height: 40
                    }}/>
                </div>
            )
        }

        let fields = [{
            key: 'customer',
            title: 'Customer',
            items: [{
                key: 'homeowner_status',
                required: false,
                title: 'Homeowner Status',
                component: 'multiple_list',
                value: searchProps.homeowner_status && homeownerStatusCodes.filter(val => searchProps.homeowner_status.includes(val.id)),
                items: homeownerStatusCodes,
                onChange: items => onUpdateSearchProps({ 'homeowner_status': items && items.map(item => item.id) })
            },{
                key: 'marital_status',
                required: false,
                title: 'Marital Status',
                component: 'multiple_list',
                value: searchProps.marital_status && maritalStatusCodes.filter(val => searchProps.marital_status.includes(val.id)),
                items: maritalStatusCodes,
                onChange: items => onUpdateSearchProps({ 'marital_status': items && items.map(item => item.id) })
            },{
                key: 'occupational_status',
                required: false,
                title: 'Occupational Status',
                component: 'multiple_list',
                value: searchProps.occupational_status && occupationalStatusCodes.filter(val => searchProps.occupational_status.includes(val.id)),
                items: occupationalStatusCodes,
                onChange: items => onUpdateSearchProps({ 'occupational_status': items && items.map(item => item.id) })
            }]
        },{
            key: 'location',
            title: 'Location',
            items: [{
                key: 'localities',
                required: false,
                title: 'City',
                component: 'multiple_list',
                value: searchProps.localities && localities.filter(val => searchProps.localities.includes(val.id)),
                items: localities,
                onChange: items => onUpdateSearchProps({ 'localities': items && items.map(item => item.id) })
            },{
                key: 'location_radius',
                required: false,
                title: 'Location Radius',
                component: 'location_radius_lookup',
                value: searchProps.location_radius,
                onChange: result => onUpdateSearchProps({ location_radius: result }),
                props: {
                    channel: 'leads'
                }
            },{
                key: 'administrative_area_levels',
                required: false,
                required: false,
                title: 'State',
                component: 'multiple_list',
                value: searchProps.administrative_area_levels && administrativeAreaLevels.filter(val => searchProps.administrative_area_levels.includes(val.id)),
                items: administrativeAreaLevels,
                onChange: items => onUpdateSearchProps({ 'administrative_area_levels': items && items.map(item => item.id) })
            }]
        },{
            key: 'details',
            title: 'Lead Details',
            items: [{
                key: 'lead_types',
                required: false,
                title: 'Lead Type',
                component: 'multiple_list',
                value: searchProps.lead_types && leadTypes.filter(val => searchProps.lead_types.includes(val.id)).map(entry => ({
                    ...entry,
                    title: entry.text
                })),
                onChange: items => onUpdateSearchProps({ 'lead_types': items && items.map(item => item.id) }),
                items: leadTypes.map(leadType => ({
                    ...leadType,
                    title: leadType.text
                }))
            },{
                key: 'lead_sub_types',
                required: false,
                title: 'Lead Sub-Type',
                component: 'multiple_list',
                value: searchProps.lead_sub_types && leadSubTypes.filter(val => searchProps.lead_sub_types.includes(val.id)).map(entry => ({
                    ...entry,
                    title: entry.text
                })),
                onChange: items => onUpdateSearchProps({ 'lead_sub_types': items && items.map(item => item.id) }),
                items: leadSubTypes.map(leadSubType => ({
                    ...leadSubType,
                    title: leadSubType.text
                }))
            },{
                key: 'priority',
                required: false,
                title: 'Priority',
                component: 'list',
                items: priorities,
                onChange: item => onUpdateSearchProps({ 'priority': item ? (item.id === 'yes' ? 1 : 0) : null }),
                value: searchProps.priority <= 1 ? (searchProps.priority ? 'Yes' : 'No') : null
            },{
                key: 'surveys',
                required: false,
                title: 'Survey',
                component: 'multiple_list',
                value: searchProps.surveys && surveys.filter(val => searchProps.surveys.includes(val.id)),
                items: surveys,
                onChange: items => onUpdateSearchProps({ 'surveys': items && items.map(item => item.id) })
            },{
                component: 'dual_date_picker',
                endDate: searchProps.sale_dates && searchProps.sale_dates.end_date,
                items: statusCodes,
                key: 'sale_date',
                onEndDateChange: date => {
                    onUpdateSearchProps({ 
                        sale_dates: {
                            ...searchProps.sale_dates,
                            end_date: date && date.format('YYYY-MM-DD')
                        }
                    });
                },
                onStartDateChange: date => {
                    onUpdateSearchProps({ 
                        sale_dates: {
                            ...searchProps.sale_dates,
                            start_date: date && date.format('YYYY-MM-DD')
                        }
                    });
                },
                props: { 
                    deselect: true,
                    style: {
                        flexGrow: 1
                    }
                },
                required: false,
                startDate: searchProps.sale_dates && searchProps.sale_dates.start_date,
                title: 'Sale Date'
            },{
                key: 'status_codes',
                required: false,
                title: 'Status',
                component: 'multiple_list',
                value: searchProps.status_codes && statusCodes.filter(val => searchProps.status_codes.includes(val.id)),
                items: statusCodes,
                onChange: items => onUpdateSearchProps({ 'status_codes': items && items.map(item => item.id) })
            },{
                key: 'tags',
                required: false,
                title: 'Tags',
                component: 'multiple_list',
                value: searchProps.tags && tags.filter(val => searchProps.tags.includes(val.id)),
                items: tags,
                onChange: items => {
                    onUpdateSearchProps({ 'tags': items && items.map(item => item.id) })
                }
            },{
                key: 'timezone',
                required: false,
                title: 'Timezone',
                component: 'timezone_lookup',
                value: searchProps.timezone,
                onChange: text => onUpdateSearchProps({ timezone: text })
            },{
                key: 'unread_sms',
                required: false,
                title: 'Unread Text Messages',
                component: 'bool_list',
                value: searchProps.unread_sms,
                onChange: item => {
                    onUpdateSearchProps({ 'unread_sms': typeof(item) === 'boolean' ? item : null })
                },
            }]
        },{
            key: 'users',
            title: 'Users',
            visible: userCount > 0,
            items: [{
                key: 'assignment_users',
                required: false,
                title: 'Assigned To',
                component: 'multiple_list',
                value: searchProps.assignment_users && assignmentUsers.filter(val => searchProps.assignment_users.includes(val.id)),
                items: assignmentUsers,
                onChange: items => onUpdateSearchProps({ 'assignment_users': items && items.map(item => item.id) })
            },{
                key: 'lead_credit_users',
                required: false,
                title: 'Lead Credit',
                component: 'multiple_list',
                value: searchProps.lead_credit_users && leadCreditUsers.filter(val => searchProps.lead_credit_users.includes(val.id)),
                items: leadCreditUsers,
                onChange: items => onUpdateSearchProps({ 'lead_credit_users': items && items.map(item => item.id) })
            },{
                key: 'setter_users',
                required: false,
                title: 'Setter',
                component: 'multiple_list',
                value: searchProps.setter_users &&  marketingUsers.filter(val => searchProps.setter_users.includes(val.id)),
                items: marketingUsers,
                onChange: items => onUpdateSearchProps({ 'setter_users': items && items.map(item => item.id) })
            },{
                key: 'enrollment_users',
                required: false,
                title: 'Survey Credit',
                component: 'multiple_list',
                value: searchProps.enrollment_users && enrollmentUsers.filter(val => searchProps.enrollment_users.includes(val.id)),
                items: enrollmentUsers,
                onChange: items => onUpdateSearchProps({ 'enrollment_users': items && items.map(item => item.id) })
            }]
        }].map(section => {
            section.items = section.items.filter(item => item.items ? item.items.length > 0 : true);
            return section;
        });

        return (
            <AltFieldMapper
            utils={utils}
            fields={fields} />
        )
    }

    const fetchFilterTargets = async () => {

        try {
            await Utils.sleep(0.5);
            let {
                administrative_area_levels, enrollment_users, homeowner_status_codes, lead_sub_types, lead_types, localities, marital_status_codes, occupational_status_codes, surveys, tags, users
            } = await Request.get(utils, '/clients/', {
                type: 'filter_targets'
            })

            setAdministrativeAreaLevels(administrative_area_levels.filter(target => {
                return target !== null;
            }).map(target => ({
                id: target,
                title: target
            })));
            setAssignmentUsers(users.map(target => ({
                id: target.user_id,
                title: target.full_name
            })));
            setEnrollmentUsers(enrollment_users.map(target => ({
                id: target.user_id,
                title: target.full_name
            })));
            setHomeownerStatusCodes(homeowner_status_codes.map(target => ({
                ...target,
                id: target.code,
                title: target.text
            })));
            setLeadCreditUsers(users.map(target => ({
                id: target.user_id,
                title: target.full_name
            })));
            setLeadSubTypes(lead_sub_types);
            setLeadTypes(lead_types);
            setLocalities(localities.filter(target => {
                return target !== null;
            }).map(target => ({
                id: target,
                title: target
            })));
            setMaritalStatusCodes(marital_status_codes.map(target => ({
                id: target.code,
                title: target.text
            })));
            setMarketingUsers(users.filter(user => {
                return user.level === User.levels.get().setter;
            }).map(target => ({
                id: target.user_id,
                title: target.full_name
            })));
            setOccupationalStatusCodes(occupational_status_codes.map(target => ({
                id: target.code,
                title: target.text
            })));
            setPriorities([{
                id: 'yes',
                title: 'Yes'
            },{
                id: 'no',
                title: 'No'
            }]);
            setSurveys(surveys.map(target => ({
                ...target,
                title: target.name
            })));
            setStatusCodes(utils.client.status_codes.get().filter(entry => {
                return value && value.exclude_status_codes && value.exclude_status_codes.includes(entry.status) ? false : true;
            }).map(entry => ({
                id: entry.code,
                title: entry.text
            })));
            setTags(tags.map(tag => ({
                ...tag,
                title: tag.text
            })));
            setUserCount(users.length);
            setLoading(false);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the filter options. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const setupValues = () => {

        let next = { ...value };
        if(value && value.assignments) {
            if(!next.assignment_users) {
                next.assignment_users = [];
            }
            next.assignment_users.push(utils.user.get().user_id);

            if(!next.setter_users) {
                next.setter_users = [];
            }
            next.setter_users.push(utils.user.get().user_id);
        }
        setSearchProps(next);
    }

    useEffect(() => {
        setupValues();
        fetchFilterTargets();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Lead Filters'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            sizing: 'medium'
        }}>
            {getFilterContent()}
        </Layer>
    )
}

export const LeadImportHistory = ({ index, options, utils }) => {

    const panelID = 'lead_import_history';
    const limit = 10;
    const offset = useRef(0);

    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(null);
    const [tasks, setTasks] = useState([]);

    const onClientChange = () => {
        offset.current = 0;
        setLoading(true);
        setPaging(null);
        fetchTasks();
    }

    const onNewLeadImportClick = () => {
        utils.layer.open({
            Component: ImportLeads,
            id: 'import_leads',
            title: 'Import Leads'
        });
    }

    const onTaskClick = task => {
        utils.layer.open({
            Component: LeadImportTaskDetails.bind(this, { task }),
            id: `lead_import_task_details_${task.id}`,
        });
    }

    const getButtons = () => {
        return [{
            key: 'new',
            onClick: onNewLeadImportClick,
            title: 'Import New Leads',
            style: 'default'
        }];
    }

    const getContent = () => {
        if(loading === true) {
            return null;
        }
        if(tasks.length === 0) {
            return (
                Views.entry({
                    bottomBorder: false,
                    icon: {
                        path: 'images/lead_import_history_icon_clear.png',
                        imageStyle: {
                            backgroundColor: Appearance.colors.grey()
                        }
                    },
                    subTitle: 'No lead import history entries were found for your client workspace.',
                    title: 'No History Found'
                })
            )
        }
        return tasks.map((task, index) => {
            return (
                Views.entry({
                    bottomBorder: index !== tasks.length - 1,
                    icon: {
                        path: 'images/lead_import_history_icon_clear.png',
                        imageStyle: {
                            backgroundColor: task.status.color
                        }
                    },
                    key: index,
                    onClick: onTaskClick.bind(this, task),
                    rightContent: (
                        <AltBadge content={task.status} />
                    ),
                    subTitle: Utils.formatDate(task.date),
                    title: task.document && task.document.name || 'Document name not available'
                })
            )
        });
    }

    const fetchTasks = async () => {
        try {
            let { paging, tasks } = await Request.get(utils, '/leads/', {
                limit: limit,
                offset: offset.current,
                type: 'import_history'
            });

            setLoading(false);
            setPaging(paging);
            setTasks(tasks.map(task=> ({
                ...task,
                date: moment.utc(task.date).local()
            })))

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the lead import history list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {

        fetchTasks();
        utils.events.on(panelID, 'client_change', onClientChange);
        utils.content.subscribe(panelID, ['lead_imports'], {
            fetch: () => {
                console.log('fetching')
                fetchTasks();
            }
        });

        return () => {
            utils.events.off(panelID, 'client_change', onClientChange);
            utils.content.unsubscribe(panelID, ['lead_imports']);
        }

    }, []);

    return (
        <Panel
        index={index}
        title={'Lead Import History'}
        utils={utils}
        options={{
            ...options,
            buttons: getButtons(),
            loading: loading === true,
            paging: {
                data: paging,
                limit: limit,
                loading: loading === 'paging',
                offset: offset.current,
                onClick: next => {
                    offset.current = next;
                    setLoading('paging');
                    fetchTasks();
                }
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel()
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const LeadTableEntry = ({ category, isLastItem = false, lead, loading, onLeadClick, onSortingChange, props, sorting, utils }) => {

    const getContent = () => {
        
        let target = lead || {};
        let fields = [{
            key: 'full_name',
            title: 'Name',
            value: target.full_name || 'Customer name not available',
            prepend: lead && (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row'
                }}>
                    {(lead.priority || lead.unread_sms) &&  (
                        <div style={{
                            backgroundColor: lead.unread_sms ? Appearance.colors.blue : Appearance.colors.primary(),
                            borderRadius: 5,
                            height: 10,
                            marginRight: 8,
                            minHeight: 10,
                            minWidth: 10,
                            overflow: 'hidden',
                            width: 10
                        }} />
                    )}
                    {lead.lead_type && lead.lead_type.icon && (
                        <img
                        src={lead.lead_type.icon.url}
                        style={{
                            height: 20,
                            marginRight: 8,
                            objectFit: 'contain',
                            width: 20
                        }} />
                    )}
                </div>
            )
        },{
            key: 'timezone',
            title: 'Timezone',
            value: target.timezone
        },{
            key: 'phone_number',
            title: 'Phone Number',
            sortable: false,
            value: target.phone_number ? Utils.formatPhoneNumber(target.phone_number) : null
        },{
            key: 'lead_type',
            title: 'Lead Type',
            value: target.lead_type && target.lead_type.text
        },{
            key: 'last_updated',
            title: 'Last Updated',
            value: target.extended_details && target.extended_details.sys_evt_date ? Utils.formatDate(target.extended_details.sys_evt_date) : null,
            visible: props && props.follow_ups === true ? true : false
        },{
            key: 'created',
            title: 'Created',
            value: Utils.formatDate(target.created)
        },{
            key: 'adjusted_date',
            title: 'Resorted',
            value: target.adjusted_date && Utils.formatDate(target.adjusted_date),
            visible: category === 'new_leads'
        },{
            key: 'status',
            title: 'Status',
            sortable: false,
            value: getStatusBadge(utils, target)
        }];

        // create table headers with custom sorting options
        if(!lead) {
            return (
                <TableListHeader
                fields={fields}
                onChange={onSortingChange}
                value={sorting} />
            )
        }

        // loop through results and create table row
        return (
            <tr
            className={`view-entry ${window.theme}`}
            style={{
                borderBottom: isLastItem === false && `1px solid ${Appearance.colors.divider()}`,
                position: 'relative',
            }}>
                {fields.filter(field => {
                    return field.visible !== false;
                }).map((field, index) => {
                    return (
                        <td
                        key={index}
                        className={'px-3 py-2 flexible-table-column'}
                        onClick={onLeadClick.bind(this, lead)}
                        style={{
                            position: 'relative'
                        }}>
                            <div style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'row'
                            }}>
                                {index === 0 && loading === true && (
                                    <img 
                                    className={'rotate'} 
                                    src={'images/green-radial-loader.png'}
                                    style={{
                                        height: 20,
                                        marginRight: 8,
                                        width: 20
                                    }} />
                                ) || field.prepend}
                                <span style={{
                                    ...Appearance.textStyles.subTitle(),
                                    width: '100%'
                                }}>{field.value}</span>
                            </div>
                            
                        </td>
                    )
                })}
            </tr>
        )
    }

    const getStatusBadge = (utils, target) => {

        // determine if a transfer related status badge is required
        if(props && props.transfers) {
            if(!target.transfer) {
                return null;
            }
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    maxWidth: 95,
                    textAlign: 'center',
                    border: `2px solid ${target.transfer.status.color}`,
                    background: Appearance.colors.softGradient(target.transfer.status.color),
                    borderRadius: 5,
                    overflow: 'hidden'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        color: 'white',
                        fontWeight: '600',
                        width: '100%'
                    }}>{target.transfer.status.text}</span>
                </div>
            )
        }

        // determine if an archive badge is required
        if(props && props.show_archived_only) {
            return (
                <div style={{
                    alignItems: 'center',
                    background: Appearance.colors.softGradient(Appearance.colors.red),
                    border: `2px solid ${Appearance.colors.red}`,
                    borderRadius: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    justifyContent: 'center',
                    maxWidth: 95,
                    overflow: 'hidden',
                    textAlign: 'center',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        color: 'white',
                        fontWeight: '600',
                        width: '100%'
                    }}>{'Archived'}</span>
                </div>
            )
        }

        // fallback to using a standard lead status badge
        return getLeadStatus(utils, target);
    }

    return getContent();
}

export const LeadsLayer = ({ filters, id, sorting, title }, { index, options, utils }) => {

    const limit = 10;
    const offset = useRef(0);
    const sortingRef = useRef(sorting || { 
        sort_key: 'date', 
        sort_type: Content.sorting.type.descending 
    });

    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState('init');
    const [paging, setPaging] = useState(null);

    const onLeadClick = async target => {
        try {

            // prevent moving forward if feature is disabled for current user
            if(utils.user.permissions.get('leads.details') === false) {
                return utils.user.permissions.reject();
            }
            
            // fetch details for lead
            // only partial details are included through the filter based search for performance purposes
            // port over transfer information if applicable
            setLoading(target.id);
            let lead = await Lead.get(utils, target.id);

            // set transfer object 
            setLoading(false);
            lead.transfer = target.transfer;

            // open details layer for lead
            utils.layer.open({
                abstract: Abstract.create({
                    object: lead,
                    type: 'leads',
                }),
                Component: LeadDetails,
                id: `lead_details_${lead.id}`,
                permissions: ['leads.details']
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this lead. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getContent = () => {
        if(loading === 'init') {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 15
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 50,
                        width: 50
                    }}/>
                </div>
            )
        }
        if(leads.length === 0) {
            return (
                Views.entry({
                    bottomBorder: false,
                    hideIcon: true,
                    subTitle: 'No leads matching your search criteria were found.',
                    title: 'No Leads Found'
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <thead style={{
                    width: '100%'
                }}>
                    <LeadTableEntry
                    key={index}
                    onSortingChange={props => {
                        sortingRef.current = props;
                        setLoading(true);
                        fetchLeads();
                    }}
                    sorting={sortingRef.current}
                    utils={utils} />
                </thead>
                <tbody style={{
                    width: '100%'
                }}>
                    {leads.map((lead, index, leads) => {
                        return (
                            <LeadTableEntry
                            isLastItem={index === leads.length - 1}
                            key={index}
                            lead={lead}
                            loading={loading === lead.id} 
                            onLeadClick={onLeadClick}
                            utils={utils} />
                        )
                    })}
                </tbody>
            </table>
        )
    }
    
    const fetchLeads = async () => {
        try {

            let { leads, paging } = await Request.post(utils, '/leads/', {
                filter_props: filters,
                limit: limit,
                offset: offset.current,
                type: 'advanced_search',
                ...sortingRef.current
            });
           
            setLoading(false);
            setPaging(paging);
            setLeads(leads);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the list of leads. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchLeads();
    }, []);

    return (
        <Layer
        id={id}
        index={index}
        title={title}
        utils={utils}
        options={{
            ...options,
            loading: loading === true,
            sizing: 'extra_large'
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {getContent()}
                {paging && (
                    <PageControl
                    data={paging}
                    limit={limit}
                    loading={loading === 'paging'}
                    offset={offset}
                    onClick={next => {
                        offset.current = next;
                        setLoading('paging');
                        fetchLeads();
                    }}/>
                )}
            </div>
        </Layer>
    )
}

export const LeadImportTaskDetails = ({ task }, { index, options, utils }) => {

    const layerID = `lead_import_task_details_${task.id}`;

    const onDocumentClick = () => {
        utils.sheet.show({
            items: [{
                key: 'download',
                title: 'Download',
                style: 'default'
            }]
        }, key => {
            if(key === 'download') {
                window.open(task.document.url);
                return;
            }
        });
    }

    const onViewLeads = () => {
        let id = `leads_layer_tag_${task.tag.id}`;
        utils.layer.open({
            Component: LeadsLayer.bind(this, {
                filters: {
                    tags: [task.tag.id]
                },
                id: id,
                sorting: {
                    sort_key: 'full_name',
                    sort_type: Content.sorting.type.ascending
                },
                title: `Leads for "${task.tag.text}" Tag`
            }),
            id: id
        });
    }


    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'leads',
            onClick: onViewLeads,
            text: 'View Leads'
        }];
    }

    const getFields = () => {
        return [{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'date',
                title: 'Date',
                value: Utils.formatDate(task.date)
            },{
                key: 'id',
                title: 'ID',
                value: task.id
            },{
                color: task.status.color,
                key: 'status',
                title: 'Status',
                value: task.status.text
            }]
        },{
            key: 'stats',
            lastItem: true,
            title: 'Statistics',
            items: [{
                key: 'tag',
                title: 'Client Tag',
                value: task.tag.text
            },{
                key: 'document',
                onClick: task.document ? onDocumentClick : null,
                title: 'Document',
                value: task.document && task.document.name || 'Not Available'
            },{
                key: 'elapsed',
                title: 'Duration',
                value: task.data && Utils.parseDuration(task.data.elapsed)
            },{
                key: 'error_message',
                title: 'Error Message',
                value: task.data?.message,
                visible: task.data?.message ? true : false
            },{
                key: 'skipped',
                title: 'Skipped Leads',
                value: task.skipped || '0'
            }]
        }];
    }

    const getTitle = () => {
        return task.document ? `Lead Import for ${task.document.name}` : `Lead Import ${task.id} Task Details`;
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={getTitle()}
        utils={utils}
        options={{
            ...options,
            sizing: 'medium'
        }}>
            <FieldMapper
            sections={getFields()} 
            utils={utils} />
        </Layer>
    )
}

export const LeadSurveys = ({ abstract, index, options, utils }) => {

    const layerID = `lead_surveys_${abstract.getID()}`;
    const [loading, setLoading] = useState(true);
    const [layerState, setLayerState] = useState(null);
    const [lead, setLead] = useState(abstract.object);
    const [surveys, setSurveys] = useState([]);

    const getBadge = survey => {
        switch(survey.status) {
            case Lead.Survey.status.in_progress:
            return {
                text: 'In Progress',
                color: Appearance.colors.primary()
            }

            case Lead.Survey.status.completed:
            return {
                text: 'Completed',
                color: Appearance.colors.green
            }

            case Lead.Survey.status.funded:
            return {
                text: 'Funded',
                color: Appearance.colors.secondary()
            }

            case Lead.Survey.status.revoked:
            return {
                text: 'Revoked',
                color: Appearance.colors.red
            }
        }
    }
    const onEnrollSurvey = () => {
        utils.layer.open({
            id: `enroll_lead_survey_${abstract.getID()}`,
            abstract: abstract,
            Component: EnrollLeadSurvey
        })
    }

    const fetchSurveys = async () => {
        try {
            let { surveys } = await Request.get(utils, '/leads/', {
                type: 'surveys',
                id: abstract.getID()
            })
            setLoading(false);
            setSurveys(surveys.map(survey => Lead.Survey.create(survey)));

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the surveys for this lead. ${e.message || 'An unknown error occurred'}`,
                onClick: () => setLayerState('close')
            })
        }
    }

    const onSurveyClick = log => {
        utils.sheet.show({
            items: [{
                key: 'link',
                title: 'Link',
                style: 'default'
            },{
                key: 'leads',
                title: 'Leads',
                style: 'default'
            },{
                key: 'notes',
                title: 'Notes',
                style: 'default'
            },{
                key: 'analytics',
                title: 'Analytics',
                style: 'default'
            }]
        }, key => {

        })
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'enroll',
            onClick: onEnrollSurvey,
            text: 'Enroll in Survey'
        }];
    }

    useEffect(() => {

        fetchSurveys();
        utils.content.subscribe(layerID, ['leads', 'lead_surveys', 'lead_status'], {
            onFetch: fetchSurveys,
            onUpdate: abstract => {
                switch(abstract.type) {
                    case 'leads':
                    setLead(lead => {
                        return lead.id === abstract.getID() ? abstract.object : lead
                    });
                    break;

                    case 'lead_surveys':
                    setSurveys(surveys => {
                        return surveys.map(survey => {
                            return survey.id === abstract.getID() ? abstract.object : survey
                        });
                    });
                    break;

                    case 'lead_status':
                    setLead(lead => {
                        if(lead.id === abstract.getID()) {
                            lead.status = abstract.object.status;
                        }
                        return lead;
                    });
                    break;
                }
            }
        });
        return () => {
            utils.content.unsubscribe(layerID);
        }
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            {surveys.length > 0
                ?
                <div style={{
                    borderRadius: 10,
                    border: `1px solid ${Appearance.colors.divider()}`
                }}>
                {surveys.map((survey, index) => {
                    return (
                        Views.entry({
                            key: index,
                            title: survey.survey.name,
                            subTitle: survey.survey.description,
                            badge: getBadge(survey),
                            hideIcon: true,
                            singleItem: surveys.length === 1,
                            firstItem: index === 0,
                            lastItem: index === surveys.length - 1,
                            bottomBorder: true,
                            onClick: onSurveyClick.bind(this, survey)
                        })
                    )
                })}
                </div>
                :
                loading
                    ?
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: 75
                    }}>
                        <LottieView
                        loop={true}
                        autoPlay={true}
                        source={window.theme === 'dark' ? 'files/lottie/dots-white.json' : 'files/lottie/dots-grey.json'}
                        style={{
                            width: 50,
                            height: 50
                        }}/>
                    </div>
                    :
                    <div style={{
                        borderRadius: 10,
                        border: `1px solid ${Appearance.colors.divider()}`
                    }}>
                        {Views.entry({
                            title: 'No Surveys Found',
                            subTitle: 'No surveys were found in the system',
                            bottomBorder: false,
                            hideIcon: true
                        })}
                    </div>
            }
        </Layer>
    )
}

export const LeadTextMessages = ({ abstract, style, utils }) => {

    const scrollContainer = useRef(null);

    const [account, setAccount] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [messages, setMessages] = useState([]);
    const [messageText, setMessageText] = useState(null);

    const onNewMessage = data => {
        try {
            if(data.lead_id === abstract.getID()) {
                setMessages(messages => {
                    return update(messages, {
                        $push: [Lead.Message.create({
                            ...data,
                            show_name: messages.length === 0 || messages[messages.length - 1].direction !== data.direction
                        })]
                    });
                });
            }
        } catch(e) {
            console.error(e.message);
        }
    }

    const onSendMessage = async props => {
        try {

            // prevent moving forward if a message was not provided
            if(!messageText) {
                throw new Error('Please type a message before moving on.');
            }

            // prevent moving forward if a credits method was not selected
            if(!account) {
                throw new Error('Please select your preferred credits account before moving on.');
            }

            if(account.balance <= 0) {
                throw new Error('The selected credits account has an insufficient balance.');
            }

            // set loading flag and submit request to server
            setLoading('send');
            let { balance, failed, messages } = await Request.post(utils, '/leads/', {
                lead_ids: [abstract.getID()],
                message: messageText,
                method: {
                    client_id: account.client_id,
                    user_id: account.user_id
                },
                type: 'notify_sms',
                ...props
            })

            // end loading and check if the request failed
            setLoading(false);
            if(failed > 0) {
                throw new Error('Please verify that the phone number is valid and complete.');
            }

            // add message to list of messages
            setMessages(targets => {

                // loop through message results and append messages that are not already in the local state
                messages.forEach(message => {
                    let match = targets.find(m => m.id === message.id);
                    if(!match) {
                        targets.push(Lead.Message.create(message));
                    }
                });

                // sort messages by date ascending and determine if a name should be shown for the message entry
                return targets.sort((a,b) => {
                    return a.date > b.date;
                }).map((message, index) => {
                    message.show_name = index === 0 || targets[index - 1].direction !== message.direction;
                    return message;
                });
            });

            // clear message text field
            setMessageText(null);

            // update balance for selected credits method
            setAccounts(accounts => {
                return accounts.map(act => {
                    if(act.id === account.id) {
                        act.balance = balance;
                    }
                    return act;
                });
            });

            // show confirmation alert
            utils.alert.show({
                title: 'All Done!',
                message: 'Your message was sent successfully but this feature is still in active development. There may be a delay when displaying incoming text messages from the customer.'
            });

        } catch(e) {

            // end loading and check for do not call list conflicts
            setLoading(false);
            if(e.code === 409) {
                utils.alert.show({
                    title: 'Just a Second',
                    message: `There was an issue sending your message. ${e.message || 'An unknown error occurred'}`,
                    buttons: [{
                        key: 'confirm',
                        title: 'Continue',
                        style: 'destructive'
                    },{
                        key: 'cancel',
                        title: 'Cancel',
                        style: 'default'
                    }],
                    onClick: key => {
                        if(key === 'confirm') {
                            onSendMessage({ do_not_call_override: true });
                            return;
                        }
                    }
                })
                return;
            }

            // fallback to standard request error
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue sending your message. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSettingsClick = evt => {
        utils.sheet.show({
            items: accounts.map(act => ({
                key: act.id,
                title: act.client_id ? 'Use Client Credits' : 'Use Personal Credits'
            })).concat([{
                key: 'about',
                title: 'About Text Messaging'
            },{
                key: 'manage',
                title: 'Manage Credits'
            }]),
            position: 'bottom',
            target: evt.target
        }, key => {

            // determine if a credits account was selected and save selected account for future use
            if(key.includes('client') || key.includes('user')) {
                Cookies.set('leads.sms.last_used_credits_account_id', key);
                setAccount(accounts.find(act => act.id === key));
                return;
            }

            if(key === 'about') {
                utils.alert.show({
                    title: 'About Text Messaging',
                    message: 'LifeWipe provides one-way and two-way mass text messaging capabilities to help you communicate with your leads. Lead text messaging supports the same dynamic keywords that you use with your lead scripts so you can tailor your content to each individual lead.'
                })
                return;
            }

            if(key === 'manage') {
                let user = utils.user.get();
                utils.layer.open({
                    abstract: Abstract.create({
                        object: user,
                        type: 'users',
                    }),
                    Component: PaymentsOverview,
                    id: `payments_overview_${user.user_id}`
                });
                return;
            }
        });
    }

    const onUpdateScrollOffset = () => {
        if(scrollContainer.current) {
            scrollContainer.current.scrollTo(0, scrollContainer.current.scrollHeight);
        }
    }

    const getCreditsAccount = () => {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column'
            }}>
                <span style={{
                    ...Appearance.textStyles.title()
                }}>{account ? (account.client_id ? 'Client Credits' : 'Personal Credits') : 'No Credits Account Selected'}</span>
                <span style={{
                    ...Appearance.textStyles.subTitle()
                }}>{`Balance: `}
                    <span style={{
                        color: account && account.balance > 0 ? Appearance.colors.green : Appearance.colors.red
                    }}>{Utils.toCurrency(account && account.balance || 0)}</span>
                </span>
            </div>
        )
    }

    const connectToSockets = async () => {
        try {
            await utils.sockets.on('leads', 'on_new_sms_message', onNewMessage);
        } catch(e) {
            console.error(e.message);
        }
    }

    const disconnectFromSockets = async () => {
        try {
            await utils.sockets.off('leads', 'on_new_sms_message', onNewMessage);
        } catch(e) {
            console.error(e.message);
        }
    }

    const fetchCreditAccounts = async () => {
        try {

            // fetch last usedd credits account
            let accountID = Cookies.get('leads.sms.last_used_credits_account_id');

            // send request to server
            setLoading(true);
            let { credits } = await Request.get(utils, '/payments/', {
                type: 'credits'
            });

            // update local state with credits account options and set selected account as the client account
            setLoading(false);
            setAccounts(credits);
            setAccount(() => {
                let match = credits.find(acct => accountID && acct.id === accountID);
                if(match) {
                    return match;
                }
                return credits.find(acct => acct.client_id ? true : false);
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving your credit account options. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const fetchMessages = async () => {
        try {
            let { messages } = await Request.get(utils, '/leads/', {
                id: abstract.getID(),
                mark_as_read: true,
                type: 'sms_thread'
            });

            // update local state with messages
            setMessages(messages.map(message => Lead.Message.create(message)));

            // update unread sms flag for target and notify subscribers of data change if applicable
            if(abstract.object.unread_sms === true) {
                abstract.object.unread_sms = false;
                utils.content.update(abstract);
            } 

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the messages list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        onUpdateScrollOffset();
    }, [messages]);

    useEffect(() => {
        connectToSockets();
        fetchCreditAccounts();
        fetchMessages();
        return disconnectFromSockets;
    }, []);

    return messages.length > 0 && (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            ...style
        }}>
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                padding: 12,
                width: '100%'
            }}>
                {getCreditsAccount()}
                <img
                className={'text-button'}
                onClick={onSettingsClick}
                src={'images/settings-button-grey.png'}
                style={{
                    height: 30,
                    width: 30
                }} />
            </div>
            <div 
            className={'custom-scrollbars'}
            ref={scrollContainer}
            style={{
                borderTop: `1px solid ${Appearance.colors.divider()}`,
                maxHeight: window.innerHeight / 3,
                overflowY: 'scroll',
                padding: 12,
                position: 'relative',
                width: '100%'
            }}>
                {messages.map((message, index) => {
                    return (
                        <MessageComponent
                        key={index}
                        message={message}
                        utils={utils}
                        style={{
                            marginBottom: index === messages.length - 1 ? 0 : 8
                        }} />
                    )
                })}
            </div>
            <div style={{
                alignItems: 'center',
                borderTop: `1px solid ${Appearance.colors.divider()}`,
                display: 'flex',
                flexDirection: 'row',
                padding: 12,
                position: 'relative',
                width: '100%'
            }}>
                {loading === 'send' && (
                    <div style={{
                        height: 2,
                        left: 0,
                        overflow: 'hidden',
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        width: '100%'
                    }}>
                        <ProgressBar 
                        barColor={Appearance.colors.primary()} 
                        trackColor={Appearance.colors.divider()}/>
                    </div>
                )}
                <TextField
                containerStyle={{
                    marginRight: 8
                }}
                onChange={setMessageText}
                placeholder={'Type your message here...'}
                value={messageText} />
                <Button
                color={'primary'}
                label={'Send'}
                loading={loading === 'send'}
                onClick={() => onSendMessage()}
                style={{
                    height: 35
                }}
                type={'large'} />
            </div>
        </div>
    )
}

export const LeadTextMessageManager = ({ onClose, leads, targets }, { index, options, utils }) => {

    const layerID = 'lead_text_message_manager';

    const [cost, setCost] = useState(0);
    const [keywords, setKeywords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [layerState, setLayerState] = useState(null);
    const [message, setMessage] = useState('');
    const [creditMethod, setCreditMethod] = useState(null);

    const onKeywordClick = keyword => {
        setMessage(text => text?.length === 0 ? `{{${keyword.id}}}` : `${text} {{${keyword.id}}}`);
    }

    const onNewKeywordClick = keyword => {

    }

    const onSendMessages = () => {
        if(!message) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'Please type a message to send to your Leads before moving on'
            });
            return;
        }
        if(!creditMethod) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'Please select your preferred credits before moving on'
            });
            return;
        }

        let items = targets || leads;
        utils.alert.show({
            title: 'Confirm',
            message: `You have requested that we send ${items.length} text ${items.length === 1 ? 'message' : 'messages'}. The service cost of ${getTotalCost()} will be deducted from your ${creditMethod.client_id ? `client's ` : ''}credits.`,
            buttons: [{
                key: 'confirm',
                title: 'Confirm and Send',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSendMessagesConfirm();
                    return;
                }
            }
        })
    }

    const onSendMessagesConfirm = async props => {
        try {
            setLoading('send');
            await Utils.sleep(0.25);

            let { balance, cost, failed, sent, total } = await Request.post(utils, '/leads/', {
                type: 'notify_sms',
                message: message,
                lead_ids: leads && leads.map(lead => lead.id),
                targets: targets,
                method: {
                    user_id: creditMethod.user_id,
                    client_id: creditMethod.client_id
                },
                ...props
            })

            setLoading(false);
            utils.banner.show({
                title: 'Messages Sent',
                message: `We have successfully sent ${sent} out of ${total} messages to your ${total === 1 ? 'lead' : 'leads'}.${failed > 0 ? `There was an issue sending ${failed} ${failed === 1 ? 'message':'message'} due to invalid or incomplete phone numbers.` : ''} The final cost of ${Utils.toCurrency(cost)} has been deducted from your credits and you have a remaining balance of ${Utils.toCurrency(balance)}.`,
                onShow: () => {
                    setLayerState('close');
                    if(typeof(onClose) === 'function') {
                        onClose();
                    }
                }
            });

        } catch(e) {
            // check for do not call list conflicts
            setLoading(false);
            if(e.code === 409) {
                utils.alert.show({
                    title: 'Just a Second',
                    message: `There was an issue sending your messages. ${e.message || 'An unknown error occurred'}`,
                    buttons: [{
                        key: 'confirm',
                        title: 'Continue',
                        style: 'destructive'
                    },{
                        key: 'cancel',
                        title: 'Cancel',
                        style: 'default'
                    }],
                    onClick: key => {
                        if(key === 'confirm') {
                            onSendMessagesConfirm({ do_not_call_override: true });
                            return;
                        }
                    }
                })
                return;
            }
            // fallback to standard request error
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue sending your messages. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const getButtons = () => {
        let items = targets || leads;
        return [{
            key: 'send',
            text: `Send ${items.length === 1 ? 'Message':'Messages'}`,
            color: 'primary',
            loading: loading === 'send',
            onClick: onSendMessages
        }];
    }

    const getDynamicKeywords = () => {
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                marginBottom: 12,
                padding: 8
            }}>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    flexGrow: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    width: '100%'
                }}>
                    {keywords.map((keyword, index) => {
                        return (
                            <AltBadge
                            key={index}
                            onClick={onKeywordClick.bind(this, keyword)}
                            style={{
                                marginTop: 4,
                                marginBottom: 4,
                                marginLeft: 4,
                                marginRight: 4,
                                padding: '8px 15px 8px 15px'
                            }}
                            content={{
                                text: keyword.title,
                                color: Appearance.colors.primary()
                            }} />
                        )
                    })}
                </div>
                <img
                className={'text-button'}
                src={'images/plus-button-green-small.png'}
                onClick={onNewKeywordClick}
                style={{
                    height: 25,
                    marginLeft: 8,
                    marginRight: 6,
                    marginTop: 5,
                    width: 25
                }} />
            </div>
        )
    }

    const getFields = () => {
        let items = targets || leads;
        return [{
            key: 'overview',
            lastItem: false,
            title: 'Overview',
            items: [{
                key: 'count',
                title: 'Queued to Send',
                value: `${items.length} ${items.length === 1 ? 'message':'messages'}`
            },{
                key: 'total',
                title: 'Estimated Total Cost',
                value: getTotalCost()
            }]
        }]
    }

    const getTitle = () => {
        let items = targets || leads;
        return `Send Lead Text ${items.length === 1 ? 'Message' : 'Messages'}`
    }

    const getTotalCost = () => {
        if(cost === 0) {
            return 'Calculating...';
        }
        let items = targets || leads;
        return Utils.toCurrency(items.length * cost);
    }

    const getTotalCostValue = () => {
        let items = targets || leads;
        return items.length * cost;
    }

    const fetchResources = async () => {
        try {
            let { cost, keywords } = await Request.get(utils, '/utils/', {
                type: 'sms_resources'
            })
            setCost(cost);
            setKeywords(keywords);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the sms per message cost. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close')
            });
        }
    }

    useEffect(() => {
        fetchResources();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={getTitle()}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <FieldMapper
            sections={getFields()}
            utils={utils} />

            <CreditsPicker
            utils={utils}
            cost={getTotalCostValue()}
            onChange={method => setCreditMethod(method)} />

            <LayerItem
            title={'Message Content'}>
                {getDynamicKeywords()}
                <TextView
                placeholder={'Type your message here'}
                onChange={setMessage} 
                value={message} />
            </LayerItem>
        </Layer>
    )
}

export const LeadTransferDetails = ({ abstract, index, options, utils }) => {

    const layerID = `lead_transfer_details_${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onCancelTransfers = () => {
        utils.alert.show({
            title: 'Cancel Transfers',
            message: `Are you sure that you want to cancel this transfer? This can not be undone.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Do Not Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onCancelTransfersConfirm();
                    return;
                }
            }
        })
    }

    const onCancelTransfersConfirm = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);
            let { lead_status } = await Request.post(utils, '/leads/', {
                status: Lead.Transfer.status.inactive,
                transfer_ids: [abstract.getID()],
                type: 'set_batch_transfer_status'
            });

            // update lead transfer object
            abstract.object.status = lead_status;
            utils.content.update(abstract);

            // update transfer details for lead
            abstract.object.lead.transfer = {
                ...abstract.object.lead.transfer,
                id: abstract.getID(),
                status: lead_status
            };

            // standard update for target
            let target = Abstract.create({
                type: 'leads',
                object: abstract.object.lead
            });
            utils.content.update(target);

            setLoading(false);
            utils.content.fetch('leads')
            utils.banner.show({
                title: 'Transfer Cancelled',
                message: `This lead transfer has been cancelled`,
                onShow: () => setLayerState('close')
            })

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue cancelling this transfer. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onClientClick = async id => {
        try {
            let client = await Client.get(utils, id);
            utils.layer.open({
                abstract: Abstract.create({
                    object: client,
                    type: 'client'
                }),
                Component: ClientDetails,
                id: `client_details_${id}`
            });

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this Client. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSetTransferStatus = status => {
        utils.alert.show({
            title: `${status === Lead.Transfer.status.accepted ? 'Accept' : 'Decline'} Lead`,
            message: `Are you sure that you want to ${status === Lead.Transfer.status.accepted ? 'accept' : 'decline'} this lead? ${status === Lead.Transfer.status.accepted ? `This will add the dead to your client workspace and remove it from the ${abstract.object.from_client.name} client workspace` : `This will return the lead back to the ${abstract.object.from_client.name} client workspace`}`,
            buttons: [{
                key: 'confirm',
                title: status === Lead.Transfer.status.accepted ? 'Accept' : 'Decline',
                style: status === Lead.Transfer.status.accepted ? 'default' : 'destructive'
            },{
                key: 'cancel',
                title: `Do Not ${status === Lead.Transfer.status.accepted ? 'Accept' : 'Decline'}`,
                style: status === Lead.Transfer.status.accepted ? 'destructive' : 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSetTransferStatusConfirm(status);
                    return;
                }
            }
        })
    }

    const onSetTransferStatusConfirm = async code => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);
            let { lead_status } = await Request.post(utils, '/leads/', {
                status: code,
                transfer_ids: [abstract.getID()],
                type: 'set_batch_transfer_status'
            });

            setLoading(false);
            utils.banner.show({
                title: 'Transfer Processed',
                message: `This lead has been ${code === Lead.Transfer.status.accepted ? `accepted and has been added to your client workspace.` : `declined and returned back to ${abstract.object.from_client.name} client workspace.`}`,
                onShow: () => {
                    setLayerState('close');
                }
            })

            // update lead transfer object
            abstract.object.status = lead_status;
            utils.content.update(abstract);

            // update transfer details for lead
            abstract.object.lead.transfer = {
                ...abstract.object.lead.transfer,
                id: abstract.getID(),
                status: lead_status
            };

            // update client details for lead if transfer was accepted
            if(code === Lead.Transfer.status.accepted) {

                abstract.object.lead.client = utils.client.get();
                abstract.object.lead.client_id = utils.client.get().id;
                abstract.object.lead.status = lead_status;

                let target = Abstract.create({
                    type: 'leads',
                    object: abstract.object.lead
                });
                utils.content.client.set(target, utils.client.get());
                return;
            }

            // standard update for target
            let target = Abstract.create({
                type: 'leads',
                object: abstract.object.lead
            });
            utils.content.update(target);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${code === Lead.Transfer.status.accepted ? 'accepting' : 'declining'} this transfer. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onUserClick = async id => {
        try {
            let user = await User.get(utils, id);
            utils.layer.open({
                abstract: Abstract.create({
                    object: user,
                    type: 'users'
                }),
                Component: UserDetails,
                id: `user_details_${id}`,
                permissions: ['users.details']
            })
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this user. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getButtons = () => {

        if(abstract.object.status.code === Lead.Transfer.status.in_progress && abstract.object.from_client.id !== utils.client.get().id) {
            return [{
                key: 'decline',
                text: 'Decline',
                color: 'danger',
                onClick: onSetTransferStatus.bind(this, Lead.Transfer.status.declined)
            },{
                key: 'accept',
                text: 'Accept',
                color: 'primary',
                onClick: onSetTransferStatus.bind(this, Lead.Transfer.status.accepted)
            }];
        }
        if(abstract.object.from_client.id === utils.client.get().id) {
            return [{
                key: 'inactive',
                text: 'Cancel Transfer',
                color: 'danger',
                onClick: onCancelTransfers
            }];
        }
        return null;
    }

    const getClient = () => {
        let clientID = utils.client.get().id;
        if(clientID === abstract.object.from_client?.id) { 
            return (
                <LayerItem
                title={'To Client'}
                collapsed={false}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel()
                    }}>
                        {Views.entry({
                            title: abstract.object.to_client.name,
                            subTitle: Utils.formatAddress(abstract.object.to_client.address),
                            hideIcon: true,
                            bottomBorder: false,
                            bottomBorder: false,
                            onClick: onClientClick.bind(this, abstract.object.to_client.id)
                        })}
                    </div>
                </LayerItem>
            )
        }
        return (
            <LayerItem
            title={'From Client'}
            collapsed={false}>
                <div style={{
                    ...Appearance.styles.unstyledPanel()
                }}>
                    {Views.entry({
                        title: abstract.object.from_client.name,
                        subTitle: Utils.formatAddress(abstract.object.from_client.address),
                        hideIcon: true,
                        bottomBorder: false,
                        bottomBorder: false,
                        onClick: onClientClick.bind(this, abstract.object.from_client.id)
                    })}
                </div>
            </LayerItem>
        )
    }

    const getFields = () => {
        return [{
            key: 'details',
            title: 'Transfer Details',
            items: [{
                key: 'id',
                title: 'ID',
                value: abstract.getID()
            },{
                key: 'full_name',
                title: 'Lead Name',
                value: abstract.object.lead.full_name
            },{
                key: 'date',
                title: 'Date',
                value: Utils.formatDate(abstract.object.date)
            },{
                key: 'status',
                title: 'Status',
                value: abstract.object.status.text
            }]
        }]
    }

    const getSender = () => {
        return (
            <LayerItem
            title={'Sent By'}
            collapsed={false}>
                <div style={{
                    ...Appearance.styles.unstyledPanel()
                }}>
                    {Views.entry({
                        bottomBorder: false,
                        icon: {
                            path: abstract.object.user.avatar
                        },
                        onClick: onUserClick.bind(this, abstract.object.user.user_id),
                        subTitle: abstract.object.user.email_address,
                        title: abstract.object.user.full_name
                    })}
                </div>
            </LayerItem>
        )
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={`Lead Transfer Deatails for ${abstract.getTitle()}`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            {getSender()}
            {getClient()}

            <FieldMapper
            sections={getFields()}
            utils={utils} />
        </Layer>
    )
}

export const SetLeadStatus = ({ abstract, index, options, utils }) => {

    const layerID = `set_lead_status_${abstract.getID()}`;
    const [loading, setLoading] = useState(false);
    const [layerState, setLayerState] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [items, setItems] = useState([]);

    const onArchiveLead = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.5);
            await Request.post(utils, '/leads/', {
                active: false,
                id: abstract.getID(),
                type: 'set_archive_status'
            });

            // close layer and show confirmation alert
            setLayerState('close');
            utils.banner.show({
                title: 'Lead Archived',
                message: 'The lead has been moved to the archive list. You can move this lead back into your active lead pool at any time by finding this lead in the archive list and selecting "Unarchive".'
            });
            
        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue archiving this lead. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onPromptLeadArchive = () => {
        let status = utils.client.status_codes.get().find(status => status.code === selectedStatus);
        utils.alert.show({
            title: 'All Done!',
            message: `The status for this lead has been updated to "${status.text}" and could benefit from being moved to the archive list. Moving non-actionable leads to the archive list helps keep your client workspace running smoothly and reduces your monthly active lead list costs. Would you like to archive this lead?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onArchiveLead();
                    return;
                }
                setLayerState('close');
            }
        });
    }

    const onSetStatus = async () => {
        try {

            // send request to server
            setLoading('done');
            let { status } = await Request.post(utils, '/leads/', {
                id: abstract.getID(),
                status: selectedStatus,
                type: 'set_status'
            });

            // end loading and update abstract target with new status object
            setLoading(false);
            abstract.object.status = status;

            // set sale date value based on requested status
            abstract.object.sale_date = status === Lead.status.get().sale ? moment.utc().local() : null;

            // prompt lead to be archived if a matching status was selected
            if(Lead.status.shouldRequestArchive(selectedStatus) === true) {
                onPromptLeadArchive();
                return;
            }

            // show confirmation alert
            utils.banner.show({
                title: 'Status Updated',
                message: 'The status for this lead has been updated',
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the status for this lead. ${e.message || 'An unknown error occurred'}`,
            });
        }
    }

    const getButtons = () => {
        return [{
            color: selectedStatus ? 'primary' : 'dark',
            key: 'done',
            loading: loading === 'done',
            onClick: onSetStatus,
            text: 'Save Changes'
        }];
    }

    const setupStatusCodes = () => {
        let codes = utils.client.status_codes.get().filter(status => {
            return status.capabilities.leads;
        }).map(status => ({
            id: status.code,
            title: status.text
        }));
        setItems(codes);
    }

    useEffect(() => {
        setupStatusCodes();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Set Lead Status'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'small'
        }}>
            <select
            className={`custom-select ${window.theme}`}
            defaultValue={'Choose a status...'}
            onChange={e => {
                let id = Utils.attributeForKey.select(e, 'id');
                setSelectedStatus(parseInt(id));
            }}
            style={{
                background: `${Appearance.colors.textField()} url("images/custom-select-list-arrows-${window.theme === 'dark' ? 'white' : 'black'}.png") no-repeat right 0.75rem center`,
                backgroundSize: '10px',
                width: '100%'
            }}>
                <option disabled={true}>{'Choose a status...'}</option>
                {items.map((item, index) => {
                    return (
                        <option key={index} id={item.id}>{item.title}</option>
                    )
                })}
            </select>
        </Layer>
    )
}

export const UnassignLeads = ({ index, options, utils }) => {

    const layerID = 'unassign_leads';

    const [layerState, setLayerState] = useState(null);
    const [leadType, setLeadType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statusCode, setStatusCode] = useState(null);
    const [user, setUser] = useState(null);

    const onUnassignLeads = () => {
        if(!user) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'Please select a user before moving on.'
            });
            return;
        }
        utils.alert.show({
            title: 'Unassign Leads',
            message: `Are you sure that you want to unassign all matching leads from ${user.full_name}? This can not be undone.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onUnassignLeadsConfirm();
                    return;
                }
            }
        });
    }

    const onUnassignLeadsConfirm = async () => {
        try {
            setLoading('submit');
            let { background, count, estimated_duration } = await Request.post(utils, '/leads/', {
                lead_type: leadType && leadType.id,
                status_code: statusCode && statusCode.id,
                type: 'batch_unassign_user',
                user_id: user.user_id
            });

            // determine if request is going to be processed in the background
            setLoading(false);
            if(background) {
                utils.alert.show({
                    title: 'Request Submitted',
                    message: `We have received your request to unassign ${count} ${count === 1 ? 'lead' : 'leads'} from ${user.full_name} and will notify you when it has completed. The estimated time to remove your assignments is about ${Utils.parseDuration(estimated_duration)}.`
                });
                return;
            }

            // show default confirmation alert for foreground task 
            utils.banner.show({
                title: 'Leads Unassigned',
                message: `We have unassigned ${count} ${count === 1 ? 'lead' : 'leads'} from ${user.full_name}. ${count === 1 ? 'This lead is' : 'These leads are'} ready to be reassigned to another user.`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue unassigning the leads from ${user.full_name}. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getButtons = () => {
        return [{
            key: 'submit',
            text: 'Unassign',
            color: 'danger',
            loading: loading === 'submit',
            onClick: onUnassignLeads
        }];
    }

    const getFields = () => {
        return [{
            key: 'options',
            title: 'Options',
            items: [{
                key: 'user',
                title: 'User',
                description: 'This will be the user that we look for when removing assignments from the leads in your client workspace.',
                component: 'user_lookup',
                value: user,
                onChange: setUser
            },{
                key: 'lead_type',
                required: false,
                title: 'Lead Type',
                description: 'If needed, you can remove the assignments from leads matching a specific lead type while leaving other lead types untouched.',
                component: 'lead_type_picker',
                value: leadType,
                onChange: setLeadType
            },{
                key: 'status',
                required: false,
                title: 'Status',
                description: 'If needed, you can target leads that have a specific status.',
                component: 'list',
                value: statusCode && statusCode.title,
                onChange: setStatusCode,
                items: utils.client.status_codes.get().map(entry => ({
                    id: entry.code,
                    title: entry.text
                }))
            }]
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        utils={utils}
        title={'Unassign Leads from User'}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
        </Layer>
    )
}

// Components
export const getLeadScore = lead => {
    if(!lead || !lead.lead_score) {
        return null;
    }

    let color = Appearance.colors.grey();
    switch(lead.lead_score) {
        case 'A':
        color = Appearance.colors.green;
        break;

        case 'B':
        color = Appearance.colors.yellow;
        break;

        case 'C':
        color = Appearance.colors.orange;
        break;

        case 'D':
        color = Appearance.colors.grey();
        break;

        case 'F':
        color = Appearance.colors.red;
        break;
    }

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            maxWidth: 95,
            textAlign: 'center',
            border: `1px solid ${color}`,
            background: Appearance.colors.softGradient(color),
            borderRadius: 5,
            overflow: 'hidden'
        }}>
            <span style={{
                ...Appearance.textStyles.subTitle(),
                color: 'white',
                fontWeight: '600',
                width: '100%'
            }}>{lead.lead_score}</span>
        </div>
    )
}

export const getLeadStatus = (utils, lead, options = {}) => {

    // prevent moving forward if no lead status was provided
    if(!lead || !lead.status) {
        return null;
    }

    // prepare status click handler
    const onStatusClick = evt => {

        // prevent root component from triggering click event
        evt.stopPropagation();

        // only allow editing if options object does not contain a false editing flag
        if(options.editable !== false) {
            utils.layer.open({
                id: `set_lead_status_${lead.id}`,
                abstract: Abstract.create({
                    object: lead,
                    type: 'leads'
                }),
                target: evt.target,
                Component: SetLeadStatus
            });
        }
    }

    return (
        <div
        className={options.editable === false ? '' : 'text-button'}
        onClick={onStatusClick}
        style={{
            alignItems: 'center',
            background: Appearance.colors.softGradient(lead.status.color),
            border: `2px solid ${lead.status.color}`,
            borderRadius: 5,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
            maxWidth: 125,
            overflow: 'hidden',
            paddingLeft: 8,
            paddingRight: 8,
            textAlign: 'center',
            width: '100%'
        }}>
            <span style={{
                ...Appearance.textStyles.subTitle(),
                color: 'white',
                fontWeight: '600',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%'
            }}>{lead.status.text}</span>
        </div>
    )
}

export const getLeadType = lead => {
    if(!lead || !lead.lead_type) {
        return null;
    }
    return lead.lead_type.text;
}
